import { useState, useEffect, useCallback, useRef } from "react";
import { api, saveSession, loadSession, clearSession } from "./api";
import Chat from "./Chat";

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const BG  = "linear-gradient(135deg,#0a0814,#1c1535,#0f0c29)";
const inp = { width:"100%", padding:"11px 13px", boxSizing:"border-box", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.14)", borderRadius:9, color:"#fff", fontSize:14, outline:"none" };
const getTodayKey = () => new Date().toISOString().slice(0, 10);
const fmtDT = iso => new Date(iso).toLocaleString("tr-TR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit", second:"2-digit" });
const fmtD  = d => { const [y,m,dd] = d.split("-"); return `${dd}.${m}.${y}`; };
const DAY_NAMES = ["Pazar","Pazartesi","Salı","Çarşamba","Perşembe","Cuma","Cumartesi"];

// ─── Toast bileşeni ────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{ position:"fixed", inset:0, zIndex:99998, pointerEvents:"none", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:toast.ok?"rgba(14,28,18,.97)":"rgba(28,10,10,.97)", border:`2px solid ${toast.ok?"#48c78e":"#ff6b6b"}`, borderRadius:18, padding:"22px 40px", color:toast.ok?"#48c78e":"#ff6b6b", fontSize:18, fontWeight:700, boxShadow:`0 16px 60px ${toast.ok?"rgba(72,199,142,.45)":"rgba(255,107,107,.45)"}`, display:"flex", alignItems:"center", gap:14, whiteSpace:"nowrap" }}>
        {toast.ok
          ? (toast.msg.includes("yor") || toast.msg.includes("…")
              ? <div style={{ width:22, height:22, borderRadius:"50%", border:"3px solid rgba(72,199,142,.25)", borderTopColor:"#48c78e", animation:"spin 0.7s linear infinite", flexShrink:0 }} />
              : <span style={{ fontSize:22 }}>✅</span>)
          : <span style={{ fontSize:22 }}>❌</span>
        }
        {toast.msg}
      </div>
    </div>
  );
}

function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, ok=true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return [toast, showToast];
}

// ─── Saat Hook'u ──────────────────────────────────────────────────────────────
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(iv); }, []);
  const pad = n => String(n).padStart(2,"0");
  return {
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
    date: `${pad(now.getDate())}.${pad(now.getMonth()+1)}.${now.getFullYear()}`,
    day:  DAY_NAMES[now.getDay()],
  };
}

// ─── Staging Banner ───────────────────────────────────────────────────────────
function StagingBanner() {
  const [isStaging, setIsStaging] = useState(false);
  useEffect(() => {
    fetch("/api/health").then(r=>r.json()).then(data => {
      if (data.env === "staging") setIsStaging(true);
    }).catch(() => {});
  }, []);
  if (!isStaging) return null;
  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:999999, background:"linear-gradient(90deg,#ff6b6b,#ee5a24)", padding:"7px 0", textAlign:"center", fontSize:13, fontWeight:700, color:"#fff", letterSpacing:"2px", boxShadow:"0 2px 12px rgba(255,107,107,.5)" }}>
      ⚠️  TEST ORTAMI — Bu site gerçek değildir  ⚠️
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [users,         setUsers]         = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [p,             setP]             = useState("");
  const [err,           setErr]           = useState("");
  const [loading,       setLoading]       = useState(true);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [adminP,        setAdminP]        = useState("");
  const [adminErr,      setAdminErr]      = useState("");

  const gradients = [
    { grad:"linear-gradient(135deg,#667eea,#764ba2)", shadow:"rgba(102,126,234,.4)" },
    { grad:"linear-gradient(135deg,#06d6a0,#048a81)", shadow:"rgba(6,214,160,.4)"   },
    { grad:"linear-gradient(135deg,#f093fb,#f5576c)", shadow:"rgba(245,87,108,.4)"  },
    { grad:"linear-gradient(135deg,#4facfe,#00f2fe)", shadow:"rgba(79,172,254,.4)"  },
    { grad:"linear-gradient(135deg,#43e97b,#38f9d7)", shadow:"rgba(67,233,123,.4)"  },
    { grad:"linear-gradient(135deg,#fa709a,#fee140)", shadow:"rgba(250,112,154,.4)" },
  ];

  useEffect(() => {
    api.getPublicUsers().catch(()=>[]).then(rows => {
      let ci = 0;
      setUsers(rows.map(u => ({
        ...u,
        ...(u.role === "admin"
          ? { grad:"linear-gradient(135deg,#f7971e,#ffd200)", shadow:"rgba(247,151,30,.4)" }
          : { ...gradients[ci++ % gradients.length] })
      })));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function attempt(username, password) {
    try {
      const { token, user } = await api.login(username, password);
      saveSession(token, user);
      onLogin(user);
    } catch (e) {
      return e.message || "Şifre hatalı";
    }
  }

  async function handleCashier() {
    if (!selected) return;
    const errMsg = await attempt(selected, p);
    if (errMsg) { setErr(errMsg); setP(""); }
  }

  async function handleAdmin() {
    const adminUser = users.find(u => u.role === "admin") || {username:"admin"};
    if (!adminUser) return;
    const errMsg = await attempt(adminUser.username, adminP);
    if (errMsg) { setAdminErr(errMsg); setAdminP(""); }
  }

  const cashiers = users.filter(u => u.role === "cashier");
  const sel      = users.find(u => u.username === selected);

  if (loading) return <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", color:"rgba(255,255,255,.3)", fontFamily:"Georgia,serif" }}>Yükleniyor…</div>;

  return (
    <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Georgia,serif" }}>
      <div style={{ background:"rgba(255,255,255,.04)", backdropFilter:"blur(24px)", border:"1px solid rgba(255,255,255,.12)", borderRadius:22, padding:"44px 38px", width:380, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 30px 80px rgba(0,0,0,.6)" }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:52 }}>🏦</div>
          <h1 style={{ color:"#fff", margin:"10px 0 4px", fontSize:26, fontWeight:700, letterSpacing:"1.5px" }}>KÜLTÜR KASA</h1>
          <p style={{ color:"rgba(255,255,255,.3)", margin:0, fontSize:11, letterSpacing:"3px" }}>KİM OLDUĞUNUZU SEÇİN</p>
        </div>

        {!selected && (
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
            {cashiers.map(u => (
              <button key={u.username} onClick={()=>{ setSelected(u.username); setP(""); setErr(""); }} style={{ width:"100%", padding:"13px 16px", borderRadius:13, border:"2px solid rgba(255,255,255,.07)", background:"rgba(255,255,255,.05)", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:13, transition:"all .2s" }}>
                <span style={{ fontSize:22 }}>💼</span>
                <div style={{ textAlign:"left" }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{u.name}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", marginTop:1 }}>Kasa {u.kasa_no} — Kasiyer</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
              <div style={{ width:2, height:20, background:sel?.grad, borderRadius:2 }} />
              <label style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1.5px" }}>{sel?.name?.toUpperCase()} — ŞİFRE</label>
            </div>
            <input type="password" value={p} onChange={e=>{ setP(e.target.value); setErr(""); }} onKeyDown={e=>e.key==="Enter"&&handleCashier()} placeholder="Şifrenizi girin" autoFocus
              style={{ ...inp, marginBottom: err ? 8 : 14, border:`1px solid ${err?"rgba(255,107,107,.5)":"rgba(255,255,255,.14)"}` }} />
            {err && <p style={{ color:"#ff6b6b", textAlign:"center", fontSize:12, margin:"0 0 12px" }}>{err}</p>}
            <button onClick={handleCashier} style={{ width:"100%", padding:13, background:sel?.grad||"#667eea", border:"none", borderRadius:10, color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", letterSpacing:"1px" }}>GİRİŞ YAP →</button>
            <button onClick={()=>{ setSelected(null); setErr(""); setP(""); }} style={{ width:"100%", marginTop:8, padding:"9px", background:"transparent", border:"none", color:"rgba(255,255,255,.3)", cursor:"pointer", fontSize:12 }}>← Geri</button>
          </div>
        )}

        {!selected && (
          <div style={{ marginTop:8, borderTop:"1px solid rgba(255,255,255,.07)", paddingTop:16 }}>
            <button onClick={()=>{ setAdminExpanded(v=>!v); setAdminP(""); setAdminErr(""); }}
              style={{ width:"100%", padding:"11px 16px", borderRadius:12, border:"1px solid rgba(255,215,0,.2)", background:"rgba(255,215,0,.05)", color:"rgba(255,215,0,.7)", cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:18 }}>👑</span>
              <span style={{ fontWeight:600, fontSize:13 }}>Yönetici Girişi</span>
              <span style={{ marginLeft:"auto", fontSize:12, color:"rgba(255,215,0,.4)" }}>{adminExpanded?"▲":"▼"}</span>
            </button>
            {adminExpanded && (
              <div style={{ marginTop:12, padding:"16px", background:"rgba(255,215,0,.04)", border:"1px solid rgba(255,215,0,.15)", borderRadius:12 }}>
                <input type="password" value={adminP} onChange={e=>{ setAdminP(e.target.value); setAdminErr(""); }} onKeyDown={e=>e.key==="Enter"&&handleAdmin()}
                  placeholder="Yönetici şifresi" autoFocus
                  style={{ ...inp, marginBottom: adminErr ? 8 : 12, border:`1px solid ${adminErr?"rgba(255,107,107,.5)":"rgba(255,215,0,.2)"}` }} />
                {adminErr && <p style={{ color:"#ff6b6b", fontSize:12, margin:"0 0 10px", textAlign:"center" }}>{adminErr}</p>}
                <button onClick={handleAdmin} style={{ width:"100%", padding:"11px", background:"linear-gradient(135deg,#f7971e,#ffd200)", border:"none", borderRadius:9, color:"#1a1a2e", fontWeight:700, fontSize:14, cursor:"pointer" }}>YÖNETİCİ GİRİŞİ →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ type, onClose, onAdd }) {
  const [amount, setAmount] = useState("");
  const [desc,   setDesc]   = useState("");
  const isG = type === "giris";
  const ac  = isG ? "#48c78e" : "#ff6b6b";
  const mi  = { ...inp, border:`1px solid ${ac}44` };
  function submit() {
    const n = parseFloat(amount.replace(",","."));
    if (!n || n <= 0) return;
    onAdd({ type, amount:n, description:desc.trim(), timestamp:new Date().toISOString() });
    onClose();
  }
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
      <div style={{ background:"#141228", border:`1px solid ${ac}44`, borderRadius:18, padding:32, width:340, boxShadow:`0 24px 70px ${ac}22` }}>
        <h3 style={{ color:ac, margin:"0 0 20px", fontSize:18 }}>{isG ? "➕ Giriş Ekle" : "➖ Çıkış Ekle"}</h3>
        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:6 }}>TUTAR (₺)</label>
        <input value={amount} onChange={e=>setAmount(e.target.value)} type="number" min="0" step="0.01" placeholder="0.00" autoFocus style={{ ...mi, marginBottom:14 }} />
        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:6 }}>AÇIKLAMA</label>
        <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={3} placeholder="Açıklama giriniz..." style={{ ...mi, resize:"none", marginBottom:20 }} />
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:11, background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.6)", cursor:"pointer" }}>İptal</button>
          <button onClick={submit} style={{ flex:2, padding:11, background:`linear-gradient(135deg,${isG?"#48c78e,#06d6a0":"#ff6b6b,#ee5a24"})`, border:"none", borderRadius:8, color:"#fff", fontWeight:700, cursor:"pointer" }}>
            {isG ? "Giriş Ekle" : "Çıkış Ekle"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EditModal ────────────────────────────────────────────────────────────────
function EditModal({ entry, onClose, onSubmit }) {
  const isG = entry.type === "giris";
  const ac  = isG ? "#48c78e" : "#ff6b6b";
  const mi  = { ...inp, border:`1px solid ${ac}44` };
  const [amount, setAmount] = useState(String(entry.amount));
  const [desc,   setDesc]   = useState(entry.description || "");
  function submit() {
    const n = parseFloat(amount.replace(",","."));
    if (!n || n <= 0) return;
    onSubmit(n, desc.trim());
  }
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.8)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
      <div style={{ background:"#141228", border:"1.5px solid rgba(100,160,255,.35)", borderRadius:18, padding:32, width:360 }}>
        <h3 style={{ color:"rgba(130,180,255,.9)", margin:"0 0 6px", fontSize:18 }}>✏️ Düzenleme Talebi</h3>
        <p style={{ color:"rgba(255,255,255,.3)", fontSize:12, margin:"0 0 20px" }}>Değişiklikler yönetici onayına gönderilecek</p>
        <div style={{ background:"rgba(255,255,255,.04)", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:12, color:"rgba(255,255,255,.4)" }}>
          <span style={{ color:isG?"#48c78e":"#ff6b6b", fontWeight:700 }}>Mevcut:</span>
          &nbsp;{isG?"+":"−"} {entry.amount.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺
          {entry.description && <span style={{ marginLeft:8 }}>— {entry.description}</span>}
        </div>
        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:6 }}>YENİ TUTAR (₺)</label>
        <input value={amount} onChange={e=>setAmount(e.target.value)} type="number" min="0" step="0.01" autoFocus style={{ ...mi, marginBottom:14 }} />
        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:6 }}>YENİ AÇIKLAMA</label>
        <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={3} style={{ ...mi, resize:"none", marginBottom:20 }} />
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:11, background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.6)", cursor:"pointer" }}>İptal</button>
          <button onClick={submit} style={{ flex:2, padding:11, background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:8, color:"#0a0814", fontWeight:700, cursor:"pointer" }}>Düzenleme Talebi Gönder</button>
        </div>
      </div>
    </div>
  );
}

// ─── KasaPanel ────────────────────────────────────────────────────────────────
function KasaPanel({ kasaKey, kasaName, kasaNo, readOnly, isAdmin, addedBy }) {
  const [entries,               setEntries]               = useState([]);
  const [datesWithData,         setDatesWithData]         = useState(new Set());
  const [modal,                 setModal]                 = useState(null);
  const [editModal,             setEditModal]             = useState(null);
  const [selDate,               setSelDate]               = useState(getTodayKey());
  const [loading,               setLoading]               = useState(true);
  const [toast,                 showToast]                = useToast();
  const [pendingDelIds,         setPendingDelIds]         = useState(new Set());
  const [pendingEditIds,        setPendingEditIds]        = useState(new Set());
  const [notes,                 setNotes]                 = useState([]);
  const [noteText,              setNoteText]              = useState("");

  const refreshEntries = useCallback(async () => {
    const rows = await api.getEntries(kasaKey, selDate);
    setEntries(rows.map(r => ({ ...r, amount: parseFloat(r.amount) })));
  }, [kasaKey, selDate]);

  const refreshDates = useCallback(async () => {
    const dates = await api.getDates(kasaKey);
    setDatesWithData(new Set(dates));
  }, [kasaKey]);

  const refreshPending = useCallback(async () => {
    const [delReqs, editReqs] = await Promise.all([api.getDeleteRequests(), api.getEditRequests()]);
    setPendingDelIds(new Set(delReqs.filter(r => r.kasa_key === kasaKey).map(r => r.entry_id)));
    setPendingEditIds(new Set(editReqs.filter(r => r.kasa_key === kasaKey).map(r => r.entry_id)));
  }, [kasaKey]);

  const refreshNotes = useCallback(async () => {
    const rows = await api.getNotes(kasaKey, selDate);
    setNotes(rows);
  }, [kasaKey, selDate]);

  useEffect(() => {
    setLoading(true);
    Promise.all([refreshEntries(), refreshDates(), refreshPending(), refreshNotes()])
      .finally(() => setLoading(false));
    const iv = setInterval(() => { refreshEntries(); refreshPending(); refreshNotes(); }, 5000);
    return () => clearInterval(iv);
  }, [kasaKey, selDate]);

  async function addEntry(entry) {
    const label = entry.type === "giris" ? "Giriş" : "Çıkış";
    showToast(`${label} ekleniyor…`);
    await api.addEntry(kasaKey, { ...entry, date: selDate, added_by: addedBy });
    await refreshEntries();
    await refreshDates();
    showToast(`${label} eklendi`);
  }

  async function deleteEntryDirect(entry) {
    showToast("Kayıt siliniyor…");
    await api.deleteEntry(entry.id);
    await refreshEntries();
    await refreshPending();
    showToast("Kayıt silindi");
  }

  async function requestDelete(entry) {
    if (pendingDelIds.has(entry.id)) return;
    showToast("Silme talebi gönderiliyor…");
    await api.addDeleteRequest({
      id:          `${Date.now()}-${Math.random()}`,
      kasa_key:    kasaKey,
      kasa_name:   kasaName,
      date:        selDate,
      entry_id:    entry.id,
      entry_data:  entry,
      requested_at: new Date().toISOString(),
    });
    await refreshPending();
    showToast("Silme talebi gönderildi");
  }

  async function requestEdit(entry, newAmount, newDescription) {
    if (pendingEditIds.has(entry.id)) return;
    showToast("Düzenleme talebi gönderiliyor…");
    await api.addEditRequest({
      id:              `${Date.now()}-${Math.random()}`,
      kasa_key:        kasaKey,
      kasa_name:       kasaName,
      date:            selDate,
      entry_id:        entry.id,
      entry_data:      entry,
      new_amount:      newAmount,
      new_description: newDescription,
      requested_by:    addedBy || kasaName,
      requested_at:    new Date().toISOString(),
    });
    await refreshPending();
    setEditModal(null);
    showToast("Düzenleme talebi gönderildi");
  }

  async function addNote() {
    const text = noteText.trim();
    if (!text) return;
    showToast("Not ekleniyor…");
    await api.addNote(kasaKey, {
      id:        `${Date.now()}-${Math.random()}`,
      date:      selDate,
      text,
      author:    addedBy || kasaName,
      timestamp: new Date().toISOString(),
    });
    await refreshNotes();
    setNoteText("");
    showToast("Not eklendi");
  }

  async function deleteNote(id) {
    showToast("Not siliniyor…");
    await api.deleteNote(id);
    await refreshNotes();
    showToast("Not silindi");
  }

  const totalGiris = entries.filter(e=>e.type==="giris").reduce((s,e)=>s+e.amount, 0);
  const totalCikis = entries.filter(e=>e.type==="cikis").reduce((s,e)=>s+e.amount, 0);
  const fark       = totalGiris - totalCikis;
  const absFark    = Math.abs(fark);
  const durum = fark === 0
    ? { text:"✅ Kasa Doğru",       color:"#48c78e", rgb:"72,199,142",  sign:"",  abs:0       }
    : fark > 0
    ? { text:"⚠️ Kasada Açık Var",  color:"#ff6b6b", rgb:"255,107,107", sign:"−", abs:absFark }
    : { text:"💰 Kasada Fazla Var", color:"#ffd700", rgb:"255,215,0",   sign:"+", abs:absFark };

  // 6 aylık tarih listesi
  const allDates = (() => {
    const dates = new Set([getTodayKey()]);
    const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - 6);
    for (let d = new Date(); d >= cutoff; d.setDate(d.getDate() - 1))
      dates.add(d.toISOString().slice(0, 10));
    return Array.from(dates).sort().reverse();
  })();

  if (loading) return <div style={{ color:"rgba(255,255,255,.3)", padding:40, textAlign:"center" }}>Yükleniyor…</div>;

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:680, margin:"0 auto" }}>
      {modal && <Modal type={modal} onClose={()=>setModal(null)} onAdd={addEntry} />}
      {editModal && <EditModal entry={editModal} onClose={()=>setEditModal(null)} onSubmit={(a,d)=>requestEdit(editModal,a,d)} />}
      <Toast toast={toast} />

      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,rgba(102,126,234,.18),rgba(118,75,162,.18))", border:"1px solid rgba(102,126,234,.28)", borderRadius:16, padding:"18px 22px", marginBottom:18, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>🏦 Kasa {kasaNo} — {kasaName}</h2>
          <p style={{ color:"rgba(255,255,255,.45)", margin:"3px 0 0", fontSize:12 }}>Kasiyer Kasası</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <AdvanceBadge kasaKey={kasaKey} kasaName={kasaName} selDate={selDate} />
          <select value={selDate} onChange={e=>setSelDate(e.target.value)} style={{ padding:"8px 12px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.18)", borderRadius:8, color:"#fff", fontSize:13, cursor:"pointer" }}>
            {allDates.map(d => (
              <option key={d} value={d} style={{ background:"#1a1a2e" }}>
                {fmtD(d)} — {d===getTodayKey()?"Bugün":DAY_NAMES[new Date(d).getDay()]}{datesWithData.has(d)?" ●":""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Butonlar */}
      {!readOnly && selDate===getTodayKey() && (
        <div style={{ display:"flex", gap:12, marginBottom:18 }}>
          {[["giris","➕ Giriş Ekle","#48c78e,#06d6a0","72,199,142"],["cikis","➖ Çıkış Ekle","#ff6b6b,#ee5a24","255,107,107"]].map(([t,lbl,grad,rgb])=>(
            <button key={t} onClick={()=>setModal(t)} style={{ flex:1, padding:13, background:`linear-gradient(135deg,${grad})`, border:"none", borderRadius:12, color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", boxShadow:`0 4px 18px rgba(${rgb},.28)` }}>{lbl}</button>
          ))}
        </div>
      )}
      {readOnly && (
        <div style={{ background:"rgba(255,193,7,.08)", border:"1px solid rgba(255,193,7,.25)", borderRadius:10, padding:"9px 16px", marginBottom:14, color:"#ffd700", fontSize:12, textAlign:"center" }}>👁️ Yönetici görünümü</div>
      )}

      {/* İşlem listesi */}
      <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, overflow:"hidden", marginBottom:18 }}>
        <div style={{ padding:"13px 20px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"flex", justifyContent:"space-between" }}>
          <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px", textTransform:"uppercase" }}>İşlem Kayıtları — {fmtD(selDate)}</span>
          <span style={{ color:"rgba(255,255,255,.3)", fontSize:11 }}>{entries.length} işlem</span>
        </div>
        {entries.length === 0
          ? <div style={{ padding:"38px 20px", textAlign:"center", color:"rgba(255,255,255,.18)", fontSize:13 }}>Bu tarih için henüz işlem yok</div>
          : <div>
              {entries.map((e,i) => {
                const g           = e.type === "giris";
                const isPendingDel  = pendingDelIds.has(e.id);
                const isPendingEdit = pendingEditIds.has(e.id);
                return (
                  <div key={e.id} style={{ padding:"13px 20px", borderBottom:i<entries.length-1?"1px solid rgba(255,255,255,.05)":"none", display:"flex", gap:13, alignItems:"center", background:i%2?"rgba(255,255,255,.015)":"transparent", opacity:isPendingDel?0.35:1, transition:"opacity .3s" }}>
                    <div style={{ width:34, height:34, borderRadius:"50%", flexShrink:0, background:g?"rgba(72,199,142,.14)":"rgba(255,107,107,.14)", border:`1px solid ${g?"rgba(72,199,142,.38)":"rgba(255,107,107,.38)"}`, display:"flex", alignItems:"center", justifyContent:"center", color:g?"#48c78e":"#ff6b6b", fontSize:18, fontWeight:700 }}>
                      {g?"+":"−"}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                        <span style={{ color:g?"#48c78e":"#ff6b6b", fontWeight:700, fontSize:15 }}>
                          {g?"+":"−"}&nbsp;{e.amount.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺
                        </span>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ color:"rgba(255,255,255,.28)", fontSize:11 }}>{fmtDT(e.timestamp)}</div>
                          {e.added_by && <div style={{ color:"rgba(255,255,255,.2)", fontSize:10, marginTop:2 }}>✍️ {e.added_by}</div>}
                        </div>
                      </div>
                      {e.description && <p style={{ color:"rgba(255,255,255,.5)", fontSize:12, margin:"4px 0 0", lineHeight:1.5 }}>{e.description}</p>}
                      {isPendingEdit && <p style={{ color:"rgba(100,160,255,.6)", fontSize:11, margin:"4px 0 0" }}>✏️ Düzenleme onay bekliyor</p>}
                    </div>
                    {!readOnly && (
                      isAdmin
                        ? <button onClick={()=>deleteEntryDirect(e)} style={{ flexShrink:0, width:30, height:30, borderRadius:"50%", background:"rgba(255,107,107,.12)", border:"1px solid rgba(255,107,107,.25)", color:"#ff6b6b", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                        : <div style={{ display:"flex", flexDirection:"column", gap:5, flexShrink:0 }}>
                            <button onClick={()=>!isPendingDel&&requestDelete(e)} disabled={isPendingDel}
                              style={{ padding:"4px 10px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", background:isPendingDel?"rgba(255,255,255,.04)":"rgba(255,107,107,.1)", border:`1px solid ${isPendingDel?"rgba(255,255,255,.1)":"rgba(255,107,107,.28)"}`, color:isPendingDel?"rgba(255,255,255,.2)":"#ff6b6b", cursor:isPendingDel?"not-allowed":"pointer" }}>
                              {isPendingDel?"⏳ Bekliyor":"Sil Talep"}
                            </button>
                            <button onClick={()=>!isPendingEdit&&!isPendingDel&&setEditModal(e)} disabled={isPendingEdit||isPendingDel}
                              style={{ padding:"4px 10px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", background:isPendingEdit?"rgba(255,255,255,.04)":"rgba(100,160,255,.1)", border:`1px solid ${isPendingEdit?"rgba(255,255,255,.1)":"rgba(100,160,255,.28)"}`, color:isPendingEdit?"rgba(255,255,255,.2)":"rgba(130,180,255,.9)", cursor:isPendingEdit||isPendingDel?"not-allowed":"pointer" }}>
                              {isPendingEdit?"⏳ Bekliyor":"✏️ Düzenle"}
                            </button>
                          </div>
                    )}
                  </div>
                );
              })}
            </div>
        }
      </div>

      {/* Rapor */}
      <div style={{ background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.09)", borderRadius:14, overflow:"hidden" }}>
        <div style={{ padding:"13px 20px", borderBottom:"1px solid rgba(255,255,255,.07)" }}>
          <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px", textTransform:"uppercase" }}>📊 Günlük Rapor — {fmtD(selDate)}</span>
        </div>
        <div style={{ padding:"16px 20px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            <div style={{ background:"rgba(72,199,142,.07)", border:"1px solid rgba(72,199,142,.2)", borderRadius:10, padding:14 }}>
              <div style={{ color:"rgba(255,255,255,.38)", fontSize:10, letterSpacing:"1px", marginBottom:5 }}>TOPLAM GİRİŞ</div>
              <div style={{ color:"#48c78e", fontSize:19, fontWeight:700 }}>+{totalGiris.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺</div>
            </div>
            <div style={{ background:"rgba(255,107,107,.07)", border:"1px solid rgba(255,107,107,.2)", borderRadius:10, padding:14 }}>
              <div style={{ color:"rgba(255,255,255,.38)", fontSize:10, letterSpacing:"1px", marginBottom:5 }}>TOPLAM ÇIKIŞ</div>
              <div style={{ color:"#ff6b6b", fontSize:19, fontWeight:700 }}>−{totalCikis.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺</div>
            </div>
          </div>
          <div style={{ background:`rgba(${durum.rgb},.07)`, border:`1px solid rgba(${durum.rgb},.22)`, borderRadius:10, padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ color:"rgba(255,255,255,.38)", fontSize:10, letterSpacing:"1px", marginBottom:5 }}>KASA DURUMU</div>
              <div style={{ color:durum.color, fontSize:15, fontWeight:700 }}>{durum.text}</div>
              <div style={{ color:"rgba(255,255,255,.35)", fontSize:11, marginTop:4 }}>
                Fark:&nbsp;<span style={{ color:durum.color }}>{durum.sign}{durum.abs===0?"0,00":durum.abs.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺</span>
              </div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ color:"rgba(255,255,255,.38)", fontSize:10, letterSpacing:"1px", marginBottom:5 }}>GENEL FARK</div>
              <div style={{ color:durum.color, fontSize:28, fontWeight:800 }}>
                {durum.sign}{durum.abs===0?"0,00":durum.abs.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notlar */}
      <div style={{ marginTop:18, background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.09)", borderRadius:14, overflow:"hidden" }}>
        <div style={{ padding:"13px 20px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px", textTransform:"uppercase" }}>📝 Notlar — {fmtD(selDate)}</span>
          <span style={{ color:"rgba(255,255,255,.3)", fontSize:11 }}>{notes.length} not</span>
        </div>
        {notes.length === 0
          ? <div style={{ padding:"24px 20px", textAlign:"center", color:"rgba(255,255,255,.18)", fontSize:13 }}>Bu tarihe ait not yok</div>
          : <div style={{ padding:"8px 0" }}>
              {notes.map(n => (
                <div key={n.id} style={{ padding:"10px 20px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ width:30, height:30, borderRadius:"50%", flexShrink:0, background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>📝</div>
                  <div style={{ flex:1 }}>
                    <p style={{ color:"rgba(255,255,255,.8)", fontSize:13, margin:"0 0 4px", lineHeight:1.6 }}>{n.text}</p>
                    <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                      <span style={{ color:"rgba(255,255,255,.25)", fontSize:11 }}>✍️ {n.author}</span>
                      <span style={{ color:"rgba(255,255,255,.15)", fontSize:10 }}>•</span>
                      <span style={{ color:"rgba(255,255,255,.25)", fontSize:11 }}>{fmtDT(n.timestamp)}</span>
                    </div>
                  </div>
                  {(isAdmin || !readOnly) && (
                    <button onClick={()=>deleteNote(n.id)} style={{ flexShrink:0, width:24, height:24, borderRadius:"50%", background:"rgba(255,107,107,.1)", border:"1px solid rgba(255,107,107,.2)", color:"rgba(255,107,107,.6)", cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                  )}
                </div>
              ))}
            </div>
        }
        {!readOnly && selDate===getTodayKey() && (
          <div style={{ padding:"12px 20px", borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:10 }}>
            <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); addNote(); } }}
              placeholder="Not ekle… (Enter ile gönder)" rows={2}
              style={{ ...inp, flex:1, resize:"none", fontSize:13, padding:"9px 12px" }} />
            <button onClick={addNote} disabled={!noteText.trim()}
              style={{ padding:"0 18px", background:noteText.trim()?"linear-gradient(135deg,#ffd700,#f7971e)":"rgba(255,255,255,.05)", border:`1px solid ${noteText.trim()?"rgba(255,215,0,.4)":"rgba(255,255,255,.1)"}`, borderRadius:10, color:noteText.trim()?"#1a1a2e":"rgba(255,255,255,.2)", fontWeight:700, cursor:noteText.trim()?"pointer":"not-allowed", fontSize:13, whiteSpace:"nowrap" }}>
              Ekle
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ayarlar Paneli ───────────────────────────────────────────────────────────
function SettingsPanel({ onUsersChange }) {
  const [users,         setUsers]         = useState([]);
  const [toast,         showToast]        = useToast();
  const [editing,       setEditing]       = useState(null);
  const [formVal,       setFormVal]       = useState("");
  const [passOld,       setPassOld]       = useState("");
  const [passNew,       setPassNew]       = useState("");
  const [passNew2,      setPassNew2]      = useState("");
  const [showAddForm,   setShowAddForm]   = useState(false);
  const [newUsername,   setNewUsername]   = useState("");
  const [newName,       setNewName]       = useState("");
  const [newPassword,   setNewPassword]   = useState("");
  const [newKasa,       setNewKasa]       = useState("1");
  const [confirmDelete, setConfirmDelete] = useState(null);

  async function refresh() {
    const rows = await api.getUsers();
    setUsers(rows);
    onUsersChange?.();
  }

  useEffect(() => { refresh(); }, []);

  async function saveName(u) {
    const v = formVal.trim();
    if (!v) return showToast("Ad boş olamaz.", false);
    await api.updateUser(u.username, { name: v });
    showToast("Görünen ad güncellendi ✓");
    setEditing(null);
    refresh();
  }

  async function saveUsername(u) {
    const newKey = formVal.trim().toLowerCase();
    if (!newKey) return showToast("Kullanıcı adı boş olamaz.", false);
    if (newKey === u.username) { setEditing(null); return; }
    await api.updateUser(u.username, { newUsername: newKey });
    showToast("Kullanıcı adı güncellendi ✓");
    setEditing(null);
    refresh();
  }

  async function savePassword(u) {
    if (!passOld || !passNew || !passNew2) return showToast("Tüm alanları doldurun.", false);
    if (passNew !== passNew2)             return showToast("Yeni şifreler eşleşmiyor.", false);
    if (passNew.length < 4)              return showToast("Şifre en az 4 karakter olmalı.", false);
    try {
      await api.updateUser(u.username, { oldPassword: passOld, newPassword: passNew });
      showToast("Şifre güncellendi ✓");
      setEditing(null);
      setPassOld(""); setPassNew(""); setPassNew2("");
    } catch (e) { showToast(e.message, false); }
  }

  async function addUser() {
    const ukey = newUsername.trim().toLowerCase();
    const uname = newName.trim();
    if (!ukey)  return showToast("Kullanıcı adı boş olamaz.", false);
    if (!uname) return showToast("Görünen ad boş olamaz.", false);
    if (!newPassword || newPassword.length < 4) return showToast("Şifre en az 4 karakter olmalı.", false);
    try {
      await api.addUser({ username: ukey, name: uname, password: newPassword, kasa_no: parseInt(newKasa) });
      showToast(`${uname} eklendi ✓`);
      setNewUsername(""); setNewName(""); setNewPassword(""); setNewKasa("1");
      setShowAddForm(false);
      refresh();
    } catch (e) { showToast(e.message, false); }
  }

  async function deleteUser(username) {
    try {
      await api.deleteUser(username);
      showToast("Kullanıcı silindi ✓");
      setConfirmDelete(null);
      refresh();
    } catch (e) { showToast(e.message, false); }
  }

  const roleLabel = { cashier:"Kasiyer", admin:"Yönetici" };
  const roleColor = { cashier:"rgba(102,126,234,.85)", admin:"rgba(255,215,0,.85)" };
  const btnSm = (color, bg, border) => ({ padding:"5px 12px", background:bg, border:`1px solid ${border}`, borderRadius:6, color, cursor:"pointer", fontSize:12, whiteSpace:"nowrap" });

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:680, margin:"0 auto" }}>
      <Toast toast={toast} />

      {confirmDelete && (
        <div style={{ position:"fixed", inset:0, zIndex:99999, background:"rgba(0,0,0,.75)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#141228", border:"2px solid rgba(255,107,107,.5)", borderRadius:20, padding:"32px 40px", width:340, textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>🗑️</div>
            <div style={{ color:"#fff", fontWeight:700, fontSize:17, marginBottom:8 }}>Kullanıcıyı Sil</div>
            <div style={{ color:"rgba(255,255,255,.5)", fontSize:13, marginBottom:24 }}>
              <span style={{ color:"#ff6b6b", fontWeight:700 }}>@{confirmDelete}</span> kalıcı olarak silinecek. Emin misiniz?
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setConfirmDelete(null)} style={{ flex:1, padding:"11px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.14)", borderRadius:10, color:"rgba(255,255,255,.6)", cursor:"pointer" }}>İptal</button>
              <button onClick={()=>deleteUser(confirmDelete)} style={{ flex:1, padding:"11px", background:"linear-gradient(135deg,#ff6b6b,#ee5a24)", border:"none", borderRadius:10, color:"#fff", fontWeight:700, cursor:"pointer" }}>Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:"linear-gradient(135deg,rgba(247,151,30,.15),rgba(255,210,0,.1))", border:"1px solid rgba(247,151,30,.28)", borderRadius:16, padding:"18px 22px", marginBottom:22 }}>
        <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>⚙️ Hesap Ayarları</h2>
        <p style={{ color:"rgba(255,255,255,.4)", margin:"4px 0 0", fontSize:12 }}>Kullanıcı adı, görünen ad ve şifre yönetimi</p>
      </div>

      {users.map(u => (
        <div key={u.username} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, marginBottom:14, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,.06)", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:42, height:42, borderRadius:"50%", background:"rgba(102,126,234,.18)", border:"1px solid rgba(102,126,234,.32)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
              {u.role==="admin"?"👑":"💼"}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ color:"#fff", fontWeight:700, fontSize:15 }}>{u.name}</div>
              <div style={{ display:"flex", gap:8, marginTop:3, alignItems:"center" }}>
                <span style={{ color:"rgba(255,255,255,.35)", fontSize:12 }}>@{u.username}</span>
                <span style={{ background:u.role==="admin"?"rgba(255,215,0,.1)":"rgba(102,126,234,.1)", border:`1px solid ${u.role==="admin"?"rgba(255,215,0,.28)":"rgba(102,126,234,.28)"}`, borderRadius:20, padding:"1px 8px", fontSize:10, color:roleColor[u.role] }}>{roleLabel[u.role]}</span>
                {u.role==="cashier" && <span style={{ background:"rgba(72,199,142,.08)", border:"1px solid rgba(72,199,142,.2)", borderRadius:20, padding:"1px 8px", fontSize:10, color:"rgba(72,199,142,.8)" }}>Kasa {u.kasa_no}</span>}
              </div>
            </div>
            {u.role!=="admin" && (
              <button onClick={()=>setConfirmDelete(u.username)} style={{ padding:"6px 14px", background:"rgba(255,107,107,.1)", border:"1px solid rgba(255,107,107,.28)", borderRadius:8, color:"#ff6b6b", cursor:"pointer", fontSize:12, fontWeight:600 }}>🗑️ Sil</button>
            )}
          </div>

          <div style={{ padding:"14px 20px", display:"flex", flexDirection:"column", gap:10 }}>
            {editing?.username===u.username && editing?.field==="name" ? (
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:12, width:110, flexShrink:0 }}>Görünen Ad</span>
                <input value={formVal} onChange={e=>setFormVal(e.target.value)} autoFocus style={{ ...inp, flex:1, fontSize:13, padding:"8px 11px" }} onKeyDown={e=>{ if(e.key==="Enter") saveName(u); if(e.key==="Escape") setEditing(null); }} />
                <button onClick={()=>saveName(u)} style={{ padding:"8px 14px", background:"linear-gradient(135deg,#48c78e,#06d6a0)", border:"none", borderRadius:8, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13 }}>Kaydet</button>
                <button onClick={()=>setEditing(null)} style={{ padding:"8px 10px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>✕</button>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", background:"rgba(255,255,255,.025)", borderRadius:8, padding:"9px 14px" }}>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:12, width:110, flexShrink:0 }}>Görünen Ad</span>
                <span style={{ color:"rgba(255,255,255,.8)", fontSize:13, flex:1 }}>{u.name}</span>
                <button onClick={()=>{ setEditing({username:u.username,field:"name"}); setFormVal(u.name); }} style={btnSm("rgba(102,126,234,.9)","rgba(102,126,234,.12)","rgba(102,126,234,.28)")}>Düzenle</button>
              </div>
            )}

            {editing?.username===u.username && editing?.field==="username" ? (
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:12, width:110, flexShrink:0 }}>Kullanıcı Adı</span>
                <input value={formVal} onChange={e=>setFormVal(e.target.value)} autoFocus style={{ ...inp, flex:1, fontSize:13, padding:"8px 11px" }} onKeyDown={e=>{ if(e.key==="Enter") saveUsername(u); if(e.key==="Escape") setEditing(null); }} />
                <button onClick={()=>saveUsername(u)} style={{ padding:"8px 14px", background:"linear-gradient(135deg,#667eea,#764ba2)", border:"none", borderRadius:8, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13 }}>Kaydet</button>
                <button onClick={()=>setEditing(null)} style={{ padding:"8px 10px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>✕</button>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", background:"rgba(255,255,255,.025)", borderRadius:8, padding:"9px 14px" }}>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:12, width:110, flexShrink:0 }}>Kullanıcı Adı</span>
                <span style={{ color:"rgba(255,255,255,.8)", fontSize:13, flex:1 }}>@{u.username}</span>
                <button onClick={()=>{ setEditing({username:u.username,field:"username"}); setFormVal(u.username); }} style={btnSm("rgba(102,126,234,.9)","rgba(102,126,234,.12)","rgba(102,126,234,.28)")}>Düzenle</button>
              </div>
            )}

            {editing?.username===u.username && editing?.field==="password" ? (
              <div style={{ background:"rgba(255,255,255,.025)", borderRadius:10, padding:"14px 16px" }}>
                <div style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", marginBottom:10 }}>ŞİFRE DEĞİŞTİR</div>
                <input type="password" value={passOld} onChange={e=>setPassOld(e.target.value)} placeholder="Mevcut şifre" style={{ ...inp, fontSize:13, padding:"9px 12px", marginBottom:8 }} />
                <input type="password" value={passNew} onChange={e=>setPassNew(e.target.value)} placeholder="Yeni şifre" style={{ ...inp, fontSize:13, padding:"9px 12px", marginBottom:8 }} />
                <input type="password" value={passNew2} onChange={e=>setPassNew2(e.target.value)} placeholder="Yeni şifre (tekrar)" style={{ ...inp, fontSize:13, padding:"9px 12px", marginBottom:12 }} onKeyDown={e=>e.key==="Enter"&&savePassword(u)} />
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>savePassword(u)} style={{ flex:2, padding:"9px", background:"linear-gradient(135deg,#667eea,#764ba2)", border:"none", borderRadius:8, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13 }}>Şifreyi Güncelle</button>
                  <button onClick={()=>setEditing(null)} style={{ flex:1, padding:"9px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>İptal</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", background:"rgba(255,255,255,.025)", borderRadius:8, padding:"9px 14px" }}>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:12, width:110, flexShrink:0 }}>Şifre</span>
                <span style={{ color:"rgba(255,255,255,.35)", fontSize:14, flex:1, letterSpacing:"3px" }}>••••••••</span>
                <button onClick={()=>{ setEditing({username:u.username,field:"password"}); setPassOld(""); setPassNew(""); setPassNew2(""); }} style={btnSm("#ff6b6b","rgba(255,107,107,.1)","rgba(255,107,107,.25)")}>Değiştir</button>
              </div>
            )}
          </div>
        </div>
      ))}

      <div style={{ marginTop:8 }}>
        {!showAddForm ? (
          <button onClick={()=>setShowAddForm(true)} style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,rgba(72,199,142,.15),rgba(6,214,160,.1))", border:"1.5px dashed rgba(72,199,142,.4)", borderRadius:14, color:"#48c78e", fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
            ＋ Yeni Kullanıcı (Kasa) Ekle
          </button>
        ) : (
          <div style={{ background:"rgba(72,199,142,.05)", border:"1.5px solid rgba(72,199,142,.25)", borderRadius:14, padding:"20px 22px" }}>
            <div style={{ color:"#48c78e", fontWeight:700, fontSize:15, marginBottom:16 }}>👤 Yeni Kullanıcı Ekle</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
              <div>
                <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>KULLANICI ADI</label>
                <input value={newUsername} onChange={e=>setNewUsername(e.target.value)} placeholder="ör: ahmet" style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
              </div>
              <div>
                <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>GÖRÜNEN AD</label>
                <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="ör: Ahmet" style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
              <div>
                <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>ŞİFRE</label>
                <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} placeholder="En az 4 karakter" style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
              </div>
              <div>
                <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>KASA NO</label>
                <select value={newKasa} onChange={e=>setNewKasa(e.target.value)} style={{ ...inp, fontSize:13, padding:"9px 12px", cursor:"pointer" }}>
                  {[1,2,3,4].map(n => <option key={n} value={n} style={{ background:"#1a1a2e" }}>Kasa {n}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={addUser} style={{ flex:2, padding:"11px", background:"linear-gradient(135deg,#48c78e,#06d6a0)", border:"none", borderRadius:10, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:14 }}>✓ Kullanıcı Ekle</button>
              <button onClick={()=>{ setShowAddForm(false); setNewUsername(""); setNewName(""); setNewPassword(""); setNewKasa("1"); }} style={{ flex:1, padding:"11px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:10, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>İptal</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Silme Talepleri ──────────────────────────────────────────────────────────
function DeleteRequestsPanel() {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      showToast]     = useToast();
  const [processing, setProcessing] = useState(null);

  async function refresh() {
    const rows = await api.getDeleteRequests();
    setRequests(rows.map(r => ({ ...r, entry: typeof r.entry_data === "string" ? JSON.parse(r.entry_data) : r.entry_data })));
    setLoading(false);
  }

  useEffect(() => { refresh(); const iv = setInterval(refresh, 5000); return () => clearInterval(iv); }, []);

  async function approve(req) {
    setProcessing("approve");
    await api.approveDeleteRequest(req.id);
    setProcessing(null);
    showToast("Kayıt başarıyla silindi ✓");
    refresh();
  }

  async function reject(req) {
    setProcessing("reject");
    await api.rejectDeleteRequest(req.id);
    setProcessing(null);
    showToast("Talep reddedildi.", false);
    refresh();
  }

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:680, margin:"0 auto" }}>
      {processing && (
        <div style={{ position:"fixed", inset:0, zIndex:99999, background:"rgba(0,0,0,.7)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#141228", border:`2px solid ${processing==="approve"?"#48c78e":"#ff6b6b"}`, borderRadius:20, padding:"36px 52px", display:"flex", flexDirection:"column", alignItems:"center", gap:18 }}>
            <div style={{ width:52, height:52, borderRadius:"50%", border:`4px solid ${processing==="approve"?"rgba(72,199,142,.2)":"rgba(255,107,107,.2)"}`, borderTopColor:processing==="approve"?"#48c78e":"#ff6b6b", animation:"spin 0.7s linear infinite" }} />
            <div style={{ color:"#fff", fontWeight:700, fontSize:17 }}>{processing==="approve"?"Kayıt siliniyor…":"Talep reddediliyor…"}</div>
          </div>
        </div>
      )}
      <Toast toast={toast} />

      <div style={{ background:"linear-gradient(135deg,rgba(255,107,107,.15),rgba(238,90,36,.1))", border:"1px solid rgba(255,107,107,.25)", borderRadius:16, padding:"18px 22px", marginBottom:22 }}>
        <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>🗑️ Silme Talepleri</h2>
        <p style={{ color:"rgba(255,255,255,.4)", margin:"4px 0 0", fontSize:12 }}>Kasiyerlerden gelen silme talepleri</p>
      </div>

      {loading ? <div style={{ color:"rgba(255,255,255,.3)", textAlign:"center", padding:40 }}>Yükleniyor…</div>
      : requests.length === 0 ? (
        <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:14, padding:"48px 20px", textAlign:"center" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
          <div style={{ color:"rgba(255,255,255,.3)", fontSize:14 }}>Bekleyen silme talebi yok</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {requests.map(req => {
            const e = req.entry; const isG = e?.type === "giris";
            return (
              <div key={req.id} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,193,7,.2)", borderRadius:14, overflow:"hidden" }}>
                <div style={{ padding:"12px 18px", background:"rgba(255,193,7,.06)", borderBottom:"1px solid rgba(255,193,7,.15)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:18 }}>⏳</span>
                    <span style={{ color:"#ffd700", fontWeight:700, fontSize:13 }}>{req.kasa_name}</span>
                    <span style={{ color:"rgba(255,255,255,.35)", fontSize:11 }}>{fmtD(req.date?.slice(0,10)||req.date)}</span>
                  </div>
                  <span style={{ color:"rgba(255,255,255,.3)", fontSize:11 }}>Talep: {fmtDT(req.requested_at)}</span>
                </div>
                <div style={{ padding:"14px 18px", display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", flexShrink:0, background:isG?"rgba(72,199,142,.14)":"rgba(255,107,107,.14)", border:`1px solid ${isG?"rgba(72,199,142,.38)":"rgba(255,107,107,.38)"}`, display:"flex", alignItems:"center", justifyContent:"center", color:isG?"#48c78e":"#ff6b6b", fontSize:20, fontWeight:700 }}>
                    {isG?"+":"−"}
                  </div>
                  <div style={{ flex:1 }}>
                    <span style={{ color:isG?"#48c78e":"#ff6b6b", fontWeight:700, fontSize:16 }}>{isG?"+":"−"} {parseFloat(e?.amount||0).toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺</span>
                    {e?.description && <p style={{ color:"rgba(255,255,255,.5)", fontSize:12, margin:"4px 0 0" }}>{e.description}</p>}
                  </div>
                </div>
                <div style={{ padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:10 }}>
                  <button onClick={()=>approve(req)} style={{ flex:1, padding:"10px", background:"linear-gradient(135deg,#48c78e,#06d6a0)", border:"none", borderRadius:9, color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>✓ Onayla & Sil</button>
                  <button onClick={()=>reject(req)}  style={{ flex:1, padding:"10px", background:"rgba(255,107,107,.12)", border:"1px solid rgba(255,107,107,.28)", borderRadius:9, color:"#ff6b6b", fontWeight:700, fontSize:13, cursor:"pointer" }}>✕ Reddet</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Düzenleme Talepleri ──────────────────────────────────────────────────────
function EditRequestsPanel() {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      showToast]     = useToast();
  const [processing, setProcessing] = useState(null);

  async function refresh() {
    const rows = await api.getEditRequests();
    setRequests(rows.map(r => ({ ...r, entry: typeof r.entry_data === "string" ? JSON.parse(r.entry_data) : r.entry_data, new_amount: parseFloat(r.new_amount) })));
    setLoading(false);
  }

  useEffect(() => { refresh(); const iv = setInterval(refresh, 5000); return () => clearInterval(iv); }, []);

  async function approve(req) {
    setProcessing("approve");
    await api.approveEditRequest(req.id);
    setProcessing(null);
    showToast("Düzenleme onaylandı ✓");
    refresh();
  }

  async function reject(req) {
    setProcessing("reject");
    await api.rejectEditRequest(req.id);
    setProcessing(null);
    showToast("Düzenleme talebi reddedildi.", false);
    refresh();
  }

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:680, margin:"0 auto" }}>
      {processing && (
        <div style={{ position:"fixed", inset:0, zIndex:99999, background:"rgba(0,0,0,.7)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#141228", border:`2px solid ${processing==="approve"?"#4facfe":"#ff6b6b"}`, borderRadius:20, padding:"36px 52px", display:"flex", flexDirection:"column", alignItems:"center", gap:18 }}>
            <div style={{ width:52, height:52, borderRadius:"50%", border:`4px solid ${processing==="approve"?"rgba(79,172,254,.2)":"rgba(255,107,107,.2)"}`, borderTopColor:processing==="approve"?"#4facfe":"#ff6b6b", animation:"spin 0.7s linear infinite" }} />
            <div style={{ color:"#fff", fontWeight:700, fontSize:17 }}>{processing==="approve"?"Düzenleme uygulanıyor…":"Talep reddediliyor…"}</div>
          </div>
        </div>
      )}
      <Toast toast={toast} />

      <div style={{ background:"linear-gradient(135deg,rgba(79,172,254,.15),rgba(0,242,254,.1))", border:"1px solid rgba(79,172,254,.25)", borderRadius:16, padding:"18px 22px", marginBottom:22 }}>
        <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>✏️ Düzenleme Talepleri</h2>
        <p style={{ color:"rgba(255,255,255,.4)", margin:"4px 0 0", fontSize:12 }}>Kasiyerlerden gelen düzenleme talepleri</p>
      </div>

      {loading ? <div style={{ color:"rgba(255,255,255,.3)", textAlign:"center", padding:40 }}>Yükleniyor…</div>
      : requests.length === 0 ? (
        <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:14, padding:"48px 20px", textAlign:"center" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
          <div style={{ color:"rgba(255,255,255,.3)", fontSize:14 }}>Bekleyen düzenleme talebi yok</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {requests.map(req => {
            const e = req.entry; const isG = e?.type === "giris";
            return (
              <div key={req.id} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(79,172,254,.2)", borderRadius:14, overflow:"hidden" }}>
                <div style={{ padding:"12px 18px", background:"rgba(79,172,254,.06)", borderBottom:"1px solid rgba(79,172,254,.15)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:18 }}>✏️</span>
                    <span style={{ color:"#4facfe", fontWeight:700, fontSize:13 }}>{req.kasa_name}</span>
                    <span style={{ color:"rgba(255,255,255,.35)", fontSize:11 }}>{fmtD(req.date?.slice(0,10)||req.date)}</span>
                  </div>
                  <span style={{ color:"rgba(255,255,255,.3)", fontSize:11 }}>Talep: {fmtDT(req.requested_at)}</span>
                </div>
                <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:"50%", flexShrink:0, background:isG?"rgba(72,199,142,.14)":"rgba(255,107,107,.14)", border:`1px solid ${isG?"rgba(72,199,142,.38)":"rgba(255,107,107,.38)"}`, display:"flex", alignItems:"center", justifyContent:"center", color:isG?"#48c78e":"#ff6b6b", fontSize:18, fontWeight:700 }}>{isG?"+":"−"}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ color:"rgba(255,255,255,.4)", fontSize:11, marginBottom:4 }}>MEVCUT</div>
                      <div style={{ color:"rgba(255,255,255,.7)", fontSize:14, fontWeight:600 }}>{isG?"+":"−"} {parseFloat(e?.amount||0).toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺{e?.description&&<span style={{ color:"rgba(255,255,255,.4)", fontWeight:400, marginLeft:8 }}>— {e.description}</span>}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, paddingLeft:4 }}>
                    <div style={{ width:28, height:1, background:"rgba(79,172,254,.3)" }} />
                    <span style={{ color:"#4facfe", fontSize:12 }}>→ yeni değer</span>
                    <div style={{ flex:1, height:1, background:"rgba(79,172,254,.3)" }} />
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:"50%", flexShrink:0, background:"rgba(79,172,254,.14)", border:"1px solid rgba(79,172,254,.38)", display:"flex", alignItems:"center", justifyContent:"center", color:"#4facfe", fontSize:18, fontWeight:700 }}>{isG?"+":"−"}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ color:"rgba(79,172,254,.6)", fontSize:11, marginBottom:4 }}>YENİ</div>
                      <div style={{ color:"#4facfe", fontSize:14, fontWeight:700 }}>{isG?"+":"−"} {req.new_amount.toLocaleString("tr-TR",{minimumFractionDigits:2})} ₺{req.new_description&&<span style={{ fontWeight:400, marginLeft:8 }}>— {req.new_description}</span>}</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:10 }}>
                  <button onClick={()=>approve(req)} style={{ flex:1, padding:"10px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:9, color:"#0a0814", fontWeight:700, fontSize:13, cursor:"pointer" }}>✓ Onayla & Uygula</button>
                  <button onClick={()=>reject(req)}  style={{ flex:1, padding:"10px", background:"rgba(255,107,107,.12)", border:"1px solid rgba(255,107,107,.28)", borderRadius:9, color:"#ff6b6b", fontWeight:700, fontSize:13, cursor:"pointer" }}>✕ Reddet</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Admin Paneli ─────────────────────────────────────────────────────────────
function AdminPanel({ user, onLogout }) {
  const [tab,              setTab]              = useState(null);
  const [kasaUsers,        setKasaUsers]        = useState([]);
  const [pendingCount,     setPendingCount]     = useState(0);
  const [pendingEditCount,    setPendingEditCount]    = useState(0);
  const [pendingAdvanceCount, setPendingAdvanceCount] = useState(0);
  const clock = useClock();
  const [showChat, setShowChat] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [allUsers, setAllUsers] = useState([]);

  async function refreshUsers() {
    const rows = await api.getUsers();
    const cashiers = rows.filter(u => u.role === "cashier");
    setKasaUsers(cashiers);
    if (!tab) setTab("home");
  }

  useEffect(() => {
    refreshUsers();
    api.getUsers().then(setAllUsers).catch(()=>{});
    async function checkPending() {
      const [del, edit, adv] = await Promise.all([api.getDeleteRequests(), api.getEditRequests(), api.getAllAdvances().catch(()=>[])]);
      setPendingCount(del.length);
      setPendingEditCount(edit.length);
      setPendingAdvanceCount(adv.filter(r => r.status === "pending").length);
      try { const u = await api.getUnreadCount(); setChatUnread(u.private + u.group); } catch {}
    }
    checkPending();
    const iv = setInterval(checkPending, 5000);
    return () => clearInterval(iv);
  }, []);

  const kasaTabs  = kasaUsers.map(u => ({ key:`kasa_${u.username}`, label:`🏦 Kasa ${u.kasa_no} — ${u.name}`, kasaKey:`kasa_${u.username}`, kasaName:u.name, kasaNo:u.kasa_no }));
  const homeTabs  = [{ key:"home", label:"🏠 Anasayfa" }];
  const fixedTabs = [
    { key:"delreqs",   label:"🗑️ Silme Talepleri"    },
    { key:"editreqs",  label:"✏️ Düzenleme Talepleri" },
    { key:"advances",  label:"💰 Kasa Başlangıç Bildirimi Onayı"     },
    { key:"customers", label:"👥 Müşteriler"             },
    { key:"game",      label:"🎮 Oyun"                  },
    { key:"logs",      label:"📋 İşlem Logları"         },
    { key:"settings",  label:"⚙️ Ayarlar"              },
  ];
  const grads = { home:"linear-gradient(135deg,#667eea,#764ba2)", kasa:"linear-gradient(135deg,#667eea,#764ba2)", delreqs:"linear-gradient(135deg,#ff6b6b,#ee5a24)", editreqs:"linear-gradient(135deg,#4facfe,#00f2fe)", advances:"linear-gradient(135deg,#48c78e,#06d6a0)", logs:"linear-gradient(135deg,#667eea,#764ba2)", settings:"linear-gradient(135deg,#f7971e,#ffd200)" };
  const activeKasa = kasaTabs.find(t => t.key === tab);

  return (
    <div style={{ minHeight:"100vh", background:BG, fontFamily:"Georgia,serif" }}>
      <div style={{ background:"rgba(0,0,0,.3)", borderBottom:"1px solid rgba(255,255,255,.07)", padding:"13px 26px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <span style={{ color:"#fff", fontWeight:700, fontSize:17 }}>🏦 Kültür Kasa</span>
          <span style={{ color:"rgba(255,255,255,.25)" }}>|</span>
          <span style={{ color:"#ffd700", fontSize:13 }}>👑 {user.name}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#fff", fontWeight:700, fontSize:18, letterSpacing:"2px", fontVariantNumeric:"tabular-nums" }}>{clock.time}</div>
            <div style={{ color:"rgba(255,255,255,.35)", fontSize:11, marginTop:1 }}>{clock.day}, {clock.date}</div>
          </div>
          <div style={{ width:1, height:32, background:"rgba(255,255,255,.1)" }} />
          <button onClick={()=>{ setShowChat(true); setChatUnread(0); }} style={{ padding:"7px 15px", background:"rgba(102,126,234,.15)", border:"1px solid rgba(102,126,234,.3)", borderRadius:8, color:"#a78bfa", cursor:"pointer", fontSize:12, marginRight:8, position:"relative" }}>💬{chatUnread>0&&<span style={{position:"absolute",top:-6,right:-6,background:"#ff6b6b",color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{chatUnread}</span>}</button>
          <button onClick={onLogout} style={{ padding:"7px 15px", background:"rgba(255,107,107,.12)", border:"1px solid rgba(255,107,107,.28)", borderRadius:8, color:"#ff6b6b", cursor:"pointer", fontSize:12 }}>Çıkış</button>
        </div>
      </div>
      {showChat && <Chat currentUser={user} onClose={()=>setShowChat(false)} />}
      <div style={{ padding:"24px 26px 40px" }}>
        <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap" }}>
          {[...homeTabs, ...kasaTabs, ...fixedTabs].map((t) => (
            <button key={t.key} onClick={()=>setTab(t.key)} style={{ padding:"10px 18px", border:"none", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:600, transition:"all .2s", position:"relative", background:tab===t.key?(grads[t.key]||grads.kasa):"rgba(255,255,255,.05)", color:tab===t.key?"#fff":"rgba(255,255,255,.45)", marginLeft:t.key==="game"?"12px":"0", borderLeft:t.key==="game"?"2px solid rgba(255,255,255,.15)":"none", paddingLeft:t.key==="game"?"20px":"18px" }}>
              {t.label}
              {t.key==="delreqs" && pendingCount>0 && <span style={{ position:"absolute", top:-6, right:-6, background:"#ff6b6b", color:"#fff", borderRadius:"50%", width:18, height:18, fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid #0a0814" }}>{pendingCount}</span>}
              {t.key==="editreqs" && pendingEditCount>0 && <span style={{ position:"absolute", top:-6, right:-6, background:"#4facfe", color:"#0a0814", borderRadius:"50%", width:18, height:18, fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid #0a0814" }}>{pendingEditCount}</span>}
              {t.key==="advances" && pendingAdvanceCount>0 && <span style={{ position:"absolute", top:-6, right:-6, background:"#ff6b6b", color:"#fff", borderRadius:"50%", width:18, height:18, fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid #0a0814" }}>{pendingAdvanceCount}</span>}
            </button>
          ))}
        </div>
        {tab==="home" && <DashboardPanel onTabChange={setTab} />}
        {activeKasa && <KasaPanel key={activeKasa.kasaKey} kasaKey={activeKasa.kasaKey} kasaName={activeKasa.kasaName} kasaNo={activeKasa.kasaNo} addedBy={user.name} readOnly={false} isAdmin={true} />}
        {tab==="delreqs"  && <DeleteRequestsPanel />}
        {tab==="editreqs" && <EditRequestsPanel />}
        {tab==="advances" && <AdvancesPanel />}
        {tab==="customers" && <CustomersPanel user={user} />}
        {tab==="game"     && <GamePanel user={user} />}
        {tab==="logs"     && <LogPanel />}
        {tab==="settings" && <SettingsPanel onUsersChange={refreshUsers} />}
      </div>
    </div>
  );
}

// ─── Kasiyer Paneli ───────────────────────────────────────────────────────────
function CashierPanel({ user, onLogout }) {
  const kasaKey = `kasa_${user.username}`;
  const clock   = useClock();
  const [showChat, setShowChat] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [cashierTab, setCashierTab] = useState("kasa");
  useEffect(() => {
    const iv = setInterval(async () => {
      try { const u = await api.getUnreadCount(); setChatUnread(u.private + u.group); } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div style={{ minHeight:"100vh", background:BG, fontFamily:"Georgia,serif" }}>
      <div style={{ background:"rgba(0,0,0,.3)", borderBottom:"1px solid rgba(255,255,255,.07)", padding:"13px 26px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ color:"#fff", fontWeight:700, fontSize:17 }}>🏦 Kültür Kasa</span>
          <span style={{ color:"rgba(255,255,255,.25)" }}>|</span>
          <span style={{ color:"rgba(255,255,255,.65)", fontSize:12 }}>Kasa {user.kasa} — {user.name}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#fff", fontWeight:700, fontSize:18, letterSpacing:"2px", fontVariantNumeric:"tabular-nums" }}>{clock.time}</div>
            <div style={{ color:"rgba(255,255,255,.35)", fontSize:11, marginTop:1 }}>{clock.day}, {clock.date}</div>
          </div>
          <div style={{ width:1, height:32, background:"rgba(255,255,255,.1)" }} />
          <button onClick={()=>{ setShowChat(true); setChatUnread(0); }} style={{ padding:"7px 15px", background:"rgba(102,126,234,.15)", border:"1px solid rgba(102,126,234,.3)", borderRadius:8, color:"#a78bfa", cursor:"pointer", fontSize:12, marginRight:8, position:"relative" }}>💬{chatUnread>0&&<span style={{position:"absolute",top:-6,right:-6,background:"#ff6b6b",color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{chatUnread}</span>}</button>
          <button onClick={onLogout} style={{ padding:"7px 15px", background:"rgba(255,107,107,.12)", border:"1px solid rgba(255,107,107,.28)", borderRadius:8, color:"#ff6b6b", cursor:"pointer", fontSize:12 }}>Çıkış</button>
        </div>
      </div>
      {showChat && <Chat currentUser={user} onClose={()=>setShowChat(false)} />}
      <div style={{ padding:"16px 26px 8px", display:"flex", gap:8 }}>
        <button onClick={()=>setCashierTab("kasa")} style={{ padding:"9px 18px", border:"none", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:600, background:cashierTab==="kasa"?"linear-gradient(135deg,#667eea,#764ba2)":"rgba(255,255,255,.05)", color:cashierTab==="kasa"?"#fff":"rgba(255,255,255,.45)" }}>🏦 Kasa</button>
        <button onClick={()=>setCashierTab("customers")} style={{ padding:"9px 18px", border:"none", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:600, background:cashierTab==="customers"?"linear-gradient(135deg,#4facfe,#00f2fe)":"rgba(255,255,255,.05)", color:cashierTab==="customers"?"#0a0814":"rgba(255,255,255,.45)" }}>👥 Müşteriler</button>
        <button onClick={()=>setCashierTab("game")} style={{ padding:"9px 18px", border:"none", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:600, background:cashierTab==="game"?"linear-gradient(135deg,#a78bfa,#7c3aed)":"rgba(255,255,255,.05)", color:cashierTab==="game"?"#fff":"rgba(255,255,255,.45)" }}>🎮 Oyun</button>
      </div>
      <div style={{ padding:"16px 26px 26px" }}>
        {cashierTab==="kasa" && <KasaPanel kasaKey={kasaKey} kasaName={user.name} kasaNo={user.kasa} addedBy={user.name} readOnly={false} isAdmin={false} />}
        {cashierTab==="customers" && <CustomersPanel user={user} />}
        {cashierTab==="game" && <GamePanel user={user} />}
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    const s = loadSession();
    return s ? s.user : null;
  });

  function handleLogin(u) { setUser(u); }
  async function handleLogout() { try { await api.addLog({ action: "Çıkış yaptı" }); } catch {} clearSession(); setUser(null); }

  return (
    <>
      <style>{`* { margin:0; padding:0; box-sizing:border-box; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <StagingBanner />
      {!user
        ? <LoginScreen onLogin={handleLogin} />
        : user.role === "admin"
          ? <AdminPanel user={user} onLogout={handleLogout} />
          : <CashierPanel user={user} onLogout={handleLogout} />
      }
    </>
  );
}
// TEST_MARKER_XYZ



// ─── Başlangıç Parası Badge (tarih yanı) ─────────────────────────────────────
function AdvanceBadge({ kasaKey, kasaName, selDate }) {
  const [advance, setAdvance] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const isToday = selDate === getTodayKey();

  async function refresh() {
    const data = await api.getAdvance(kasaKey, selDate).catch(() => null);
    setAdvance(data);
  }

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, [kasaKey, selDate]);

  async function send() {
    const n = parseFloat(amount.replace(",", "."));
    if (!n || n <= 0) return;
    setSending(true);
    try {
      await api.addAdvance({
        id: `${Date.now()}-${Math.random()}`,
        kasa_key: kasaKey,
        kasa_name: kasaName,
        date: selDate,
        amount: n,
        requested_at: new Date().toISOString(),
      });
      setAmount("");
      setShowInput(false);
      refresh();
    } catch (e) {}
    setSending(false);
  }

  const fmtMoney = n => parseFloat(n).toLocaleString("tr-TR", { minimumFractionDigits:2 });

  if (advance?.status === "approved") return (
    <span style={{ color:"rgba(72,199,142,.45)", fontSize:11, whiteSpace:"nowrap" }}>
      💰 {fmtMoney(advance.amount)} ₺ ✓
    </span>
  );

  if (advance?.status === "pending") return (
    <span style={{ color:"rgba(255,193,7,.5)", fontSize:11, whiteSpace:"nowrap" }}>
      💰 {fmtMoney(advance.amount)} ₺ ⏳
    </span>
  );

  if (!advance && isToday) return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      {showInput ? (
        <>
          <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} autoFocus
            placeholder="₺" min="0" step="0.01"
            onKeyDown={e=>{ if(e.key==="Enter") send(); if(e.key==="Escape") setShowInput(false); }}
            style={{ width:80, padding:"5px 8px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(72,199,142,.4)", borderRadius:6, color:"#fff", fontSize:12, outline:"none" }} />
          <button onClick={send} disabled={sending||!amount}
            style={{ padding:"5px 10px", background:"rgba(72,199,142,.2)", border:"1px solid rgba(72,199,142,.4)", borderRadius:6, color:"#48c78e", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
            {sending?"…":"Gönder"}
          </button>
          <button onClick={()=>setShowInput(false)}
            style={{ padding:"5px 8px", background:"transparent", border:"none", color:"rgba(255,255,255,.3)", fontSize:11, cursor:"pointer" }}>✕</button>
        </>
      ) : (
        <button onClick={()=>setShowInput(true)}
          style={{ padding:"5px 10px", background:"rgba(72,199,142,.08)", border:"1px solid rgba(72,199,142,.2)", borderRadius:6, color:"rgba(72,199,142,.6)", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
          💰 Kasa Başlangıç Bildirimi
        </button>
      )}
    </div>
  );

  return null;
}



// ─── Dashboard (Admin Anasayfa) ───────────────────────────────────────────────
function DashboardPanel({ onTabChange }) {
  const [summary,    setSummary]    = useState(null);
  const [todos,      setTodos]      = useState([]);
  const [notes,      setNotes]      = useState([]);
  const [events,     setEvents]     = useState([]);
  const [todoText,   setTodoText]   = useState("");
  const [noteText,   setNoteText]   = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate,  setEventDate]  = useState(getTodayKey());
  const [eventColor, setEventColor] = useState("#667eea");
  const [selDate,    setSelDate]    = useState(getTodayKey());
  const [showEventForm, setShowEventForm] = useState(false);
  const clock = useClock();

  async function refresh() {
    try {
      const [s, t, n, e] = await Promise.all([
        api.getDashboardSummary(),
        api.getTodos(),
        api.getAdminNotes(selDate),
        api.getEvents(),
      ]);
      setSummary(s);
      setTodos(t);
      setNotes(n);
      setEvents(e);
    } catch {}
  }

  useEffect(() => { refresh(); const iv = setInterval(refresh, 10000); return () => clearInterval(iv); }, [selDate]);

  const fmtMoney = n => parseFloat(n||0).toLocaleString("tr-TR", { minimumFractionDigits:2 });
  const fmtD = d => { const [y,m,dd] = (d?.slice?.(0,10)||"").split("-"); return dd ? `${dd}.${m}.${y}` : d; };
  const fmtDT = iso => new Date(iso).toLocaleString("tr-TR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });

  async function addTodo() {
    const text = todoText.trim();
    if (!text) return;
    await api.addTodo({ id: `${Date.now()}-${Math.random()}`, text });
    setTodoText("");
    refresh();
  }

  async function toggleTodo(todo) {
    await api.updateTodo(todo.id, { done: !todo.done });
    refresh();
  }

  async function deleteTodo(id) {
    await api.deleteTodo(id);
    refresh();
  }

  async function addNote() {
    const text = noteText.trim();
    if (!text) return;
    await api.addAdminNote({ id: `${Date.now()}-${Math.random()}`, text, date: selDate });
    setNoteText("");
    refresh();
  }

  async function deleteNote(id) {
    await api.deleteAdminNote(id);
    refresh();
  }

  async function addEvent() {
    const title = eventTitle.trim();
    if (!title) return;
    await api.addEvent({ id: `${Date.now()}-${Math.random()}`, title, date: eventDate, color: eventColor });
    setEventTitle(""); setShowEventForm(false);
    refresh();
  }

  async function deleteEvent(id) {
    await api.deleteEvent(id);
    refresh();
  }

  const actionColor = (action) => {
    if (action?.includes("Giriş"))   return "#48c78e";
    if (action?.includes("sildi") || action?.includes("reddetti") || action?.includes("iptal")) return "#ff6b6b";
    if (action?.includes("ekledi") || action?.includes("onayladı")) return "#4facfe";
    return "rgba(255,255,255,.4)";
  };

  // Takvim
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthNames = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  const dayNames = ["Pz","Pt","Sa","Ça","Pe","Cu","Ct"];
  const eventDates = new Set(events.map(e => e.date?.slice?.(0,10)));
  const todayStr = getTodayKey();

  const colors = ["#667eea","#48c78e","#ff6b6b","#ffd700","#4facfe","#f093fb"];

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:900, margin:"0 auto" }}>

      {/* Başlık */}
      <div style={{ background:"linear-gradient(135deg,rgba(102,126,234,.18),rgba(118,75,162,.15))", border:"1px solid rgba(102,126,234,.28)", borderRadius:16, padding:"18px 24px", marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:22, fontWeight:700 }}>🏠 Anasayfa</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"3px 0 0", fontSize:12 }}>{clock.day}, {clock.date}</p>
        </div>
        <div style={{ color:"#fff", fontSize:28, fontWeight:700, letterSpacing:"2px", fontVariantNumeric:"tabular-nums" }}>{clock.time}</div>
      </div>

      {/* Özet kartları */}
      {summary && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:12, marginBottom:20 }}>
          {[
            { label:"BUGÜN GİRİŞ", value:"+" + fmtMoney(summary.giris) + " ₺", color:"#48c78e", bg:"rgba(72,199,142,.08)", border:"rgba(72,199,142,.2)" },
            { label:"BUGÜN ÇIKIŞ", value:"−" + fmtMoney(summary.cikis) + " ₺", color:"#ff6b6b", bg:"rgba(255,107,107,.08)", border:"rgba(255,107,107,.2)" },
            { label:"SİLME TALEBİ", value:summary.pendingDelete, color:"#ff6b6b", bg:"rgba(255,107,107,.06)", border:"rgba(255,107,107,.15)", tab:"delreqs" },
            { label:"DÜZENLEME", value:summary.pendingEdit, color:"#4facfe", bg:"rgba(79,172,254,.06)", border:"rgba(79,172,254,.15)", tab:"editreqs" },
            { label:"BAŞLANGIÇ ONAYI", value:summary.pendingAdvance, color:"#ffd700", bg:"rgba(255,215,0,.06)", border:"rgba(255,215,0,.15)", tab:"advances" },
          ].map((c,i) => (
            <div key={i} onClick={c.tab ? () => onTabChange(c.tab) : undefined}
              style={{ background:c.bg, border:`1px solid ${c.border}`, borderRadius:12, padding:"14px 16px", cursor:c.tab?"pointer":"default", transition:"transform .15s" }}
              onMouseEnter={e=>{ if(c.tab) e.currentTarget.style.transform="translateY(-2px)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0)"; }}>
              <div style={{ color:"rgba(255,255,255,.35)", fontSize:9, letterSpacing:"1px", marginBottom:6 }}>{c.label}</div>
              <div style={{ color:c.color, fontSize:18, fontWeight:800 }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

        {/* Yapılacaklar */}
        <div style={{ background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px" }}>✅ YAPILACAKLAR</span>
            <span style={{ color:"rgba(255,255,255,.25)", fontSize:11 }}>{todos.filter(t=>!t.done).length} bekliyor</span>
          </div>
          <div style={{ maxHeight:220, overflowY:"auto" }}>
            {todos.length === 0
              ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.18)", fontSize:12 }}>Liste boş</div>
              : todos.map(t => (
                <div key={t.id} style={{ padding:"9px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:10 }}>
                  <div onClick={()=>toggleTodo(t)} style={{ width:18, height:18, borderRadius:5, border:`2px solid ${t.done?"#48c78e":"rgba(255,255,255,.2)"}`, background:t.done?"#48c78e":"transparent", cursor:"pointer", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#fff" }}>
                    {t.done && "✓"}
                  </div>
                  <span style={{ flex:1, color:t.done?"rgba(255,255,255,.25)":"rgba(255,255,255,.8)", fontSize:13, textDecoration:t.done?"line-through":"none" }}>{t.text}</span>
                  <button onClick={()=>deleteTodo(t.id)} style={{ background:"none", border:"none", color:"rgba(255,107,107,.4)", cursor:"pointer", fontSize:13, padding:"0 4px" }}>✕</button>
                </div>
              ))
            }
          </div>
          <div style={{ padding:"10px 18px", borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:8 }}>
            <input value={todoText} onChange={e=>setTodoText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTodo()}
              placeholder="Yeni görev…" style={{ ...inp, flex:1, fontSize:12, padding:"7px 10px" }} />
            <button onClick={addTodo} disabled={!todoText.trim()} style={{ padding:"7px 14px", background:todoText.trim()?"rgba(72,199,142,.2)":"rgba(255,255,255,.05)", border:`1px solid ${todoText.trim()?"rgba(72,199,142,.4)":"rgba(255,255,255,.1)"}`, borderRadius:8, color:todoText.trim()?"#48c78e":"rgba(255,255,255,.2)", cursor:todoText.trim()?"pointer":"not-allowed", fontSize:12 }}>Ekle</button>
          </div>
        </div>

        {/* Takvim */}
        <div style={{ background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px" }}>📅 TAKVİM — {monthNames[month].toUpperCase()} {year}</span>
            <button onClick={()=>setShowEventForm(v=>!v)} style={{ padding:"4px 10px", background:"rgba(102,126,234,.15)", border:"1px solid rgba(102,126,234,.3)", borderRadius:6, color:"#a78bfa", fontSize:11, cursor:"pointer" }}>+ Etkinlik</button>
          </div>
          <div style={{ padding:"10px 14px" }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
              {dayNames.map(d => <div key={d} style={{ textAlign:"center", color:"rgba(255,255,255,.25)", fontSize:9, padding:"2px 0" }}>{d}</div>)}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
              {Array.from({length: firstDay === 0 ? 6 : firstDay - 1}).map((_,i) => <div key={"e"+i} />)}
              {Array.from({length: daysInMonth}).map((_,i) => {
                const d = i + 1;
                const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                const isToday = dateStr === todayStr;
                const hasEvent = eventDates.has(dateStr);
                return (
                  <div key={d} style={{ textAlign:"center", padding:"4px 2px", borderRadius:6, background:isToday?"rgba(102,126,234,.3)":"transparent", border:isToday?"1px solid rgba(102,126,234,.5)":"1px solid transparent", position:"relative" }}>
                    <span style={{ color:isToday?"#fff":"rgba(255,255,255,.6)", fontSize:11, fontWeight:isToday?700:400 }}>{d}</span>
                    {hasEvent && <div style={{ width:4, height:4, borderRadius:"50%", background:"#ffd700", margin:"1px auto 0" }} />}
                  </div>
                );
              })}
            </div>
          </div>
          {showEventForm && (
            <div style={{ padding:"10px 14px", borderTop:"1px solid rgba(255,255,255,.06)" }}>
              <input value={eventTitle} onChange={e=>setEventTitle(e.target.value)} placeholder="Etkinlik adı…"
                style={{ ...inp, fontSize:12, padding:"7px 10px", marginBottom:8 }} />
              <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                <input type="date" value={eventDate} onChange={e=>setEventDate(e.target.value)}
                  style={{ ...inp, flex:1, fontSize:12, padding:"7px 10px" }} />
                <div style={{ display:"flex", gap:4 }}>
                  {colors.map(c => (
                    <div key={c} onClick={()=>setEventColor(c)} style={{ width:20, height:20, borderRadius:"50%", background:c, cursor:"pointer", border:eventColor===c?"2px solid #fff":"2px solid transparent" }} />
                  ))}
                </div>
              </div>
              <button onClick={addEvent} disabled={!eventTitle.trim()} style={{ width:"100%", padding:"7px", background:"linear-gradient(135deg,#667eea,#764ba2)", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>Etkinlik Ekle</button>
            </div>
          )}
          {events.length > 0 && (
            <div style={{ maxHeight:80, overflowY:"auto", borderTop:"1px solid rgba(255,255,255,.06)" }}>
              {events.map(e => (
                <div key={e.id} style={{ padding:"6px 14px", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid rgba(255,255,255,.04)" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:e.color, flexShrink:0 }} />
                  <span style={{ color:"rgba(255,255,255,.6)", fontSize:11, flex:1 }}>{e.title}</span>
                  <span style={{ color:"rgba(255,255,255,.25)", fontSize:10 }}>{fmtD(e.date)}</span>
                  <button onClick={()=>deleteEvent(e.id)} style={{ background:"none", border:"none", color:"rgba(255,107,107,.4)", cursor:"pointer", fontSize:11 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Notlar */}
      <div style={{ background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, overflow:"hidden", marginBottom:16 }}>
        <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px" }}>📝 NOTLAR</span>
          <select value={selDate} onChange={e=>setSelDate(e.target.value)} style={{ padding:"5px 10px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.15)", borderRadius:6, color:"#fff", fontSize:11, cursor:"pointer" }}>
            {Array.from({length:30}).map((_,i) => {
              const d = new Date(); d.setDate(d.getDate() - i);
              const s = d.toISOString().slice(0,10);
              const [y,m,dd] = s.split("-");
              return <option key={s} value={s} style={{ background:"#1a1a2e" }}>{dd}.{m}.{y}</option>;
            })}
          </select>
        </div>
        <div style={{ maxHeight:150, overflowY:"auto" }}>
          {notes.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.18)", fontSize:12 }}>Bu tarih için not yok</div>
            : notes.map(n => (
              <div key={n.id} style={{ padding:"9px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"flex-start", gap:10 }}>
                <span style={{ color:"rgba(255,255,255,.75)", fontSize:13, flex:1, lineHeight:1.5 }}>{n.text}</span>
                <button onClick={()=>deleteNote(n.id)} style={{ background:"none", border:"none", color:"rgba(255,107,107,.4)", cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
              </div>
            ))
          }
        </div>
        <div style={{ padding:"10px 18px", borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:8 }}>
          <input value={noteText} onChange={e=>setNoteText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addNote()}
            placeholder="Not ekle…" style={{ ...inp, flex:1, fontSize:12, padding:"7px 10px" }} />
          <button onClick={addNote} disabled={!noteText.trim()} style={{ padding:"7px 14px", background:noteText.trim()?"rgba(255,215,0,.15)":"rgba(255,255,255,.05)", border:`1px solid ${noteText.trim()?"rgba(255,215,0,.35)":"rgba(255,255,255,.1)"}`, borderRadius:8, color:noteText.trim()?"#ffd700":"rgba(255,255,255,.2)", cursor:noteText.trim()?"pointer":"not-allowed", fontSize:12 }}>Ekle</button>
        </div>
      </div>

      {/* Son işlemler */}
      {summary?.recentLogs?.length > 0 && (
        <div style={{ background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,.07)" }}>
            <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px" }}>📋 SON İŞLEMLER</span>
          </div>
          {summary.recentLogs.map((log,i) => (
            <div key={log.id} style={{ padding:"9px 18px", borderBottom:i<summary.recentLogs.length-1?"1px solid rgba(255,255,255,.04)":"none", display:"flex", gap:12, alignItems:"center", background:i%2?"rgba(255,255,255,.015)":"transparent" }}>
              <span style={{ color:"rgba(255,255,255,.25)", fontSize:10, whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>{fmtDT(log.created_at)}</span>
              <span style={{ color:"rgba(255,255,255,.5)", fontSize:12, width:90, flexShrink:0 }}>{log.name}</span>
              <span style={{ color:actionColor(log.action), fontSize:12 }}>{log.action}</span>
              {log.detail && <span style={{ color:"rgba(255,255,255,.25)", fontSize:11 }}>{log.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ─── Müşteriler Paneli ────────────────────────────────────────────────────────
function CustomersPanel({ user }) {
  const [customers,   setCustomers]   = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [search,      setSearch]      = useState("");
  const [searching,   setSearching]   = useState(false);
  const [showAdd,     setShowAdd]     = useState(false);
  const [showAddSub,  setShowAddSub]  = useState(false);
  const [showEdit,    setShowEdit]    = useState(false);
  const [toast,       showToast]      = useToast();
  const [lastCopied,  setLastCopied]  = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [editingSub,  setEditingSub]  = useState(null);
  const [subEditForm, setSubEditForm] = useState({ type:"Elektrik", abone_no:"", notes:"" });

  const SUBS = ["Elektrik","Doğalgaz","Su","Telefon/GSM","İnternet","Kablo TV","Diğer"];
  const SUB_ICONS = { "Elektrik":"⚡","Doğalgaz":"🔥","Su":"💧","Telefon/GSM":"📱","İnternet":"🌐","Kablo TV":"📺","Diğer":"📋" };
  const SUB_COLORS = { "Elektrik":"#ffd700","Doğalgaz":"#ff6b35","Su":"#4facfe","Telefon/GSM":"#48c78e","İnternet":"#a78bfa","Kablo TV":"#ff6b6b","Diğer":"rgba(255,255,255,.4)" };

  const [form,    setForm]    = useState({ full_name:"", tc:"", phone:"", notes:"" });
  const [editForm,setEditForm]= useState({ full_name:"", tc:"", phone:"", notes:"" });
  const [subForm, setSubForm] = useState({ type:"Elektrik", abone_no:"", notes:"" });

  async function search_customers(q) {
    setSearching(true);
    try { const rows = await api.getCustomers(q); setCustomers(rows); } catch {}
    setSearching(false);
  }

  useEffect(() => { search_customers(""); }, []);
  useEffect(() => {
    const t = setTimeout(() => search_customers(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  async function loadSelected(id) {
    try { const data = await api.getCustomer(id); setSelected(data); } catch {}
  }

  async function addCustomer() {
    if (!form.full_name.trim()) return showToast("Ad Soyad zorunlu!", false);
    try {
      const { id } = await api.addCustomer(form);
      showToast("Müşteri eklendi ✓");
      setShowAdd(false);
      setForm({ full_name:"", tc:"", phone:"", notes:"" });
      await search_customers(search);
      await loadSelected(id);
    } catch (e) { showToast(e.message, false); }
  }

  async function updateCustomer() {
    if (!editForm.full_name.trim()) return showToast("Ad Soyad zorunlu!", false);
    try {
      await api.updateCustomer(selected.id, editForm);
      showToast("Güncellendi ✓");
      setShowEdit(false);
      await search_customers(search);
      await loadSelected(selected.id);
    } catch (e) { showToast(e.message, false); }
  }

  async function deleteCustomer(id) {
    try {
      await api.deleteCustomer(id);
      showToast("Müşteri silindi");
      setSelected(null);
      setConfirmDel(false);
      search_customers(search);
    } catch (e) { showToast(e.message, false); }
  }

  async function addSubscription() {
    if (!subForm.abone_no.trim()) return showToast("Abone numarası zorunlu!", false);
    try {
      await api.addSubscription(selected.id, subForm);
      showToast("Abonelik eklendi ✓");
      setShowAddSub(false);
      setSubForm({ type:"Elektrik", abone_no:"", notes:"" });
      loadSelected(selected.id);
    } catch (e) { showToast(e.message, false); }
  }

  async function updateSubscription() {
    if (!subEditForm.abone_no.trim()) return showToast("Abone numarası zorunlu!", false);
    try {
      await api.updateSubscription(editingSub, subEditForm);
      showToast("Abonelik güncellendi ✓");
      setEditingSub(null);
      loadSelected(selected.id);
    } catch (e) { showToast(e.message, false); }
  }

  async function deleteSubscription(id) {
    try {
      await api.deleteSubscription(id);
      showToast("Abonelik silindi");
      if (lastCopied === id) setLastCopied(null);
      loadSelected(selected.id);
    } catch (e) { showToast(e.message, false); }
  }

  function copyToClipboard(text, subId) {
    navigator.clipboard.writeText(text).then(() => {
      showToast("Kopyalandı! ✓");
      setLastCopied(subId || text);
    }).catch(() => showToast("Kopyalanamadı", false));
  }

  const fmtDT = iso => new Date(iso).toLocaleString("tr-TR", { day:"2-digit", month:"2-digit", year:"numeric" });

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:900, margin:"0 auto" }}>
      <Toast toast={toast} />

      {confirmDel && (
        <div style={{ position:"fixed", inset:0, zIndex:99999, background:"rgba(0,0,0,.75)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#141228", border:"2px solid rgba(255,107,107,.5)", borderRadius:20, padding:"32px 40px", width:340, textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>🗑️</div>
            <div style={{ color:"#fff", fontWeight:700, fontSize:17, marginBottom:8 }}>Müşteriyi Sil</div>
            <div style={{ color:"rgba(255,255,255,.5)", fontSize:13, marginBottom:24 }}>
              <span style={{ color:"#ff6b6b", fontWeight:700 }}>{selected?.full_name}</span> ve tüm abonelikleri silinecek. Emin misiniz?
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setConfirmDel(false)} style={{ flex:1, padding:"11px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.14)", borderRadius:10, color:"rgba(255,255,255,.6)", cursor:"pointer" }}>İptal</button>
              <button onClick={()=>deleteCustomer(selected.id)} style={{ flex:1, padding:"11px", background:"linear-gradient(135deg,#ff6b6b,#ee5a24)", border:"none", borderRadius:10, color:"#fff", fontWeight:700, cursor:"pointer" }}>Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:"linear-gradient(135deg,rgba(79,172,254,.15),rgba(0,242,254,.1))", border:"1px solid rgba(79,172,254,.25)", borderRadius:16, padding:"16px 22px", marginBottom:18, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>👥 Müşteriler</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"3px 0 0", fontSize:12 }}>Müşteri kayıtları ve abonelik bilgileri</p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          {user?.role === "admin" && (
            <button onClick={async () => {
              try {
                const token = localStorage.getItem("kk_token");
                const res = await fetch("/api/customers/export/excel", { headers: { Authorization: "Bearer " + token } });
                if (!res.ok) throw new Error("Hata");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "musteriler_" + new Date().toISOString().slice(0,10) + ".xlsx";
                a.click();
                URL.revokeObjectURL(url);
              } catch(e) { alert("Excel indirilemedi!"); }
            }} style={{ padding:"9px 18px", background:"linear-gradient(135deg,#48c78e,#06d6a0)", border:"none", borderRadius:10, color:"#0a0814", fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
              📥 Excel'e Aktar
            </button>
          )}
          <button onClick={()=>{ setShowAdd(v=>!v); setShowEdit(false); }} style={{ padding:"9px 18px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:10, color:"#0a0814", fontWeight:700, fontSize:13, cursor:"pointer" }}>+ Yeni Müşteri</button>
        </div>
      </div>

      {showAdd && (
        <div style={{ background:"rgba(79,172,254,.05)", border:"1px solid rgba(79,172,254,.2)", borderRadius:14, padding:"20px 22px", marginBottom:18 }}>
          <div style={{ color:"#4facfe", fontWeight:700, fontSize:15, marginBottom:14 }}>👤 Yeni Müşteri Ekle</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
            <div>
              <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>AD SOYAD *</label>
              <input value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} placeholder="Ad Soyad" style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
            </div>
            <div>
              <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>TC KİMLİK NO</label>
              <input value={form.tc} onChange={e=>setForm(f=>({...f,tc:e.target.value}))} placeholder="11 haneli TC" maxLength={11} style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
            </div>
            <div>
              <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>TELEFON</label>
              <input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="05xx xxx xx xx" style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, letterSpacing:"1px", display:"block", marginBottom:5 }}>NOT</label>
            <input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Opsiyonel not..." style={{ ...inp, fontSize:13, padding:"9px 12px" }} />
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={addCustomer} style={{ flex:2, padding:"10px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:9, color:"#0a0814", fontWeight:700, cursor:"pointer" }}>Kaydet</button>
            <button onClick={()=>setShowAdd(false)} style={{ flex:1, padding:"10px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:9, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>İptal</button>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"320px 1fr", gap:16 }}>
        <div>
          <div style={{ marginBottom:10 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 İsim, TC, telefon veya abone no ara..." style={{ ...inp, width:"100%", fontSize:13, padding:"10px 14px" }} />
          </div>
          <div style={{ background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.07)", borderRadius:12, overflow:"hidden", maxHeight:500, overflowY:"auto" }}>
            {searching
              ? <div style={{ padding:20, textAlign:"center", color:"rgba(255,255,255,.3)", fontSize:12 }}>Aranıyor…</div>
              : customers.length === 0
              ? <div style={{ padding:30, textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:13 }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>👥</div>
                  {search ? "Sonuç bulunamadı" : "Henüz müşteri yok"}
                </div>
              : <div style={{ padding:"20px 16px", textAlign:"center" }}>
                  <div style={{ color:"#4facfe", fontSize:28, fontWeight:800, marginBottom:4 }}>{customers.length}</div>
                  <div style={{ color:"rgba(255,255,255,.4)", fontSize:12, marginBottom:16 }}>kayıtlı müşteri</div>
                  <div style={{ color:"#48c78e", fontSize:28, fontWeight:800, marginBottom:4 }}>{customers.reduce((t,c) => t + (c.sub_count||0), 0)}</div>
                  <div style={{ color:"rgba(255,255,255,.4)", fontSize:12, marginBottom:20 }}>toplam abonelik</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {customers.map(c => (
                      <div key={c.id} onClick={()=>{ loadSelected(c.id); setShowEdit(false); setLastCopied(null); }}
                        style={{ padding:"9px 14px", borderRadius:10, cursor:"pointer", background:selected?.id===c.id?"rgba(79,172,254,.15)":"rgba(255,255,255,.04)", border:selected?.id===c.id?"1px solid rgba(79,172,254,.4)":"1px solid rgba(255,255,255,.07)", transition:"all .15s", textAlign:"left" }}>
                        <div style={{ color:selected?.id===c.id?"#4facfe":"rgba(255,255,255,.7)", fontWeight:600, fontSize:13 }}>{c.full_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
            }
          </div>

        </div>

        <div>
          {!selected
            ? <div style={{ background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.07)", borderRadius:12, padding:"60px 20px", textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>👈</div>
                <div style={{ color:"rgba(255,255,255,.3)", fontSize:14 }}>Müşteri seçin</div>
              </div>
            : <div>
                <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(79,172,254,.2)", borderRadius:12, padding:"18px 20px", marginBottom:14 }}>
                  {!showEdit ? (
                    <>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                        <div>
                          <div style={{ color:"#fff", fontWeight:700, fontSize:20 }}>{selected.full_name}</div>
                          <div style={{ color:"rgba(255,255,255,.3)", fontSize:11, marginTop:3 }}>Ekleyen: {selected.added_by} — {fmtDT(selected.created_at)}</div>
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={()=>{ setShowEdit(true); setEditForm({ full_name:selected.full_name, tc:selected.tc||"", phone:selected.phone||"", notes:selected.notes||"" }); }} style={{ padding:"6px 12px", background:"rgba(79,172,254,.1)", border:"1px solid rgba(79,172,254,.25)", borderRadius:8, color:"#4facfe", fontSize:12, cursor:"pointer" }}>✏️ Düzenle</button>
                          <button onClick={()=>setConfirmDel(true)} style={{ padding:"6px 12px", background:"rgba(255,107,107,.1)", border:"1px solid rgba(255,107,107,.25)", borderRadius:8, color:"#ff6b6b", fontSize:12, cursor:"pointer" }}>🗑️ Sil</button>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:20 }}>
                        {selected.tc && (
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ color:"rgba(255,255,255,.35)", fontSize:12 }}>TC:</span>
                            <span style={{ color:"rgba(255,255,255,.8)", fontSize:13, fontWeight:600 }}>{selected.tc}</span>
                            <button onClick={()=>copyToClipboard(selected.tc, "tc-"+selected.id)} style={{ padding:"2px 8px", background: lastCopied==="tc-"+selected.id?"rgba(72,199,142,.2)":"rgba(255,255,255,.07)", border:"1px solid "+(lastCopied==="tc-"+selected.id?"rgba(72,199,142,.5)":"rgba(255,255,255,.12)"), borderRadius:5, color:lastCopied==="tc-"+selected.id?"#48c78e":"rgba(255,255,255,.4)", fontSize:10, cursor:"pointer", transition:"all .2s" }}>
                              {lastCopied==="tc-"+selected.id?"✓ Kopyalandı":"Kopyala"}
                            </button>
                          </div>
                        )}
                        {selected.phone && (
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ color:"rgba(255,255,255,.35)", fontSize:12 }}>📱</span>
                            <span style={{ color:"rgba(255,255,255,.8)", fontSize:13, fontWeight:600 }}>{selected.phone}</span>
                            <button onClick={()=>copyToClipboard(selected.phone, "phone-"+selected.id)} style={{ padding:"2px 8px", background: lastCopied==="phone-"+selected.id?"rgba(72,199,142,.2)":"rgba(255,255,255,.07)", border:"1px solid "+(lastCopied==="phone-"+selected.id?"rgba(72,199,142,.5)":"rgba(255,255,255,.12)"), borderRadius:5, color:lastCopied==="phone-"+selected.id?"#48c78e":"rgba(255,255,255,.4)", fontSize:10, cursor:"pointer", transition:"all .2s" }}>
                              {lastCopied==="phone-"+selected.id?"✓ Kopyalandı":"Kopyala"}
                            </button>
                          </div>
                        )}
                      </div>
                      {selected.notes && <div style={{ marginTop:10, color:"rgba(255,255,255,.4)", fontSize:12, background:"rgba(255,255,255,.04)", borderRadius:8, padding:"8px 12px" }}>📝 {selected.notes}</div>}
                    </>
                  ) : (
                    <>
                      <div style={{ color:"#4facfe", fontWeight:700, fontSize:15, marginBottom:14 }}>✏️ Müşteri Düzenle</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>AD SOYAD *</label>
                          <input value={editForm.full_name} onChange={e=>setEditForm(f=>({...f,full_name:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                        </div>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>TC KİMLİK NO</label>
                          <input value={editForm.tc} onChange={e=>setEditForm(f=>({...f,tc:e.target.value}))} maxLength={11} style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                        </div>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>TELEFON</label>
                          <input value={editForm.phone} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                        </div>
                      </div>
                      <div style={{ marginBottom:12 }}>
                        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>NOT</label>
                        <input value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                      </div>
                      <div style={{ display:"flex", gap:10 }}>
                        <button onClick={updateCustomer} style={{ flex:2, padding:"9px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:8, color:"#0a0814", fontWeight:700, cursor:"pointer" }}>Kaydet</button>
                        <button onClick={()=>setShowEdit(false)} style={{ flex:1, padding:"9px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>İptal</button>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.08)", borderRadius:12, overflow:"hidden" }}>
                  <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ color:"rgba(255,255,255,.5)", fontSize:11, letterSpacing:"1px" }}>📋 ABONELİKLER <span style={{ color:"#4facfe", fontWeight:700 }}>({selected.subscriptions?.length || 0})</span></span>
                    <button onClick={()=>setShowAddSub(v=>!v)} style={{ padding:"5px 12px", background:"rgba(79,172,254,.15)", border:"1px solid rgba(79,172,254,.3)", borderRadius:7, color:"#4facfe", fontSize:11, cursor:"pointer" }}>+ Abonelik Ekle</button>
                  </div>

                  {showAddSub && (
                    <div style={{ padding:"14px 18px", borderBottom:"1px solid rgba(255,255,255,.07)", background:"rgba(79,172,254,.04)" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>TÜR</label>
                          <select value={subForm.type} onChange={e=>setSubForm(f=>({...f,type:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px", cursor:"pointer" }}>
                            {SUBS.map(s => <option key={s} value={s} style={{ background:"#1a1a2e" }}>{SUB_ICONS[s]} {s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>ABONE NUMARASI *</label>
                          <input value={subForm.abone_no} onChange={e=>setSubForm(f=>({...f,abone_no:e.target.value}))} placeholder="Abone no girin" style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                        </div>
                      </div>
                      <div style={{ marginBottom:10 }}>
                        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>NOT (opsiyonel)</label>
                        <input value={subForm.notes} onChange={e=>setSubForm(f=>({...f,notes:e.target.value}))} placeholder="Ek bilgi..." style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={addSubscription} style={{ flex:2, padding:"8px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:8, color:"#0a0814", fontWeight:700, cursor:"pointer", fontSize:13 }}>Kaydet</button>
                        <button onClick={()=>setShowAddSub(false)} style={{ flex:1, padding:"8px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>İptal</button>
                      </div>
                    </div>
                  )}

                  {editingSub && (
                    <div style={{ padding:"14px 18px", borderBottom:"1px solid rgba(255,255,255,.07)", background:"rgba(79,172,254,.04)" }}>
                      <div style={{ color:"#4facfe", fontSize:12, fontWeight:700, marginBottom:10 }}>✏️ Abonelik Düzenle</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>TÜR</label>
                          <select value={subEditForm.type} onChange={e=>setSubEditForm(f=>({...f,type:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px", cursor:"pointer" }}>
                            {SUBS.map(s => <option key={s} value={s} style={{ background:"#1a1a2e" }}>{SUB_ICONS[s]} {s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>ABONE NUMARASI *</label>
                          <input value={subEditForm.abone_no} onChange={e=>setSubEditForm(f=>({...f,abone_no:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                        </div>
                      </div>
                      <div style={{ marginBottom:10 }}>
                        <label style={{ color:"rgba(255,255,255,.4)", fontSize:11, display:"block", marginBottom:5 }}>NOT</label>
                        <input value={subEditForm.notes} onChange={e=>setSubEditForm(f=>({...f,notes:e.target.value}))} style={{ ...inp, fontSize:13, padding:"8px 10px" }} />
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={updateSubscription} style={{ flex:2, padding:"8px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:8, color:"#0a0814", fontWeight:700, cursor:"pointer", fontSize:13 }}>Güncelle</button>
                        <button onClick={()=>setEditingSub(null)} style={{ flex:1, padding:"8px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:8, color:"rgba(255,255,255,.5)", cursor:"pointer" }}>İptal</button>
                      </div>
                    </div>
                  )}

                  {!selected.subscriptions || selected.subscriptions.length === 0
                    ? <div style={{ padding:"30px 20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:13 }}>Henüz abonelik kaydı yok</div>
                    : selected.subscriptions.map(s => {
                        const isCopied = lastCopied === s.id;
                        const color = SUB_COLORS[s.type] || "rgba(255,255,255,.4)";
                        return (
                          <div key={s.id} style={{ padding:"14px 18px", borderBottom:"1px solid rgba(255,255,255,.05)", display:"flex", alignItems:"center", gap:12, background: isCopied?"rgba(72,199,142,.06)":"transparent", border: isCopied?"1px solid rgba(72,199,142,.3)":"1px solid transparent", borderRadius: isCopied?10:0, margin: isCopied?"4px 8px":0, transition:"all .3s" }}>
                            <div style={{ width:38, height:38, borderRadius:10, background: isCopied?"rgba(72,199,142,.15)":"rgba(255,255,255,.06)", border:"1px solid "+(isCopied?"rgba(72,199,142,.4)":"rgba(255,255,255,.1)"), display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0, transition:"all .3s" }}>
                              {SUB_ICONS[s.type] || "📋"}
                            </div>
                            <div style={{ flex:1 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                                <span style={{ color: isCopied?"#48c78e":color, fontSize:12, fontWeight:700 }}>{s.type}</span>
                                {isCopied && <span style={{ color:"#48c78e", fontSize:10, background:"rgba(72,199,142,.1)", border:"1px solid rgba(72,199,142,.3)", borderRadius:10, padding:"1px 6px" }}>✓ Son kopyalanan</span>}
                              </div>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <span style={{ color: isCopied?"#fff":"rgba(255,255,255,.8)", fontSize:14, fontWeight:700, fontFamily:"monospace", letterSpacing:"1px" }}>{s.abone_no}</span>
                                <button onClick={()=>copyToClipboard(s.abone_no, s.id)} style={{ padding:"3px 10px", background: isCopied?"rgba(72,199,142,.2)":"rgba(79,172,254,.1)", border:"1px solid "+(isCopied?"rgba(72,199,142,.4)":"rgba(79,172,254,.25)"), borderRadius:6, color: isCopied?"#48c78e":"#4facfe", fontSize:11, cursor:"pointer", fontWeight:600, transition:"all .3s" }}>
                                  {isCopied?"✓ Kopyalandı":"Kopyala"}
                                </button>
                              </div>
                              {s.notes && <div style={{ color:"rgba(255,255,255,.3)", fontSize:11, marginTop:3 }}>{s.notes}</div>}
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                              <button onClick={()=>{ setEditingSub(s.id); setSubEditForm({ type:s.type, abone_no:s.abone_no, notes:s.notes||"" }); }} style={{ padding:"4px 10px", background:"rgba(79,172,254,.08)", border:"1px solid rgba(79,172,254,.2)", borderRadius:6, color:"#4facfe", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>✏️ Düzenle</button>
                              <button onClick={()=>deleteSubscription(s.id)} style={{ padding:"4px 10px", background:"rgba(255,107,107,.08)", border:"1px solid rgba(255,107,107,.2)", borderRadius:6, color:"rgba(255,107,107,.7)", fontSize:11, cursor:"pointer" }}>Sil</button>
                            </div>
                          </div>
                        );
                      })
                  }
                </div>
              </div>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Log Paneli (Admin) ───────────────────────────────────────────────────────
function LogPanel() {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  async function refresh(manual = false) {
    if (manual) setRefreshing(true);
    try {
      const rows = await api.getLogs();
      setLogs(rows);
    } catch {}
    setLoading(false);
    if (manual) setTimeout(() => setRefreshing(false), 600);
  }

  useEffect(() => { refresh(); const iv = setInterval(() => refresh(), 10000); return () => clearInterval(iv); }, []);

  const fmtDT = iso => new Date(iso).toLocaleString("tr-TR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit", second:"2-digit" });

  const actionColor = (action) => {
    if (action.includes("Giriş"))    return "#48c78e";
    if (action.includes("sildi") || action.includes("reddetti") || action.includes("iptal")) return "#ff6b6b";
    if (action.includes("ekledi") || action.includes("onayladı")) return "#4facfe";
    if (action.includes("düzenledi") || action.includes("talebi")) return "#ffd700";
    return "rgba(255,255,255,.5)";
  };

  const actionIcon = (action) => {
    if (action.includes("Giriş"))    return "🔑";
    if (action.includes("sildi") || action.includes("reddetti") || action.includes("iptal")) return "🗑️";
    if (action.includes("ekledi") || action.includes("onayladı")) return "✅";
    if (action.includes("düzenledi") || action.includes("talebi")) return "✏️";
    if (action.includes("Başlangıç")) return "💰";
    return "📋";
  };

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:720, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(102,126,234,.15),rgba(118,75,162,.1))", border:"1px solid rgba(102,126,234,.25)", borderRadius:16, padding:"18px 22px", marginBottom:22, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>📋 İşlem Logları</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"4px 0 0", fontSize:12 }}>Sistemde yapılan tüm işlemlerin kaydı (Son 6 ay)</p>
        </div>
        <button onClick={()=>refresh(true)} style={{ padding:"7px 14px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.14)", borderRadius:8, color:refreshing?"#48c78e":"rgba(255,255,255,.5)", fontSize:12, cursor:"pointer", transition:"color .3s" }}>
          <span style={{ display:"inline-block", animation: refreshing ? "spin 0.6s linear" : "none" }}>↻</span> {refreshing ? "Yenilendi ✓" : "Yenile"}
        </button>
      </div>

      {loading
        ? <div style={{ color:"rgba(255,255,255,.3)", textAlign:"center", padding:40 }}>Yükleniyor…</div>
        : logs.length === 0
        ? <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:14, padding:"48px 20px", textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:14 }}>Henüz log kaydı yok</div>
          </div>
        : <div style={{ background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.07)", borderRadius:14, overflow:"hidden" }}>
            <div style={{ padding:"10px 20px", borderBottom:"1px solid rgba(255,255,255,.07)", display:"grid", gridTemplateColumns:"160px 120px 1fr 1fr", gap:12 }}>
              <span style={{ color:"rgba(255,255,255,.3)", fontSize:10, letterSpacing:"1px" }}>TARİH & SAAT</span>
              <span style={{ color:"rgba(255,255,255,.3)", fontSize:10, letterSpacing:"1px" }}>KULLANICI</span>
              <span style={{ color:"rgba(255,255,255,.3)", fontSize:10, letterSpacing:"1px" }}>İŞLEM</span>
              <span style={{ color:"rgba(255,255,255,.3)", fontSize:10, letterSpacing:"1px" }}>DETAY</span>
            </div>
            {logs.map((log, i) => (
              <div key={log.id} style={{ padding:"11px 20px", borderBottom: i < logs.length-1 ? "1px solid rgba(255,255,255,.04)" : "none", display:"grid", gridTemplateColumns:"160px 120px 1fr 1fr", gap:12, alignItems:"center", background: i%2 ? "rgba(255,255,255,.015)" : "transparent" }}>
                <span style={{ color:"rgba(255,255,255,.3)", fontSize:11, fontVariantNumeric:"tabular-nums" }}>{fmtDT(log.created_at)}</span>
                <span style={{ color:"rgba(255,255,255,.6)", fontSize:12, fontWeight:600 }}>{log.name}</span>
                <span style={{ color: actionColor(log.action), fontSize:12 }}>{actionIcon(log.action)} {log.action}</span>
                <span style={{ color:"rgba(255,255,255,.3)", fontSize:11 }}>{log.detail || "—"}</span>
              </div>
            ))}
          </div>
      }
    </div>
  );
}



// ─── Oyun Paneli (Ana) ───────────────────────────────────────────────────────
function GamePanel({ user }) {
  const [gameTab, setGameTab] = useState("runner");
  const GAME_TABS = [
    { key:"runner",   label:"⚡ USB'yi Koru", grad:"linear-gradient(135deg,#a78bfa,#7c3aed)", dark:false },
    { key:"snake",    label:"🐍 Yılan",        grad:"linear-gradient(135deg,#06d6a0,#048a81)", dark:false },
    { key:"chess",    label:"♟️ Satranç",       grad:"linear-gradient(135deg,#ffd700,#f7971e)", dark:true  },
    { key:"space",    label:"🚀 Uzay",          grad:"linear-gradient(135deg,#4facfe,#00f2fe)", dark:true  },
    { key:"tetris",   label:"🟦 Tetris",        grad:"linear-gradient(135deg,#a78bfa,#7c3aed)", dark:false },
    { key:"pingpong", label:"🏓 Ping Pong",     grad:"linear-gradient(135deg,#4facfe,#00f2fe)", dark:true  },
    { key:"pacman",   label:"👻 Pac-Man",       grad:"linear-gradient(135deg,#ffd700,#f7971e)", dark:true  },
    { key:"flappy",   label:"🐦 Flappy Kuş",    grad:"linear-gradient(135deg,#ffd700,#f7971e)", dark:true  },
    { key:"shooter",  label:"🎯 Shooter",         grad:"linear-gradient(135deg,#ff6b6b,#ee5a24)", dark:false },
    { key:"wordle",   label:"📰 Çapraz Bulmaca",    grad:"linear-gradient(135deg,#48c78e,#06d6a0)", dark:true  },
    { key:"batak",    label:"🃏 Batak",               grad:"linear-gradient(135deg,#e74c3c,#c0392b)", dark:false },
  ];
  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, padding:"12px 14px", marginBottom:16, display:"flex", gap:8, flexWrap:"wrap" }}>
        {GAME_TABS.map(t => (
          <button key={t.key} onClick={()=>setGameTab(t.key)}
            style={{ padding:"9px 16px", border:"none", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:600, background:gameTab===t.key?t.grad:"rgba(255,255,255,.05)", color:gameTab===t.key?(t.dark?"#0a0814":"#fff"):"rgba(255,255,255,.45)" }}>
            {t.label}
          </button>
        ))}
      </div>
      {gameTab==="runner"   && <RunnerGame   user={user} />}
      {gameTab==="snake"    && <SnakeGame    user={user} />}
      {gameTab==="chess"    && <ChessGame    user={user} />}
      {gameTab==="space"    && <SpaceGame    user={user} />}
      {gameTab==="tetris"   && <TetrisGame   user={user} />}
      {gameTab==="pingpong" && <PingPongGame user={user} />}
      {gameTab==="pacman"   && <PacmanGame   user={user} />}
      {gameTab==="flappy"   && <FlappyGame   user={user} />}
      {gameTab==="shooter"  && <ShooterGame  user={user} />}
      {gameTab==="wordle"   && <WordleGame   user={user} />}
      {gameTab==="batak"    && <BatakGame    user={user} />}
    </div>
  );
}

// ─── USB Runner Oyunu ─────────────────────────────────────────────────────────
function RunnerGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false,
    score: 0, hiScore: 0, speed: 4, frame: 0,
    player: { x: 80, y: 0, vy: 0, jumping: false, w: 36, h: 52 },
    obstacles: [], particles: [], stars: [],
    groundY: 0, animFrame: null, timeOfDay: 0,
  });
  const [display, setDisplay] = useState({ score: 0, hiScore: 0, over: false, started: false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  async function loadScores() {
    try { const rows = await api.getScores("runner"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;
    g.groundY = H - 70;
    g.player.y = g.groundY - g.player.h;
    g.stars = Array.from({length:60}, () => ({
      x: Math.random() * W, y: Math.random() * (H * 0.7),
      size: Math.random() * 2 + 0.5, twinkle: Math.random() * Math.PI * 2,
    }));

    const VIRUS_TYPES = [
      { color:"#ff6b6b", accent:"#ff4444", name:"basic" },
      { color:"#ff3399", accent:"#ff0066", name:"spike" },
      { color:"#aa00ff", accent:"#7700cc", name:"fat" },
      { color:"#ff6600", accent:"#cc4400", name:"flying" },
    ];

    function spawnObstacle() {
      const type = VIRUS_TYPES[Math.floor(Math.random() * VIRUS_TYPES.length)];
      const isFlying = type.name === "flying";
      const isFat = type.name === "fat";
      g.obstacles.push({
        x: W + 20,
        y: isFlying ? g.groundY - 90 - Math.random() * 30 : g.groundY - (isFat ? 60 : 45),
        w: isFat ? 50 : 32, h: isFat ? 60 : 45,
        type, wobble: 0, wing: 0,
      });
    }

    function jump() {
      if (!g.started) { g.started = true; g.running = true; setDisplay(d => ({...d, started:true})); return; }
      if (g.over) { restart(); return; }
      if (!g.player.jumping) { g.player.vy = -15; g.player.jumping = true; }
    }

    function restart() {
      g.running = true; g.over = false; g.started = true;
      g.score = 0; g.speed = 4; g.frame = 0; g.timeOfDay = 0;
      g.obstacles = []; g.particles = [];
      g.player.y = g.groundY - g.player.h;
      g.player.vy = 0; g.player.jumping = false;
      setDisplay(d => ({...d, over:false, score:0, started:true}));
    }

    function drawUSB(ctx, p) {
      const legOff = p.jumping ? 0 : Math.sin(g.frame * 0.35) * 7;
      ctx.fillStyle = "#a78bfa";
      ctx.beginPath(); ctx.roundRect(p.x, p.y + 14, p.w, p.h - 14, 6); ctx.fill();
      ctx.fillStyle = "#7c3aed";
      ctx.fillRect(p.x + 8, p.y, p.w - 16, 18);
      ctx.fillStyle = "#c4b5fd";
      ctx.fillRect(p.x + 11, p.y + 3, p.w - 22, 10);
      ctx.fillStyle = "#1a1a2e";
      ctx.beginPath(); ctx.arc(p.x + 10, p.y + 26, 4, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(p.x + p.w - 10, p.y + 26, 4, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(p.x + 11, p.y + 25, 2, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(p.x + p.w - 9, p.y + 25, 2, 0, Math.PI*2); ctx.fill();
      ctx.strokeStyle = "#1a1a2e"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(p.x + p.w/2, p.y + 36, 6, 0, Math.PI); ctx.stroke();
      ctx.fillStyle = "#8b5cf6";
      ctx.fillRect(p.x - 8, p.y + 20 + legOff, 10, 5);
      ctx.fillRect(p.x + p.w - 2, p.y + 20 - legOff, 10, 5);
      if (!p.jumping) {
        ctx.fillStyle = "#7c3aed";
        ctx.fillRect(p.x + 4, p.y + p.h, 10, 8 + legOff);
        ctx.fillRect(p.x + p.w - 14, p.y + p.h, 10, 8 - legOff);
      }
      ctx.strokeStyle = "rgba(167,139,250,.3)"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(p.x + p.w/2, p.y + p.h/2, 34, 0, Math.PI*2); ctx.stroke();
    }

    function drawVirus(ctx, o) {
      o.wobble += 0.1;
      const w = Math.sin(o.wobble) * 3;
      const { color, accent, name } = o.type;
      if (name === "flying") {
        o.wing += 0.2;
        const wOff = Math.sin(o.wing) * 12;
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(o.x + o.w/2, o.y + o.h/2, o.w/2, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = accent;
        ctx.beginPath(); ctx.ellipse(o.x + o.w/2, o.y + wOff, o.w/2 - 4, 8, -0.3, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(o.x + o.w/2, o.y + o.h - wOff, o.w/2 - 4, 8, 0.3, 0, Math.PI*2); ctx.fill();
      } else if (name === "spike") {
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(o.x + o.w/2, o.y + o.h/2 + w, o.w/2, 0, Math.PI*2); ctx.fill();
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2 + o.wobble;
          ctx.fillStyle = accent;
          ctx.beginPath();
          ctx.moveTo(o.x + o.w/2 + Math.cos(angle) * (o.w/2), o.y + o.h/2 + Math.sin(angle) * (o.h/2) + w);
          ctx.lineTo(o.x + o.w/2 + Math.cos(angle) * (o.w/2 + 10), o.y + o.h/2 + Math.sin(angle) * (o.h/2 + 10) + w);
          ctx.lineTo(o.x + o.w/2 + Math.cos(angle + 0.2) * (o.w/2), o.y + o.h/2 + Math.sin(angle + 0.2) * (o.h/2) + w);
          ctx.fill();
        }
      } else {
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(o.x + o.w/2, o.y + o.h/2 + w, o.w/2, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = accent;
        ctx.beginPath(); ctx.arc(o.x + o.w/2 - 5, o.y + o.h/2 - 5 + w, o.w/4, 0, Math.PI*2); ctx.fill();
      }
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(o.x + o.w/2 - 7, o.y + o.h/2 - 4 + w, 5, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(o.x + o.w/2 + 7, o.y + o.h/2 - 4 + w, 5, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = "#1a1a2e";
      ctx.beginPath(); ctx.arc(o.x + o.w/2 - 6, o.y + o.h/2 - 3 + w, 3, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(o.x + o.w/2 + 8, o.y + o.h/2 - 3 + w, 3, 0, Math.PI*2); ctx.fill();
    }

    function getSkyColors(tod) {
      if (tod < 0.33) {
        const t = tod / 0.33;
        return { top: "rgb(" + Math.floor(10+t*30) + "," + Math.floor(8+t*20) + "," + Math.floor(20+t*40) + ")", bot: "rgb(" + Math.floor(28+t*40) + "," + Math.floor(21+t*30) + "," + Math.floor(53+t*50) + ")", gnd: "rgb(" + Math.floor(45+t*30) + "," + Math.floor(27+t*20) + "," + Math.floor(105+t*30) + ")" };
      } else if (tod < 0.66) {
        const t = (tod - 0.33) / 0.33;
        return { top: "rgb(" + Math.floor(40+t*150) + "," + Math.floor(40+t*120) + "," + Math.floor(90+t*100) + ")", bot: "rgb(" + Math.floor(68+t*150) + "," + Math.floor(51+t*130) + "," + Math.floor(103+t*80) + ")", gnd: "rgb(" + Math.floor(75+t*60) + "," + Math.floor(47+t*50) + "," + Math.floor(135+t*40) + ")" };
      } else {
        const t = (tod - 0.66) / 0.34;
        return { top: "rgb(" + Math.floor(190-t*180) + "," + Math.floor(160-t*152) + "," + Math.floor(190-t*170) + ")", bot: "rgb(" + Math.floor(218-t*190) + "," + Math.floor(181-t*160) + "," + Math.floor(183-t*155) + ")", gnd: "rgb(" + Math.floor(135-t*90) + "," + Math.floor(97-t*70) + "," + Math.floor(175-t*70) + ")" };
      }
    }

    function loop() {
      ctx.clearRect(0, 0, W, H);
      const tod = g.timeOfDay;
      const { top, bot, gnd } = getSkyColors(tod);
      const sky = ctx.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, top); sky.addColorStop(1, bot);
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);

      const starAlpha = tod < 0.25 ? 1 : tod < 0.4 ? 1 - (tod - 0.25) / 0.15 : tod > 0.8 ? (tod - 0.8) / 0.2 : 0;
      if (starAlpha > 0) {
        g.stars.forEach(s => {
          s.twinkle += 0.05;
          ctx.fillStyle = "rgba(255,255,255," + (starAlpha * (0.5 + Math.sin(s.twinkle) * 0.5)) + ")";
          ctx.beginPath(); ctx.arc(s.x, s.y, s.size, 0, Math.PI*2); ctx.fill();
        });
      }

      if (tod > 0.3 && tod < 0.8) {
        const sunX = W * ((tod - 0.3) / 0.5);
        const sunY = g.groundY * 0.3 + Math.sin((tod - 0.3) / 0.5 * Math.PI) * (-g.groundY * 0.2);
        ctx.fillStyle = tod > 0.65 ? "#ff9933" : "#ffd700";
        ctx.beginPath(); ctx.arc(sunX, sunY, 22, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = "rgba(255,215,0,.15)";
        ctx.beginPath(); ctx.arc(sunX, sunY, 35, 0, Math.PI*2); ctx.fill();
      }

      ctx.fillStyle = gnd; ctx.fillRect(0, g.groundY, W, H - g.groundY);
      ctx.fillStyle = "rgba(102,126,234,.6)"; ctx.fillRect(0, g.groundY, W, 3);
      for (let i = 0; i < 12; i++) {
        const gx = ((i * 70 - g.frame * g.speed * 0.4) % W + W) % W;
        ctx.fillStyle = "rgba(255,255,255,.1)";
        ctx.fillRect(gx, g.groundY + 8, 25, 2);
      }

      if (!g.started) {
        ctx.fillStyle = "rgba(255,255,255,.92)"; ctx.font = "bold 24px Georgia"; ctx.textAlign = "center";
        ctx.fillText("USB'YI KORU!", W/2, H/2 - 38);
        ctx.font = "13px Georgia"; ctx.fillStyle = "rgba(255,255,255,.5)";
        ctx.fillText("Viruslerden kac! SPACE / Tikla - Zipla", W/2, H/2 - 4);
        ctx.textAlign = "left";
        drawUSB(ctx, g.player);
        g.animFrame = requestAnimationFrame(loop);
        return;
      }

      if (g.running) {
        g.frame++;
        g.score += 0.1;
        g.speed = 4 + g.score * 0.012;
        g.timeOfDay = (g.timeOfDay + 0.0003) % 1;
        const p = g.player;
        p.vy += 0.75; p.y += p.vy;
        if (p.y >= g.groundY - p.h) { p.y = g.groundY - p.h; p.vy = 0; p.jumping = false; }
        const interval = Math.max(55, 110 - Math.floor(g.score / 8));
        if (g.frame % interval === 0) spawnObstacle();
        g.obstacles = g.obstacles.filter(o => o.x > -80);
        g.obstacles.forEach(o => { o.x -= g.speed; });
        g.obstacles.forEach(o => {
          const pad = 8;
          if (p.x + pad < o.x + o.w - pad && p.x + p.w - pad > o.x + pad &&
              p.y + pad < o.y + o.h - pad && p.y + p.h - pad > o.y + pad) {
            g.running = false; g.over = true;
            const finalScore = Math.floor(g.score);
            if (finalScore > g.hiScore) g.hiScore = finalScore;
            api.saveScore(finalScore, "runner").catch(() => {});
            loadScores();
            setDisplay({ score: finalScore, hiScore: Math.floor(g.hiScore), over: true, started: true });
            for (let i = 0; i < 25; i++) {
              g.particles.push({ x: p.x + p.w/2, y: p.y + p.h/2, vx:(Math.random()-0.5)*8, vy:(Math.random()-0.5)*8, life:50, color:["#a78bfa","#ffd700","#48c78e","#ff6b6b"][Math.floor(Math.random()*4)] });
            }
          }
        });
        setDisplay(d => ({...d, score: Math.floor(g.score), hiScore: Math.floor(g.hiScore)}));
      }

      g.particles = g.particles.filter(p => p.life > 0);
      g.particles.forEach(p => {
        ctx.fillStyle = p.color; ctx.globalAlpha = p.life / 50;
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI*2); ctx.fill();
        p.x += p.vx; p.y += p.vy; p.vy += 0.2; p.life--;
      });
      ctx.globalAlpha = 1;
      g.obstacles.forEach(o => drawVirus(ctx, o));
      drawUSB(ctx, g.player);

      if (g.over) {
        ctx.fillStyle = "rgba(0,0,0,.65)"; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = "#ff6b6b"; ctx.font = "bold 30px Georgia"; ctx.textAlign = "center";
        ctx.fillText("VIRUS CARPTI!", W/2, H/2 - 24);
        ctx.fillStyle = "rgba(255,255,255,.6)"; ctx.font = "14px Georgia";
        ctx.fillText("SPACE / Tikla - Tekrar Oyna", W/2, H/2 + 16);
        ctx.textAlign = "left";
      }
      g.animFrame = requestAnimationFrame(loop);
    }

    g.animFrame = requestAnimationFrame(loop);
    const onKey = e => { if (e.code === "Space" || e.code === "ArrowUp") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", onKey);
    canvas.addEventListener("click", jump);
    canvas.addEventListener("touchend", jump);
    return () => { cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown", onKey); };
  }, []);

  const medals = ["gold","silver","#cd7f32"];
  const medalEmoji = ["gold","silver","#cd7f32"];

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(167,139,250,.15),rgba(124,58,237,.1))", border:"1px solid rgba(167,139,250,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>USB yi Koru!</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>Space / Tikla - Zipla | Viruslerden kac!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#a78bfa", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN IYI: {display.hiScore}</div>
          </div>
          <button onClick={() => { setShowBoard(v => !v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={720} height={230} style={{ width:"100%", borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"pointer", display:"block" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>SKOR TABLOSU</span>
          </div>
          {scores.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henuz skor yok</div>
            : scores.map((s, i) => (
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color: i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#ffd700", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}












// ─── Batak Kart Oyunu ─────────────────────────────────────────────────────────
function BatakGame({ user }) {
  const SUITS = ["♠","♥","♦","♣"];
  const VALUES = ["2","3","4","5","6","7","8","9","10","J","Q","K","A"];
  const SUIT_COLORS = { "♠":"#1a1a2e","♥":"#e74c3c","♦":"#e74c3c","♣":"#1a1a2e" };

  function createDeck() {
    const deck = [];
    for (const s of SUITS) for (const v of VALUES) deck.push({s,v,val:VALUES.indexOf(v)});
    return deck.sort(()=>Math.random()-0.5);
  }

  function cardPower(card, trump, leadSuit) {
    if (card.s===trump) return 100 + card.val;
    if (card.s===leadSuit) return 50 + card.val;
    return card.val;
  }

  const [gameState, setGameState] = useState("bid"); // bid, play, result
  const [deck,      setDeck]      = useState([]);
  const [hands,     setHands]     = useState([[],[],[],[]]); // 0=player
  const [scores,    setScores]    = useState([0,0,0,0]);
  const [bids,      setBids]      = useState([null,null,null,null]);
  const [trump,     setTrump]     = useState(null);
  const [currentBid, setCurrentBid] = useState(0);
  const [bidder,    setBidder]    = useState(null);
  const [table,     setTable]     = useState([null,null,null,null]);
  const [turn,      setTurn]      = useState(0);
  const [tricks,    setTricks]    = useState([0,0,0,0]);
  const [leadSuit,  setLeadSuit]  = useState(null);
  const [message,   setMessage]   = useState("");
  const [round,     setRound]     = useState(1);
  const [totalScores, setTotalScores] = useState([0,0,0,0]);
  const [gameOver,  setGameOver]  = useState(false);
  const [apiScores, setApiScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);
  const [bidPhase,  setBidPhase]  = useState(0); // hangi oyuncu bid yapıyor
  const [selectedCard, setSelectedCard] = useState(null);

  async function loadApiScores() {
    try { const rows = await api.getScores("batak"); setApiScores(rows); } catch {}
  }

  useEffect(() => { loadApiScores(); startGame(); }, []);

  function startGame() {
    const d = createDeck();
    const h = [[],[],[],[]];
    for (let i=0;i<52;i++) h[i%4].push(d[i]);
    h.forEach(hand => hand.sort((a,b)=>a.s.localeCompare(b.s)||a.val-b.val));
    setDeck(d); setHands(h);
    setScores([0,0,0,0]); setBids([null,null,null,null]);
    setTrump(null); setCurrentBid(4); setBidder(null);
    setTable([null,null,null,null]); setTurn(0);
    setTricks([0,0,0,0]); setLeadSuit(null);
    setGameState("showHand"); // Önce eli göster
    setMessage("Elinize bakın! Kaç el alabilirsiniz? İhale min. 5");
    setGameOver(false);
  }

  function aiBid(hand, minBid) {
    const strongCards = hand.filter(c=>c.val>=9).length;
    const estimate = Math.floor(strongCards * 1.5);
    if (estimate >= minBid) return Math.max(minBid, estimate);
    return null; // pas
  }

  function handleBid(bid) {
    const newBids = [...bids];
    let cb = currentBid;
    let bd = bidder;

    if (bid === null) {
      newBids[0] = "pas";
    } else {
      newBids[0] = bid;
      if (bid > cb) { cb = bid; bd = 0; }
    }

    // AI bidleri sırayla
    for (let i=1;i<4;i++) {
      const aib = aiBid(hands[i], cb+1);
      if (aib && aib > cb) { newBids[i]=aib; cb=aib; bd=i; }
      else newBids[i]="pas";
    }

    setBids([...newBids]);
    setCurrentBid(cb);
    setBidder(bd);

    if (bd !== null) {
      if (bd === 0) {
        setMessage(`Sen ${cb} el teklif verdin! Koz seç:`);
        setGameState("trump");
      } else {
        // AI kazandı - en güçlü seriden koz seç
        const aiHand = hands[bd];
        const suitCounts = SUITS.map(s=>({s,count:aiHand.filter(c=>c.s===s).length}));
        const bestSuit = suitCounts.sort((a,b)=>b.count-a.count)[0].s;
        setMessage(`${["AI-1","AI-2","AI-3"][bd-1]} ${cb} el teklif verdi! Koz: ${bestSuit}`);
        setTrump(bestSuit);
        setGameState("play");
        setTurn(bd);
        setTimeout(()=>aiStartTrick(hands, bd), 800);
      }
    } else {
      // Kimse teklif vermedi - minimum 5 ile ilk oyuncuya ver
      setMessage("Kimse teklif vermedi! İhale sana (5 el) kaldı, koz seç:");
      setBidder(0); setCurrentBid(5);
      setGameState("trump");
    }
  }

  function handleTrump(suit) {
    setTrump(suit);
    setGameState("play");
    setTurn(bidder||0);
    setMessage(`Koz: ${suit} | ${bidder===0?"Sen":"AI"} ${currentBid} el söyledi. Oyna!`);
  }

  function playCard(playerIdx, cardIdx) {
    if (playerIdx !== 0 || turn !== 0) return;
    const card = hands[0][cardIdx];
    if (leadSuit && card.s !== leadSuit && hands[0].some(c=>c.s===leadSuit)) {
      setMessage("Önce " + leadSuit + " oynamalısın!");
      return;
    }
    const newHands = hands.map(h=>[...h]);
    newHands[0] = newHands[0].filter((_,i)=>i!==cardIdx);
    const newTable = [...table];
    newTable[0] = card;
    const newLeadSuit = leadSuit || card.s;
    setHands(newHands); setTable(newTable); setLeadSuit(newLeadSuit);
    setSelectedCard(null);
    // Masada kaç kart var kontrol et, 4 olduysa el bitti
    const filled = newTable.filter(c=>c!==null).length;
    if (filled === 4) {
      setTimeout(()=>resolveTrick(newHands, newTable, newLeadSuit), 500);
    } else {
      setTimeout(()=>aiPlay(newHands, newTable, newLeadSuit, 1), 500);
    }
  }

  function aiPlay(h, t, ls, aiIdx) {
    // aiIdx sırası: 1,2,3 - 0 atla
    const nextAI = aiIdx > 3 ? null : aiIdx === 0 ? 1 : aiIdx;
    if (nextAI === null || t.filter(c=>c!==null).length === 4) {
      resolveTrick(h, t, ls);
      return;
    }
    const hand = h[nextAI];
    if (!hand || hand.length === 0) { aiPlay(h, t, ls, nextAI+1); return; }
    let card;
    const suitCards = hand.filter(c=>c.s===ls);
    const trumpCards = hand.filter(c=>c.s===trump);
    if (suitCards.length>0) card = suitCards[Math.floor(Math.random()*suitCards.length)];
    else if (trumpCards.length>0) card = trumpCards[0];
    else card = hand[Math.floor(Math.random()*hand.length)];
    const newHands = h.map(hh=>[...hh]);
    newHands[nextAI] = newHands[nextAI].filter(c=>c!==card);
    const newTable = [...t];
    newTable[nextAI] = card;
    setHands(newHands); setTable(newTable);
    const next = nextAI + 1;
    if (next > 3 || newTable.filter(c=>c!==null).length === 4) {
      setTimeout(()=>resolveTrick(newHands, newTable, ls), 600);
    } else {
      setTimeout(()=>aiPlay(newHands, newTable, ls, next), 500);
    }
  }

  function resolveTrick(h, t, ls) {
    let winner = 0;
    let best = cardPower(t[0], trump, ls);
    for (let i=1;i<4;i++) {
      const p = cardPower(t[i], trump, ls);
      if (p>best) { best=p; winner=i; }
    }
    const newTricks = [...tricks];
    newTricks[winner]++;
    setTricks(newTricks);
    setMessage(`${winner===0?"Sen":["AI-1","AI-2","AI-3"][winner-1]} bu eli aldı!`);
    setTimeout(()=>{
      setTable([null,null,null,null]);
      setLeadSuit(null);
      if (h[0].length===0) {
        endRound(newTricks);
      } else {
        setTurn(winner);
        if (winner!==0) setTimeout(()=>aiStartTrick(h, winner), 600);
      }
    }, 1000);
  }

  function aiStartTrick(h, aiIdx) {
    const hand = h[aiIdx];
    const card = hand[Math.floor(Math.random()*hand.length)];
    const newHands = h.map(hh=>[...hh]);
    newHands[aiIdx] = newHands[aiIdx].filter(c=>c!==card);
    const newTable = [null,null,null,null];
    newTable[aiIdx] = card;
    setHands(newHands); setTable(newTable); setLeadSuit(card.s);
    setTurn(0);
    setTimeout(()=>aiPlay(newHands, newTable, card.s, (aiIdx+1)%4===0?1:(aiIdx+1)%4), 400);
  }

  function endRound(t) {
    const newTotal = [...totalScores];
    let msg = "";
    // Teklife ulaşan taraf puan alır
    const bidderTricks = t[bidder||0];
    if (bidderTricks >= currentBid) {
      newTotal[bidder||0] += bidderTricks;
      msg = `${bidder===0?"Sen":["AI-1","AI-2","AI-3"][(bidder||0)-1]} ${bidderTricks}/${currentBid} el aldı! +${bidderTricks} puan!`;
    } else {
      newTotal[bidder||0] -= currentBid;
      msg = `${bidder===0?"Sen":["AI-1","AI-2","AI-3"][(bidder||0)-1]} sadece ${bidderTricks} el aldı! -${currentBid} puan!`;
    }
    setTotalScores(newTotal);
    setMessage(msg);
    const newRound = round+1;
    setRound(newRound);
    if (newRound > 5 || newTotal.some(s=>s>=50||s<=-30)) {
      setGameOver(true);
      const playerScore = newTotal[0];
      api.saveScore(Math.max(0,playerScore),"batak").catch(()=>{});
      loadApiScores();
    } else {
      setTimeout(startGame, 2000);
    }
  }

  function renderCard(card, onClick, selected=false, faceDown=false) {
    if (!card) return <div style={{ width:56,height:80,borderRadius:8,background:"rgba(255,255,255,.05)",border:"1px dashed rgba(255,255,255,.1)" }} />;
    if (faceDown) return (
      <div style={{ width:56,height:80,borderRadius:8,background:"linear-gradient(135deg,#1a1a6e,#4444aa)",border:"2px solid rgba(255,255,255,.2)",cursor:"pointer" }} onClick={onClick} />
    );
    const color = SUIT_COLORS[card.s];
    const isRed = card.s==="♥"||card.s==="♦";
    return (
      <div onClick={onClick} style={{ width:56,height:80,borderRadius:8,background:selected?"#fffde7":"#fff",border:selected?"3px solid #ffd700":"2px solid #ddd",cursor:onClick?"pointer":"default",display:"flex",flexDirection:"column",justifyContent:"space-between",padding:"4px 5px",boxShadow:selected?"0 4px 16px rgba(255,215,0,.5)":"0 2px 8px rgba(0,0,0,.3)",transform:selected?"translateY(-10px)":"none",transition:"transform .2s",userSelect:"none" }}>
        <div style={{ fontSize:12,fontWeight:800,color:isRed?"#e74c3c":"#1a1a2e",lineHeight:1 }}>{card.v}</div>
        <div style={{ fontSize:20,textAlign:"center",color:isRed?"#e74c3c":"#1a1a2e" }}>{card.s}</div>
        <div style={{ fontSize:12,fontWeight:800,color:isRed?"#e74c3c":"#1a1a2e",lineHeight:1,transform:"rotate(180deg)",alignSelf:"flex-end" }}>{card.v}</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:800, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(231,76,60,.15),rgba(192,57,43,.1))", border:"1px solid rgba(231,76,60,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🃏 Batak</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>Koz belirle, teklif ver, en çok el al!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ display:"flex", gap:8 }}>
            {totalScores.map((s,i)=>(
              <div key={i} style={{ textAlign:"center", padding:"4px 8px", background:"rgba(255,255,255,.05)", borderRadius:8 }}>
                <div style={{ color:i===0?"#4facfe":"rgba(255,255,255,.5)", fontSize:10 }}>{i===0?"Sen":["AI1","AI2","AI3"][i-1]}</div>
                <div style={{ color:i===0?"#4facfe":"rgba(255,255,255,.6)", fontSize:16, fontWeight:800 }}>{s}</div>
              </div>
            ))}
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadApiScores(); }} style={{ padding:"6px 12px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:11, cursor:"pointer" }}>🏆</button>
          <button onClick={startGame} style={{ padding:"6px 12px", background:"rgba(231,76,60,.15)", border:"1px solid rgba(231,76,60,.3)", borderRadius:8, color:"#e74c3c", fontSize:11, cursor:"pointer" }}>Yeni</button>
        </div>
      </div>

      {/* Mesaj */}
      <div style={{ background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"10px 16px", marginBottom:12, color:"rgba(255,255,255,.8)", fontSize:13, textAlign:"center", minHeight:38 }}>
        {message || "Oyun başlıyor..."}
      </div>

      {/* Masa */}
      <div style={{ background:"#1a5c1a", borderRadius:20, padding:20, marginBottom:14, minHeight:220, position:"relative" }}>
        {/* AI Kartları */}
        <div style={{ display:"flex", justifyContent:"center", gap:8, marginBottom:12 }}>
          {[1,2,3].map(i=>(
            <div key={i} style={{ textAlign:"center" }}>
              <div style={{ color:"rgba(255,255,255,.5)", fontSize:10, marginBottom:4 }}>AI-{i} ({hands[i]?.length||0} kart)</div>
              <div style={{ display:"flex", gap:2 }}>
                {hands[i]?.slice(0,5).map((_,j)=>(
                  <div key={j} style={{ width:20,height:32,borderRadius:4,background:"linear-gradient(135deg,#1a1a6e,#4444aa)",border:"1px solid rgba(255,255,255,.2)" }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Masadaki kartlar */}
        <div style={{ display:"flex", justifyContent:"center", gap:12, marginBottom:12 }}>
          {[0,1,2,3].map(i=>(
            <div key={i} style={{ textAlign:"center" }}>
              <div style={{ color:"rgba(255,255,255,.4)", fontSize:9, marginBottom:3 }}>{i===0?"Sen":["AI-1","AI-2","AI-3"][i-1]}</div>
              {table[i] ? renderCard(table[i]) : <div style={{ width:56,height:80,borderRadius:8,border:"2px dashed rgba(255,255,255,.15)" }} />}
            </div>
          ))}
        </div>

        {/* Koz göstergesi */}
        {trump && (
          <div style={{ position:"absolute", top:10, right:10, background:"rgba(0,0,0,.5)", borderRadius:8, padding:"4px 10px", color:"#ffd700", fontSize:16, fontWeight:700 }}>
            Koz: {trump}
          </div>
        )}
      </div>

      {/* Oyun durumu */}
      {(gameState==="showHand" || gameState==="bid") && (
        <div style={{ background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", borderRadius:12, padding:16, marginBottom:14 }}>
          {gameState==="showHand" && (
            <button onClick={()=>setGameState("bid")} style={{ width:"100%", padding:"10px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:8, color:"#0a0814", fontWeight:700, fontSize:14, cursor:"pointer", marginBottom:8 }}>
              İhaleye Gir →
            </button>
          )}
          {gameState==="bid" && (
            <>
              <div style={{ color:"rgba(255,255,255,.6)", fontSize:12, marginBottom:10 }}>Kaç el alacaksın? (Min: {currentBid+1}, mevcut en yüksek: {currentBid})</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {[5,6,7,8,9,10,11,12,13].filter(b=>b>currentBid).map(b=>(
                  <button key={b} onClick={()=>handleBid(b)} style={{ padding:"8px 16px", background:"linear-gradient(135deg,#4facfe,#00f2fe)", border:"none", borderRadius:8, color:"#0a0814", fontWeight:700, cursor:"pointer" }}>{b}</button>
                ))}
                <button onClick={()=>handleBid(null)} style={{ padding:"8px 16px", background:"rgba(255,107,107,.2)", border:"1px solid rgba(255,107,107,.3)", borderRadius:8, color:"#ff6b6b", fontWeight:700, cursor:"pointer" }}>Pas</button>
              </div>
            </>
          )}
        </div>
      )}

      {gameState==="trump" && bidder===0 && (
        <div style={{ background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", borderRadius:12, padding:16, marginBottom:14 }}>
          <div style={{ color:"rgba(255,255,255,.6)", fontSize:12, marginBottom:10 }}>Koz seç:</div>
          <div style={{ display:"flex", gap:12 }}>
            {SUITS.map(s=>(
              <button key={s} onClick={()=>handleTrump(s)} style={{ padding:"10px 20px", background:"#fff", border:"none", borderRadius:8, fontSize:22, cursor:"pointer", boxShadow:"0 2px 8px rgba(0,0,0,.3)" }}>{s}</button>
            ))}
          </div>
        </div>
      )}



      {/* Oyuncu eli */}
      {gameState==="play" && (
        <div>
          <div style={{ color:"rgba(255,255,255,.5)", fontSize:11, marginBottom:8, textAlign:"center" }}>
            {turn===0?"Senin sıran — kart seç":"AI oynuyor..."}
            {tricks[0]>0&&<span style={{ marginLeft:12, color:"#4facfe" }}>Sen: {tricks[0]} el</span>}
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"center" }}>
            {hands[0].map((card,i)=>(
              <div key={i} onClick={()=>turn===0&&playCard(0,i)}>
                {renderCard(card, turn===0?()=>setSelectedCard(selectedCard===i?null:i):null, selectedCard===i)}
              </div>
            ))}
          </div>
          {selectedCard!==null && turn===0 && (
            <div style={{ textAlign:"center", marginTop:10 }}>
              <button onClick={()=>playCard(0,selectedCard)} style={{ padding:"10px 24px", background:"linear-gradient(135deg,#48c78e,#06d6a0)", border:"none", borderRadius:10, color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer" }}>
                Oyna →
              </button>
            </div>
          )}
        </div>
      )}

      {gameOver && (
        <div style={{ background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.3)", borderRadius:12, padding:20, textAlign:"center", marginTop:14 }}>
          <div style={{ color:"#ffd700", fontSize:20, fontWeight:700, marginBottom:8 }}>
            {totalScores[0]===Math.max(...totalScores)?"🏆 Kazandın!":"😢 Kaybettin!"}
          </div>
          <div style={{ color:"rgba(255,255,255,.6)", fontSize:13, marginBottom:14 }}>
            Senin puanın: {totalScores[0]}
          </div>
          <button onClick={()=>{ setRound(1); setTotalScores([0,0,0,0]); startGame(); }} style={{ padding:"10px 28px", background:"linear-gradient(135deg,#e74c3c,#c0392b)", border:"none", borderRadius:10, color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer" }}>Tekrar Oyna</button>
        </div>
      )}

      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700 }}>🏆 SKOR TABLOSU</span>
          </div>
          {apiScores.length===0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : apiScores.map((s,i)=>(
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#e74c3c", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Çapraz Bulmaca ───────────────────────────────────────────────────────────
function WordleGame({ user }) {
  const WORD_LIST = [
    {w:"KALEM",h:"Yazmak için kullanılan araç"},
    {w:"MASA",h:"Üzerinde çalışılan mobilya"},
    {w:"KAPI",h:"Odaya girişi sağlayan"},
    {w:"ARABA",h:"Dört tekerlekli taşıt"},
    {w:"KITAP",h:"Sayfalardan oluşan eser"},
    {w:"OKUL",h:"Eğitim verilen yer"},
    {w:"BAHCE",h:"Çiçek ve ağaçların bulunduğu alan"},
    {w:"DENIZ",h:"Tuzlu su kütlesi"},
    {w:"ORMAN",h:"Ağaçlarla kaplı alan"},
    {w:"NEHIR",h:"Akan tatlı su"},
    {w:"KOPRU",h:"İki yakayı birleştiren yapı"},
    {w:"SEHIR",h:"Büyük yerleşim yeri"},
    {w:"EKMEK",h:"Undan yapılan temel besin"},
    {w:"PEYNIR",h:"Sütten yapılan gıda"},
    {w:"DOMATES",h:"Kırmızı sebze"},
    {w:"ELMA",h:"Kırmızı veya yeşil meyve"},
    {w:"UZUM",h:"Salkım halinde yetişen meyve"},
    {w:"KIRAZ",h:"Saplı küçük kırmızı meyve"},
    {w:"ASLAN",h:"Ormanların kralı"},
    {w:"KAPLAN",h:"Çizgili yırtıcı hayvan"},
    {w:"TILKI",h:"Kurnaz hayvan"},
    {w:"TAVSAN",h:"Uzun kulaklı hayvan"},
    {w:"GUNES",h:"Güneş sistemi merkezi"},
    {w:"YILDIZ",h:"Geceleri parlayan cisim"},
    {w:"BULUT",h:"Gökyüzündeki beyaz kitle"},
    {w:"YAGMUR",h:"Gökten düşen su"},
    {w:"KAR",h:"Beyaz ve soğuk yağış"},
    {w:"RUZGAR",h:"Hareket eden hava"},
    {w:"MUTLU",h:"Sevinçli, neşeli"},
    {w:"UZGUN",h:"Kederli, hüzünlü"},
    {w:"KIZGIN",h:"Öfkeli, sinirli"},
    {w:"KORKU",h:"Tehlike karşısında duygu"},
    {w:"SEVGI",h:"Derin bağlılık duygusu"},
    {w:"UMUT",h:"İyiye inanç"},
    {w:"SABIR",h:"Bekleyebilme erdemi"},
    {w:"CESUR",h:"Korkusuz, yiğit"},
    {w:"AKIL",h:"Düşünme yetisi"},
    {w:"ZEKA",h:"Anlama ve kavrama gücü"},
    {w:"BILIM",h:"Sistematik bilgi alanı"},
    {w:"SANAT",h:"Güzellik yaratan etkinlik"},
    {w:"MUZIK",h:"Seslerden oluşan sanat"},
    {w:"RESIM",h:"Görsel sanat eseri"},
    {w:"DANS",h:"Ritme uygun hareket"},
    {w:"SPOR",h:"Beden egzersizi"},
    {w:"FUTBOL",h:"11 kişilik top oyunu"},
    {w:"YUZME",h:"Suda hareket etme"},
    {w:"KOSMA",h:"Hızlı yürüme"},
    {w:"DOKTOR",h:"Hasta tedavi eden kişi"},
    {w:"OGRETMEN",h:"Bilgi aktaran kişi"},
    {w:"MUHENDIS",h:"Teknik çözümler üreten"},
    {w:"AVUKAT",h:"Hukuki savunma yapan"},
    {w:"PILOT",h:"Uçak kullanan kişi"},
    {w:"ASKER",h:"Ülkeyi koruyan kişi"},
    {w:"ITFAIYE",h:"Yangın söndüren ekip"},
    {w:"POLIS",h:"Kamu düzenini koruyan"},
    {w:"CALISAN",h:"İş yapan kişi"},
    {w:"YONETICI",h:"Bir kurumu yöneten"},
    {w:"TURKIYE",h:"Anadolu'daki ülke"},
    {w:"ISTANBUL",h:"Boğaz kenarındaki şehir"},
    {w:"ANKARA",h:"Türkiye'nin başkenti"},
    {w:"IZMIR",h:"Ege kıyısındaki şehir"},
    {w:"BURSA",h:"Osmanlı'nın ilk başkenti"},
    {w:"ANTALYA",h:"Turizm şehri"},
    {w:"ADANA",h:"Kebabıyla ünlü şehir"},
    {w:"TRABZON",h:"Karadeniz'deki şehir"},
    {w:"TELEFON",h:"Uzaktan iletişim aracı"},
    {w:"BILGISAYAR",h:"Elektronik işlem cihazı"},
    {w:"INTERNET",h:"Küresel ağ"},
    {w:"TELEVIZYON",h:"Görüntülü yayın cihazı"},
    {w:"RADYO",h:"Ses yayın cihazı"},
    {w:"UÇAK",h:"Havada uçan taşıt"},
    {w:"TREN",h:"Raylarda giden taşıt"},
    {w:"GEMI",h:"Denizde yüzen taşıt"},
    {w:"BISIKLET",h:"İki tekerlekli insan taşıtı"},
    {w:"MOTOSIKLET",h:"Motorlu iki tekerlekli araç"},
    {w:"HASTANE",h:"Hastaların tedavi edildiği yer"},
    {w:"ECZANE",h:"İlaç satılan yer"},
    {w:"MARKET",h:"Alışveriş yapılan yer"},
    {w:"RESTORAN",h:"Yemek yenilen yer"},
    {w:"OTEL",h:"Konaklama yeri"},
    {w:"KÜTÜPHANE",h:"Kitapların bulunduğu yer"},
    {w:"MÜZE",h:"Tarihi eserlerin sergilendiği yer"},
    {w:"PARK",h:"Yeşil alan"},
    {w:"PLAJ",h:"Kumsal alanı"},
    {w:"DAG",h:"Yüksek doğa oluşumu"},
    {w:"OVA",h:"Düz geniş arazi"},
    {w:"VADI",h:"Dağlar arasındaki çukurluk"},
    {w:"GÖÇMEN",h:"Başka yere göç eden"},
    {w:"ÇEVRE",h:"Etraf, doğal ortam"},
    {w:"DOGAL",h:"Tabiata ait olan"},
    {w:"YAPAY",h:"İnsan yapımı"},
    {w:"MODERN",h:"Çağdaş, günümüze ait"},
    {w:"KLASIK",h:"Eski usul, geleneksel"},
    {w:"RENKLI",h:"Birden fazla renk içeren"},
    {w:"SESSIZ",h:"Ses çıkarmayan"},
    {w:"GÜRÜLTÜ",h:"Rahatsız edici ses"},
    {w:"HIZLI",h:"Süratli, çabuk"},
    {w:"YAVAS",h:"Az hızlı"},
    {w:"BUYUK",h:"Hacim olarak geniş"},
    {w:"KUCUK",h:"Küçük boyutlu"},
  ].map(item => ({...item, w: item.w.toUpperCase().replace(/İ/g,"I").replace(/Ğ/g,"G").replace(/Ü/g,"U").replace(/Ş/g,"S").replace(/Ö/g,"O").replace(/Ç/g,"C")}));

  const GRID_SIZE = 15;

  function generatePuzzle() {
    const grid = Array.from({length:GRID_SIZE}, ()=>Array(GRID_SIZE).fill(""));
    const placed = [];
    const shuffled = [...WORD_LIST].sort(()=>Math.random()-0.5).slice(0,30);

    function canPlace(word, row, col, dir) {
      if (!word || word.length === 0) return false;
      for (let i=0; i<word.length; i++) {
        const r = dir==="H" ? row : row+i;
        const c = dir==="H" ? col+i : col;
        if (r<0||r>=GRID_SIZE||c<0||c>=GRID_SIZE) return false;
        if (grid[r][c] && grid[r][c]!==word[i]) return false;
      }
      if (dir==="H") {
        if (col>0&&grid[row][col-1]) return false;
        if (col+word.length<GRID_SIZE&&grid[row][col+word.length]) return false;
      } else {
        if (row>0&&grid[row-1][col]) return false;
        if (row+word.length<GRID_SIZE&&grid[row+word.length][col]) return false;
      }
      return true;
    }

    function placeWord(word, row, col, dir) {
      for (let i=0; i<word.length; i++) {
        const r = dir==="H" ? row : row+i;
        const c = dir==="H" ? col+i : col;
        grid[r][c] = word[i];
      }
    }

    // İlk kelime ortaya yerleştir
    const first = shuffled[0];
    const startCol = Math.floor((GRID_SIZE-first.w.length)/2);
    const startRow = Math.floor(GRID_SIZE/2);
    placeWord(first.w, startRow, startCol, "H");
    placed.push({...first, row:startRow, col:startCol, dir:"H", num:1});

    let num = 2;
    for (let attempt=0; attempt<shuffled.length && placed.length<16; attempt++) {
      const item = shuffled[attempt];
      if (!item || !item.w) continue;
      if (placed.some(p=>p.w===item.w)) continue;
      let bestPos = null;

      for (const p of placed) {
        if (bestPos) break;
        for (let pi=0; pi<p.w.length && !bestPos; pi++) {
          for (let wi=0; wi<item.w.length && !bestPos; wi++) {
            if (item.w[wi] !== p.w[pi]) continue;
            let finalRow, finalCol, newDir;
            if (p.dir==="H") {
              finalCol = p.col + pi;
              finalRow = p.row - wi;
              newDir = "V";
            } else {
              finalRow = p.row + pi;
              finalCol = p.col - wi;
              newDir = "H";
            }
            if (canPlace(item.w, finalRow, finalCol, newDir)) {
              bestPos = {row:finalRow, col:finalCol, dir:newDir};
            }
          }
        }
      }

      if (bestPos) {
        placeWord(item.w, bestPos.row, bestPos.col, bestPos.dir);
        placed.push({...item, ...bestPos, num});
        num++;
      }
    }

    return {grid, placed};
  }

    const [puzzle,    setPuzzle]    = useState(() => generatePuzzle());
  const [userGrid,  setUserGrid]  = useState(() => Array.from({length:GRID_SIZE},()=>Array(GRID_SIZE).fill("")));
  const [selected,  setSelected]  = useState(null);
  const [direction, setDirection] = useState("H");
  const [completed, setCompleted] = useState(false);
  const [revealed,  setRevealed]  = useState(false);
  const [scores,    setScores]    = useState([]);
  const [showBoard, setShowBoard] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [timer,     setTimer]     = useState(0);

  useEffect(() => {
    const iv = setInterval(() => { if (!completed) setTimer(Math.floor((Date.now()-startTime)/1000)); }, 1000);
    return () => clearInterval(iv);
  }, [startTime, completed]);

  async function loadScores() {
    try { const rows = await api.getScores("crossword"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  function newGame() {
    const p = generatePuzzle();
    setPuzzle(p);
    setUserGrid(Array.from({length:GRID_SIZE},()=>Array(GRID_SIZE).fill("")));
    setSelected(null); setCompleted(false); setRevealed(false);
    setStartTime(Date.now()); setTimer(0);
  }

  function handleCellClick(row, col) {
    if (!puzzle.grid[row][col]) return;
    if (selected && selected[0]===row && selected[1]===col) {
      setDirection(d => d==="H"?"V":"H");
    } else {
      setSelected([row,col]);
    }
  }

  function handleInput(e, row, col) {
    const val = e.target.value.slice(-1).toUpperCase()
      .replace(/İ/g,"I").replace(/Ğ/g,"G").replace(/Ü/g,"U").replace(/Ş/g,"S").replace(/Ö/g,"O").replace(/Ç/g,"C");
    if (!/^[A-Z]$/.test(val) && val!=="") return;
    const ng = userGrid.map(r=>[...r]);
    ng[row][col] = val;
    setUserGrid(ng);

    // Tamamlandı mı kontrol et
    let done = true;
    for (let r=0;r<GRID_SIZE;r++) for (let c=0;c<GRID_SIZE;c++) {
      if (puzzle.grid[r][c] && ng[r][c]!==puzzle.grid[r][c]) done=false;
    }
    if (done) {
      setCompleted(true);
      const pts = Math.max(100, 1000 - timer*2);
      api.saveScore(pts,"crossword").catch(()=>{});
      loadScores();
    }

    // Sonraki hücreye geç
    if (val) {
      const nr = direction==="H"?row:row+1;
      const nc = direction==="H"?col+1:col;
      if (nr<GRID_SIZE&&nc<GRID_SIZE&&puzzle.grid[nr][nc]) setSelected([nr,nc]);
    }
  }

  function formatTime(s) {
    return Math.floor(s/60)+":"+(s%60).toString().padStart(2,"0");
  }

  const cellNumbers = {};
  // Her hücre için en küçük numarayı göster
  puzzle.placed.forEach(p => {
    const key = `${p.row},${p.col}`;
    if (!cellNumbers[key] || p.num < cellNumbers[key]) cellNumbers[key] = p.num;
  });
  // Sayılanmayan numaraları kontrol et - eğer bir numara hücrede görünmüyorsa, o numarayı kelime listesinden kaldırabiliriz
  // Tüm numaraların göründüğünden emin ol
  const allNums = new Set(puzzle.placed.map(p=>p.num));
  const shownNums = new Set(Object.values(cellNumbers));
  puzzle.placed.forEach(p => {
    if (!shownNums.has(p.num)) {
      // Bu numaranın başlangıç hücresi başka bir numara tarafından işgal edilmiş
      // Numarayı hücreye zorla ekle
      const key = `${p.row},${p.col}`;
      cellNumbers[key] = Math.min(cellNumbers[key]||999, p.num);
    }
  });

  const hClues = puzzle.placed.filter(p=>p.dir==="H").sort((a,b)=>a.num-b.num);
  const vClues = puzzle.placed.filter(p=>p.dir==="V").sort((a,b)=>a.num-b.num);

  // Seçili kelimenin hücreleri
  const activeWord = selected && puzzle.placed.find(p => {
    if (p.dir!==direction) return false;
    if (direction==="H") return p.row===selected[0]&&p.col<=selected[1]&&p.col+p.w.length>selected[1];
    return p.col===selected[1]&&p.row<=selected[0]&&p.row+p.w.length>selected[0];
  });

  const activeCells = new Set();
  if (activeWord) {
    for (let i=0;i<activeWord.w.length;i++) {
      const r=activeWord.dir==="H"?activeWord.row:activeWord.row+i;
      const c=activeWord.dir==="H"?activeWord.col+i:activeWord.col;
      activeCells.add(`${r},${c}`);
    }
  }

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:900, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(72,199,142,.15),rgba(6,214,160,.1))", border:"1px solid rgba(72,199,142,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>📰 Çapraz Bulmaca</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>İpuçlarından harfleri bul ve yerleştir!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ color:"#ffd700", fontSize:16, fontWeight:700 }}>{formatTime(timer)}</div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"6px 12px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:11, cursor:"pointer" }}>🏆</button>
          <button onClick={()=>setRevealed(true)} style={{ padding:"6px 12px", background:"rgba(255,107,107,.1)", border:"1px solid rgba(255,107,107,.25)", borderRadius:8, color:"#ff6b6b", fontSize:11, cursor:"pointer" }}>Çözüm</button>
          <button onClick={newGame} style={{ padding:"6px 12px", background:"rgba(72,199,142,.15)", border:"1px solid rgba(72,199,142,.3)", borderRadius:8, color:"#48c78e", fontSize:11, cursor:"pointer" }}>Yeni</button>
        </div>
      </div>

      {completed && (
        <div style={{ background:"rgba(72,199,142,.15)", border:"1px solid rgba(72,199,142,.3)", borderRadius:12, padding:"12px 20px", marginBottom:14, textAlign:"center", color:"#48c78e", fontWeight:700, fontSize:16 }}>
          🎉 Tebrikler! Bulmacayı {formatTime(timer)}'de tamamladın!
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:16 }}>
        {/* Bulmaca ızgarası */}
        <div style={{ overflow:"auto" }}>
          <div style={{ display:"inline-grid", gridTemplateColumns:`repeat(${GRID_SIZE},30px)`, gap:1, background:"rgba(255,255,255,.1)", padding:1, borderRadius:8 }}>
            {Array.from({length:GRID_SIZE}).map((_,r)=>
              Array.from({length:GRID_SIZE}).map((_,c)=>{
                const letter = puzzle.grid[r][c];
                const num = cellNumbers[`${r},${c}`];
                const isActive = activeCells.has(`${r},${c}`);
                const isSelected = selected&&selected[0]===r&&selected[1]===c;
                const userLetter = revealed ? letter : userGrid[r][c];
                const isCorrect = userLetter===letter&&letter;
                const isWrong = userLetter&&userLetter!==letter;
                return (
                  <div key={`${r}-${c}`} onClick={()=>handleCellClick(r,c)}
                    style={{ width:28, height:28, background:!letter?"#050810":isSelected?"#ffd700":isActive?"rgba(255,215,0,.25)":"rgba(255,255,255,.95)", position:"relative", cursor:letter?"pointer":"default", borderRadius:2 }}>
                    {num && letter && <span style={{ position:"absolute", top:1, left:1, fontSize:8, color:isSelected?"#1a1a2e":"#555", fontWeight:800, lineHeight:1, zIndex:2 }}>{num}</span>}
                    {letter && (
                      <input
                        value={revealed?letter:userGrid[r][c]}
                        readOnly={revealed}
                        onChange={e=>handleInput(e,r,c)}
                        onClick={()=>handleCellClick(r,c)}
                        maxLength={1}
                        style={{
                          width:"100%", height:"100%", border:"none", outline:"none",
                          background:"transparent", textAlign:"center",
                          fontSize:13, fontWeight:800, cursor:"pointer",
                          color: revealed?"#1a6e2e":isWrong?"#ff4444":isCorrect?"#1a6e2e":isSelected?"#1a1a2e":"#1a1a2e",
                          paddingTop:num?"9px":"0",
                        }}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* İpuçları */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, maxHeight:420, overflowY:"auto" }}>
          <div>
            <div style={{ color:"#48c78e", fontSize:12, fontWeight:700, letterSpacing:"1px", marginBottom:8 }}>→ YATAY</div>
            {hClues.map(p=>(
              <div key={p.num} onClick={()=>{ setSelected([p.row,p.col]); setDirection("H"); }}
                style={{ padding:"4px 8px", marginBottom:4, borderRadius:6, cursor:"pointer", background:activeWord?.num===p.num&&direction==="H"?"rgba(255,215,0,.15)":"transparent", border:activeWord?.num===p.num&&direction==="H"?"1px solid rgba(255,215,0,.3)":"1px solid transparent" }}>
                <span style={{ color:"#ffd700", fontSize:11, fontWeight:700 }}>{p.num}. </span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:11 }}>{p.h}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{ color:"#4facfe", fontSize:12, fontWeight:700, letterSpacing:"1px", marginBottom:8 }}>↓ DİKEY</div>
            {vClues.map(p=>(
              <div key={p.num} onClick={()=>{ setSelected([p.row,p.col]); setDirection("V"); }}
                style={{ padding:"4px 8px", marginBottom:4, borderRadius:6, cursor:"pointer", background:activeWord?.num===p.num&&direction==="V"?"rgba(79,172,254,.15)":"transparent", border:activeWord?.num===p.num&&direction==="V"?"1px solid rgba(79,172,254,.3)":"1px solid transparent" }}>
                <span style={{ color:"#4facfe", fontSize:11, fontWeight:700 }}>{p.num}. </span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:11 }}>{p.h}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700 }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length===0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s,i)=>(
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#48c78e", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Top-Down Shooter ─────────────────────────────────────────────────────────
function ShooterGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false,
    score: 0, hiScore: 0, wave: 1, frame: 0,
    player: { x:0, y:0, r:16, angle:0, hp:100, maxHp:100, speed:3 },
    bullets: [], enemies: [], particles: [], drops: [],
    keys: {}, mouse: { x:0, y:0 },
    shootCooldown: 0, waveTimer: 0, animFrame: null,
    killCount: 0, waveEnemies: 0,
  });
  const [display, setDisplay] = useState({ score:0, hiScore:0, hp:100, wave:1, over:false, started:false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  async function loadScores() {
    try { const rows = await api.getScores("shooter"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;

    function spawnWave() {
      const count = 5 + g.wave * 3;
      g.waveEnemies = count;
      g.killCount = 0;
      for (let i=0; i<count; i++) {
        setTimeout(() => {
          if (!g.running) return;
          const side = Math.floor(Math.random()*4);
          let x, y;
          if (side===0) { x=Math.random()*W; y=-30; }
          else if (side===1) { x=W+30; y=Math.random()*H; }
          else if (side===2) { x=Math.random()*W; y=H+30; }
          else { x=-30; y=Math.random()*H; }
          const type = g.wave > 3 && Math.random()<0.3 ? "tank" : g.wave > 1 && Math.random()<0.2 ? "fast" : "basic";
          g.enemies.push({
            x, y,
            r: type==="tank"?22:type==="fast"?10:16,
            hp: type==="tank"?6:type==="fast"?1:2,
            maxHp: type==="tank"?6:type==="fast"?1:2,
            speed: type==="tank"?1:type==="fast"?3.5:1.8,
            color: type==="tank"?"#ff6b6b":type==="fast"?"#ffd700":"#a78bfa",
            type, shootTimer: Math.random()*120,
          });
        }, i*300);
      }
    }

    function start() {
      g.started=true; g.running=true; g.over=false;
      g.score=0; g.wave=1; g.frame=0; g.killCount=0;
      g.player = { x:W/2, y:H/2, r:16, angle:0, hp:100, maxHp:100, speed:3 };
      g.bullets=[]; g.enemies=[]; g.particles=[]; g.drops=[];
      g.shootCooldown=0;
      spawnWave();
      setDisplay(d=>({...d,started:true,over:false,score:0,hp:100,wave:1}));
    }

    function explode(x, y, color, count=12) {
      for (let i=0; i<count; i++) {
        const angle = (i/count)*Math.PI*2 + Math.random()*0.5;
        g.particles.push({
          x, y,
          vx: Math.cos(angle)*(1+Math.random()*4),
          vy: Math.sin(angle)*(1+Math.random()*4),
          life: 25+Math.random()*15, color,
          size: 2+Math.random()*4,
        });
      }
    }

    function shoot(fromX, fromY, toX, toY, isEnemy=false) {
      const angle = Math.atan2(toY-fromY, toX-fromX);
      const speed = isEnemy ? 3 : 8;
      g.bullets.push({
        x: fromX, y: fromY,
        vx: Math.cos(angle)*speed,
        vy: Math.sin(angle)*speed,
        isEnemy, r: isEnemy?5:4,
        color: isEnemy?"#ff4444":"#4facfe",
        life: isEnemy?120:80,
      });
    }

    const onKey = e => {
      g.keys[e.code]=true;
      if (!g.started && e.code==="Space") { start(); e.preventDefault(); }
      if (g.over && e.code==="Space") { start(); e.preventDefault(); }
    };
    const onKeyUp = e => { g.keys[e.code]=false; };
    const onMouseMove = e => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = W/rect.width, scaleY = H/rect.height;
      g.mouse.x = (e.clientX-rect.left)*scaleX;
      g.mouse.y = (e.clientY-rect.top)*scaleY;
    };
    const onMouseDown = e => {
      if (!g.started||g.over) { start(); return; }
      if (g.shootCooldown<=0) {
        shoot(g.player.x, g.player.y, g.mouse.x, g.mouse.y);
        g.shootCooldown = 10;
      }
    };

    // Touch
    let touchAim = {x:0,y:0};
    canvas.addEventListener("touchstart", e=>{
      if (!g.started||g.over) { start(); return; }
      touchAim={x:e.touches[0].clientX,y:e.touches[0].clientY};
      e.preventDefault();
    },{passive:false});
    canvas.addEventListener("touchmove", e=>{
      const rect=canvas.getBoundingClientRect();
      const scaleX=W/rect.width, scaleY=H/rect.height;
      g.mouse.x=(e.touches[0].clientX-rect.left)*scaleX;
      g.mouse.y=(e.touches[0].clientY-rect.top)*scaleY;
      e.preventDefault();
    },{passive:false});

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mousedown", onMouseDown);

    function drawGrid() {
      ctx.strokeStyle="rgba(255,255,255,.04)";
      ctx.lineWidth=1;
      for (let x=0; x<W; x+=40) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
      for (let y=0; y<H; y+=40) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }
    }

    function drawPlayer() {
      const p=g.player;
      p.angle = Math.atan2(g.mouse.y-p.y, g.mouse.x-p.x);

      // Gölge
      ctx.fillStyle="rgba(0,0,0,.3)";
      ctx.beginPath(); ctx.ellipse(p.x+4, p.y+4, p.r, p.r*0.6, 0, 0, Math.PI*2); ctx.fill();

      // Gövde
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.angle);
      ctx.fillStyle="#4facfe";
      ctx.beginPath(); ctx.arc(0, 0, p.r, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle="#00f2fe";
      ctx.beginPath(); ctx.arc(-2, -2, p.r*0.5, 0, Math.PI*2); ctx.fill();
      // Silah
      ctx.fillStyle="#2980b9";
      ctx.fillRect(p.r*0.3, -4, p.r*0.9, 8);
      ctx.fillStyle="#3498db";
      ctx.fillRect(p.r*0.8, -3, p.r*0.5, 6);
      ctx.restore();

      // HP bar
      const bw=50, bh=6;
      ctx.fillStyle="rgba(0,0,0,.5)";
      ctx.fillRect(p.x-bw/2, p.y-p.r-14, bw, bh);
      ctx.fillStyle=p.hp>50?"#48c78e":p.hp>25?"#ffd700":"#ff6b6b";
      ctx.fillRect(p.x-bw/2, p.y-p.r-14, bw*(p.hp/p.maxHp), bh);
    }

    function drawEnemy(e) {
      ctx.save(); ctx.translate(e.x, e.y);
      const angle=Math.atan2(g.player.y-e.y, g.player.x-e.x);
      ctx.rotate(angle);

      if (e.type==="tank") {
        ctx.fillStyle=e.color;
        ctx.beginPath(); ctx.arc(0,0,e.r,0,Math.PI*2); ctx.fill();
        ctx.fillStyle="#cc0000";
        ctx.fillRect(e.r*0.2,-5,e.r*1.2,10);
        ctx.fillStyle="#ff9999";
        ctx.beginPath(); ctx.arc(-4,-4,6,0,Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(-4,4,6,0,Math.PI*2); ctx.fill();
      } else if (e.type==="fast") {
        ctx.fillStyle=e.color;
        ctx.beginPath();
        ctx.moveTo(e.r,0); ctx.lineTo(-e.r,-e.r*0.7); ctx.lineTo(-e.r,e.r*0.7);
        ctx.closePath(); ctx.fill();
      } else {
        ctx.fillStyle=e.color;
        ctx.beginPath(); ctx.arc(0,0,e.r,0,Math.PI*2); ctx.fill();
        ctx.fillStyle="rgba(0,0,0,.4)";
        ctx.beginPath(); ctx.arc(-4,-4,4,0,Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(4,-4,4,0,Math.PI*2); ctx.fill();
        ctx.fillRect(e.r*0.2,-3,e.r*0.8,6);
      }
      ctx.restore();

      // HP bar
      if (e.maxHp>1) {
        ctx.fillStyle="rgba(0,0,0,.5)";
        ctx.fillRect(e.x-20,e.y-e.r-10,40,5);
        ctx.fillStyle="#ff6b6b";
        ctx.fillRect(e.x-20,e.y-e.r-10,40*(e.hp/e.maxHp),5);
      }
    }

    function loop() {
      ctx.fillStyle="#080c14"; ctx.fillRect(0,0,W,H);
      drawGrid();

      if (!g.started) {
        ctx.fillStyle="#4facfe"; ctx.font="bold 26px Georgia"; ctx.textAlign="center";
        ctx.fillText("🎯 TOP-DOWN SHOOTER", W/2, H/2-50);
        ctx.font="14px Georgia"; ctx.fillStyle="rgba(255,255,255,.5)";
        ctx.fillText("WASD veya Ok tuşları → Hareket", W/2, H/2-10);
        ctx.fillText("Mouse → Nişan Al | Click → Ateş", W/2, H/2+16);
        ctx.fillText("Tüm dalgaları geç!", W/2, H/2+42);
        ctx.fillText("SPACE veya Tıkla → Başla", W/2, H/2+72);
        ctx.textAlign="left";
        g.animFrame=requestAnimationFrame(loop); return;
      }

      if (g.running) {
        g.frame++;
        const p=g.player;

        // Oyuncu hareketi
        if (g.keys["KeyW"]||g.keys["ArrowUp"])    p.y=Math.max(p.r,p.y-p.speed);
        if (g.keys["KeyS"]||g.keys["ArrowDown"])  p.y=Math.min(H-p.r,p.y+p.speed);
        if (g.keys["KeyA"]||g.keys["ArrowLeft"])  p.x=Math.max(p.r,p.x-p.speed);
        if (g.keys["KeyD"]||g.keys["ArrowRight"]) p.x=Math.min(W-p.r,p.x+p.speed);

        // Otomatik ateş (mouse basılıysa)
        if (g.shootCooldown>0) g.shootCooldown--;

        // Mermi hareketi
        g.bullets=g.bullets.filter(b=>b.life>0&&b.x>-20&&b.x<W+20&&b.y>-20&&b.y<H+20);
        g.bullets.forEach(b=>{ b.x+=b.vx; b.y+=b.vy; b.life--; });

        // Düşman hareketi ve ateşi
        g.enemies.forEach(e=>{
          const dx=p.x-e.x, dy=p.y-e.y;
          const dist=Math.hypot(dx,dy);
          e.x+=dx/dist*e.speed;
          e.y+=dy/dist*e.speed;

          // Düşman ateşi
          e.shootTimer--;
          if (e.shootTimer<=0 && e.type!=="fast") {
            shoot(e.x,e.y,p.x,p.y,true);
            e.shootTimer=60+Math.random()*60;
          }

          // Oyuncuya çarpma
          if (dist<p.r+e.r) {
            p.hp-=e.type==="tank"?2:1;
            explode(e.x,e.y,e.color,8);
            e.x-=dx/dist*20; e.y-=dy/dist*20;
            setDisplay(d=>({...d,hp:p.hp}));
            if (p.hp<=0) {
              g.running=false; g.over=true;
              if (g.score>g.hiScore) g.hiScore=g.score;
              api.saveScore(g.score,"shooter").catch(()=>{});
              loadScores();
              setDisplay(d=>({...d,over:true,hiScore:g.hiScore}));
            }
          }
        });

        // Mermi vs düşman
        g.bullets.filter(b=>!b.isEnemy).forEach(b=>{
          g.enemies.forEach(e=>{
            if (Math.hypot(b.x-e.x,b.y-e.y)<e.r+b.r) {
              b.life=0; e.hp--;
              explode(b.x,b.y,e.color,6);
              if (e.hp<=0) {
                explode(e.x,e.y,e.color,15);
                e.hp=-1;
                const pts=e.type==="tank"?30:e.type==="fast"?20:10;
                g.score+=pts; g.killCount++;
                setDisplay(d=>({...d,score:g.score}));
              }
            }
          });
        });

        // Düşman mermisi vs oyuncu
        g.bullets.filter(b=>b.isEnemy).forEach(b=>{
          if (Math.hypot(b.x-p.x,b.y-p.y)<p.r+b.r) {
            b.life=0; p.hp-=5;
            explode(p.x,p.y,"#4facfe",6);
            setDisplay(d=>({...d,hp:p.hp}));
            if (p.hp<=0) {
              g.running=false; g.over=true;
              if (g.score>g.hiScore) g.hiScore=g.score;
              api.saveScore(g.score,"shooter").catch(()=>{});
              loadScores();
              setDisplay(d=>({...d,over:true,hiScore:g.hiScore}));
            }
          }
        });

        // Ölü düşmanları kaldır
        g.enemies=g.enemies.filter(e=>e.hp>0);

        // Dalga bitti mi?
        if (g.enemies.length===0 && g.killCount>=g.waveEnemies && g.running) {
          g.wave++;
          g.player.hp=Math.min(g.player.maxHp,g.player.hp+30);
          setDisplay(d=>({...d,wave:g.wave,hp:g.player.hp}));
          setTimeout(()=>{ if(g.running) spawnWave(); }, 1500);
          g.killCount=0; g.waveEnemies=999;
        }
      }

      // Drops çiz
      g.drops=g.drops.filter(d=>d.life>0);
      g.drops.forEach(d=>{
        d.life--;
        ctx.fillStyle=d.color; ctx.globalAlpha=d.life/60;
        ctx.beginPath(); ctx.arc(d.x,d.y,8,0,Math.PI*2); ctx.fill();
        ctx.globalAlpha=1;
      });

      // Parçacıklar
      g.particles=g.particles.filter(p=>p.life>0);
      g.particles.forEach(p=>{
        ctx.fillStyle=p.color; ctx.globalAlpha=p.life/40;
        ctx.beginPath(); ctx.arc(p.x,p.y,p.size,0,Math.PI*2); ctx.fill();
        p.x+=p.vx; p.y+=p.vy; p.vy+=0.1; p.life--;
      });
      ctx.globalAlpha=1;

      // Düşman mermileri
      g.bullets.filter(b=>b.isEnemy).forEach(b=>{
        ctx.fillStyle=b.color;
        ctx.shadowBlur=6; ctx.shadowColor=b.color;
        ctx.beginPath(); ctx.arc(b.x,b.y,b.r,0,Math.PI*2); ctx.fill();
        ctx.shadowBlur=0;
      });

      // Oyuncu mermileri
      g.bullets.filter(b=>!b.isEnemy).forEach(b=>{
        ctx.fillStyle=b.color;
        ctx.shadowBlur=8; ctx.shadowColor=b.color;
        ctx.beginPath(); ctx.arc(b.x,b.y,b.r,0,Math.PI*2); ctx.fill();
        ctx.shadowBlur=0;
      });

      // Düşmanlar
      g.enemies.forEach(e=>drawEnemy(e));

      // Oyuncu
      if (!g.over) drawPlayer();

      // HUD
      ctx.fillStyle="rgba(255,255,255,.7)"; ctx.font="13px Georgia"; ctx.textAlign="left";
      ctx.fillText("Dalga: "+g.wave, 10, H-10);
      ctx.textAlign="center";
      ctx.fillText("Skor: "+g.score, W/2, H-10);
      ctx.textAlign="right";
      ctx.fillText("Düşman: "+g.enemies.length, W-10, H-10);
      ctx.textAlign="left";

      if (g.over) {
        ctx.fillStyle="rgba(0,0,0,.72)"; ctx.fillRect(0,0,W,H);
        ctx.fillStyle="#ff6b6b"; ctx.font="bold 30px Georgia"; ctx.textAlign="center";
        ctx.fillText("ÖLDÜN! 💀", W/2, H/2-30);
        ctx.fillStyle="#ffd700"; ctx.font="bold 20px Georgia";
        ctx.fillText("Skor: "+g.score+" | Dalga: "+g.wave, W/2, H/2+10);
        ctx.fillStyle="rgba(255,255,255,.5)"; ctx.font="14px Georgia";
        ctx.fillText("SPACE veya Tıkla → Tekrar", W/2, H/2+46);
        ctx.textAlign="left";
      }

      g.animFrame=requestAnimationFrame(loop);
    }

    g.animFrame=requestAnimationFrame(loop);
    return ()=>{ cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown",onKey); window.removeEventListener("keyup",onKeyUp); };
  }, []);

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(255,107,107,.15),rgba(238,90,36,.1))", border:"1px solid rgba(255,107,107,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🎯 Top-Down Shooter</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>WASD Hareket | Mouse Nişan | Click Ateş | Dalgaları geç!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#ff6b6b", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={720} height={480}
        style={{ width:"100%", borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"crosshair", display:"block" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length===0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s,i)=>(
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#ff6b6b", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Flappy Bird ──────────────────────────────────────────────────────────────
function FlappyGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false,
    score: 0, hiScore: 0, frame: 0,
    bird: { x:80, y:0, vy:0, r:12, rot:0 },
    pipes: [], particles: [], stars: [],
    gravity: 0.28, jumpForce: -5.5,
    pipeSpeed: 2.2, pipeGap: 190, pipeInterval: 100,
    animFrame: null, bgOffset: 0,
  });
  const [display, setDisplay] = useState({ score:0, hiScore:0, over:false, started:false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  async function loadScores() {
    try { const rows = await api.getScores("flappy"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;

    // Yıldızlar
    g.stars = Array.from({length:60}, () => ({
      x: Math.random()*W, y: Math.random()*H*0.7,
      size: Math.random()*2+0.5, twinkle: Math.random()*Math.PI*2,
    }));

    function initGame() {
      g.bird = { x:80, y:H/2, vy:0, r:14, rot:0 };
      g.pipes = []; g.particles = [];
      g.score = 0; g.frame = 0;
      g.pipeSpeed = 2.5;
      setDisplay(d => ({...d, score:0, over:false}));
    }

    function jump() {
      if (!g.started) { g.started = true; g.running = true; initGame(); setDisplay(d=>({...d,started:true})); return; }
      if (g.over) { g.over = false; g.running = true; initGame(); return; }
      g.bird.vy = g.jumpForce;
    }

    function explode(x, y) {
      for (let i=0; i<15; i++) {
        const angle = (i/15)*Math.PI*2;
        g.particles.push({
          x, y,
          vx: Math.cos(angle)*(2+Math.random()*4),
          vy: Math.sin(angle)*(2+Math.random()*4),
          life: 35,
          color: ["#ffd700","#ff6b6b","#4facfe","#48c78e"][Math.floor(Math.random()*4)],
        });
      }
    }

    function drawBird(x, y, rot) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.min(Math.max(rot*0.05, -0.5), 1.2));

      // Gövde
      ctx.fillStyle = "#ffd700";
      ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI*2); ctx.fill();

      // Kanat
      const wingY = Math.sin(g.frame*0.3)*5;
      ctx.fillStyle = "#f7971e";
      ctx.beginPath();
      ctx.ellipse(-4, wingY, 10, 6, -0.3, 0, Math.PI*2);
      ctx.fill();

      // Göz
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(6, -4, 5, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = "#1a1a2e";
      ctx.beginPath(); ctx.arc(7, -4, 3, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(8, -5, 1, 0, Math.PI*2); ctx.fill();

      // Gaga
      ctx.fillStyle = "#ff9f43";
      ctx.beginPath();
      ctx.moveTo(10, -2); ctx.lineTo(18, 0); ctx.lineTo(10, 4);
      ctx.closePath(); ctx.fill();

      ctx.restore();
    }

    function drawPipe(x, topH, botY) {
      const pw = 52;
      // Üst boru
      const gradT = ctx.createLinearGradient(x, 0, x+pw, 0);
      gradT.addColorStop(0, "#48c78e");
      gradT.addColorStop(0.4, "#06d6a0");
      gradT.addColorStop(1, "#048a81");
      ctx.fillStyle = gradT;
      ctx.fillRect(x, 0, pw, topH);
      // Üst başlık
      ctx.fillStyle = "#06d6a0";
      ctx.fillRect(x-6, topH-22, pw+12, 22);
      ctx.fillStyle = "rgba(255,255,255,.15)";
      ctx.fillRect(x+4, 0, 8, topH-22);

      // Alt boru
      ctx.fillStyle = gradT;
      ctx.fillRect(x, botY, pw, H-botY);
      // Alt başlık
      ctx.fillStyle = "#06d6a0";
      ctx.fillRect(x-6, botY, pw+12, 22);
      ctx.fillStyle = "rgba(255,255,255,.15)";
      ctx.fillRect(x+4, botY+22, 8, H-botY);
    }

    const onKey = e => {
      if (e.code==="Space"||e.code==="ArrowUp") { e.preventDefault(); jump(); }
    };
    window.addEventListener("keydown", onKey);
    canvas.addEventListener("click", jump);
    canvas.addEventListener("touchend", e => { e.preventDefault(); jump(); }, {passive:false});

    function loop() {
      // Arka plan
      const sky = ctx.createLinearGradient(0,0,0,H);
      sky.addColorStop(0, "#0a0814");
      sky.addColorStop(0.6, "#1c1535");
      sky.addColorStop(1, "#2d1b69");
      ctx.fillStyle = sky; ctx.fillRect(0,0,W,H);

      // Yıldızlar
      g.stars.forEach(s => {
        s.twinkle += 0.03;
        ctx.fillStyle = `rgba(255,255,255,${0.3+Math.sin(s.twinkle)*0.3})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.size, 0, Math.PI*2); ctx.fill();
      });

      // Zemin
      g.bgOffset = (g.bgOffset + (g.running?g.pipeSpeed:0)) % 40;
      ctx.fillStyle = "#2d1b69";
      ctx.fillRect(0, H-40, W, 40);
      ctx.fillStyle = "#48c78e";
      ctx.fillRect(0, H-42, W, 4);
      for (let i=0; i<W/40+2; i++) {
        ctx.fillStyle = "rgba(255,255,255,.08)";
        ctx.fillRect(i*40 - g.bgOffset, H-35, 20, 4);
      }

      if (!g.started) {
        // Kuş animasyonu
        g.bird.y = H/2 + Math.sin(g.frame*0.05)*15;
        g.frame++;
        drawBird(g.bird.x, g.bird.y, 0);
        ctx.fillStyle = "#ffd700"; ctx.font="bold 26px Georgia"; ctx.textAlign="center";
        ctx.fillText("🐦 FLAPPY KUŞ", W/2, H/2-60);
        ctx.font="14px Georgia"; ctx.fillStyle="rgba(255,255,255,.5)";
        ctx.fillText("SPACE veya Tıkla → Zıpla", W/2, H/2+60);
        ctx.fillText("Borulardan kaç!", W/2, H/2+84);
        ctx.textAlign="left";
        g.animFrame=requestAnimationFrame(loop); return;
      }

      if (g.running) {
        g.frame++;
        g.pipeSpeed = 2.5 + Math.floor(g.score/5)*0.2;

        // Kuş fiziği
        g.bird.vy += g.gravity;
        g.bird.y += g.bird.vy;
        g.bird.rot = g.bird.vy;

        // Boru oluştur
        if (g.frame % g.pipeInterval === 0) {
          const topH = 60 + Math.random()*(H-g.pipeGap-120);
          g.pipes.push({ x:W+10, topH, botY:topH+g.pipeGap, scored:false });
        }

        // Boru hareketi
        g.pipes = g.pipes.filter(p => p.x > -60);
        g.pipes.forEach(p => {
          p.x -= g.pipeSpeed;
          // Skor
          if (!p.scored && p.x+52 < g.bird.x) {
            p.scored=true; g.score++;
            setDisplay(d=>({...d,score:g.score}));
          }
        });

        // Çarpışma: zemin/tavan
        if (g.bird.y+g.bird.r > H-40 || g.bird.y-g.bird.r < 0) {
          explode(g.bird.x, g.bird.y);
          g.running=false; g.over=true;
          if (g.score>g.hiScore) g.hiScore=g.score;
          api.saveScore(g.score,"flappy").catch(()=>{});
          loadScores();
          setDisplay(d=>({...d,over:true,hiScore:g.hiScore}));
        }

        // Çarpışma: borular
        g.pipes.forEach(p => {
          if (g.bird.x+g.bird.r > p.x+4 && g.bird.x-g.bird.r < p.x+52-4) {
            if (g.bird.y-g.bird.r < p.topH || g.bird.y+g.bird.r > p.botY) {
              explode(g.bird.x, g.bird.y);
              g.running=false; g.over=true;
              if (g.score>g.hiScore) g.hiScore=g.score;
              api.saveScore(g.score,"flappy").catch(()=>{});
              loadScores();
              setDisplay(d=>({...d,over:true,hiScore:g.hiScore}));
            }
          }
        });
      }

      // Boruları çiz
      g.pipes.forEach(p => drawPipe(p.x, p.topH, p.botY));

      // Parçacıklar
      g.particles = g.particles.filter(p=>p.life>0);
      g.particles.forEach(p => {
        ctx.fillStyle=p.color; ctx.globalAlpha=p.life/35;
        ctx.beginPath(); ctx.arc(p.x,p.y,4,0,Math.PI*2); ctx.fill();
        p.x+=p.vx; p.y+=p.vy; p.vy+=0.2; p.life--;
      });
      ctx.globalAlpha=1;

      // Kuş
      if (!g.over) drawBird(g.bird.x, g.bird.y, g.bird.rot);

      // Skor
      ctx.fillStyle="#fff"; ctx.font="bold 32px Georgia"; ctx.textAlign="center";
      ctx.shadowBlur=8; ctx.shadowColor="rgba(0,0,0,.8)";
      ctx.fillText(g.score, W/2, 50);
      ctx.shadowBlur=0; ctx.textAlign="left";

      if (g.over) {
        ctx.fillStyle="rgba(0,0,0,.65)"; ctx.fillRect(0,0,W,H);
        ctx.fillStyle="#ff6b6b"; ctx.font="bold 30px Georgia"; ctx.textAlign="center";
        ctx.fillText("ÇARPTИН! 💥", W/2, H/2-30);
        ctx.fillStyle="#ffd700"; ctx.font="bold 20px Georgia";
        ctx.fillText("Skor: "+g.score, W/2, H/2+10);
        if (g.score===g.hiScore && g.score>0) {
          ctx.fillStyle="#48c78e"; ctx.font="14px Georgia";
          ctx.fillText("🏆 Yeni Rekor!", W/2, H/2+36);
        }
        ctx.fillStyle="rgba(255,255,255,.5)"; ctx.font="14px Georgia";
        ctx.fillText("SPACE veya Tıkla → Tekrar", W/2, H/2+62);
        ctx.textAlign="left";
      }

      g.animFrame=requestAnimationFrame(loop);
    }

    g.animFrame=requestAnimationFrame(loop);
    return ()=>{ cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown",onKey); };
  }, []);

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(255,215,0,.15),rgba(247,151,30,.1))", border:"1px solid rgba(255,215,0,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🐦 Flappy Kuş</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>SPACE veya Tıkla → Zıpla | Borulardan kaç!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#ffd700", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={480} height={520}
        style={{ width:"100%", maxWidth:480, borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"pointer", display:"block", margin:"0 auto" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length===0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s,i)=>(
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#ffd700", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Pac-Man ──────────────────────────────────────────────────────────────────
function PacmanGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false, won: false,
    score: 0, hiScore: 0, lives: 3, level: 1, frame: 0,
    pac: { x:0, y:0, dir:{x:1,y:0}, nextDir:{x:1,y:0}, mouth:0, mouthDir:1, speed:2 },
    ghosts: [], dots: [], powerDots: [],
    frightTimer: 0, keys: {}, animFrame: null,
  });
  const [display, setDisplay] = useState({ score:0, hiScore:0, lives:3, over:false, started:false, won:false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  const CELL = 24;
  const MAP = [
    "####################",
    "#........##........#",
    "#.##.###.##.###.##.#",
    "#O##.###.##.###.##O#",
    "#..................#",
    "#.##.#.######.#.##.#",
    "#....#...##...#....#",
    "####.###.##.###.####",
    "   #.#        #.#   ",
    "####.# ##--## #.####",
    "     .  #    #  .   ",
    "####.# ###### #.####",
    "   #.#        #.#   ",
    "####.# ###### #.####",
    "#........##........#",
    "#.##.###.##.###.##.#",
    "#O..#.....P.....#..O#",
    "###.#.#.######.#.###",
    "#......#..##..#......#",
    "####################",
  ];

  // Basit maze
  const MAZE = [
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,2,2,2,2,2,2,2,2,1,1,2,2,2,2,2,2,2,2,1],
    [1,2,1,1,2,1,1,1,2,1,1,2,1,1,1,2,1,1,2,1],
    [1,3,1,1,2,1,1,1,2,1,1,2,1,1,1,2,1,1,3,1],
    [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
    [1,2,1,1,2,1,2,1,1,1,1,1,1,2,1,2,1,1,2,1],
    [1,2,2,2,2,1,2,2,2,1,1,2,2,2,1,2,2,2,2,1],
    [1,1,1,1,2,1,1,1,0,1,1,0,1,1,1,2,1,1,1,1],
    [1,1,1,1,2,1,0,0,0,0,0,0,0,0,1,2,1,1,1,1],
    [1,1,1,1,2,1,0,1,1,0,0,1,1,0,1,2,1,1,1,1],
    [0,0,0,0,2,0,0,1,0,0,0,0,1,0,0,2,0,0,0,0],
    [1,1,1,1,2,1,0,1,1,1,1,1,1,0,1,2,1,1,1,1],
    [1,1,1,1,2,1,0,0,0,0,0,0,0,0,1,2,1,1,1,1],
    [1,1,1,1,2,1,0,1,1,1,1,1,1,0,1,2,1,1,1,1],
    [1,2,2,2,2,2,2,2,2,1,1,2,2,2,2,2,2,2,2,1],
    [1,2,1,1,2,1,1,1,2,1,1,2,1,1,1,2,1,1,2,1],
    [1,3,2,1,2,2,2,2,2,2,2,2,2,2,2,2,1,2,3,1],
    [1,1,2,1,2,1,2,1,1,1,1,1,1,2,1,2,1,2,1,1],
    [1,2,2,2,2,1,2,2,2,1,1,2,2,2,1,2,2,2,2,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  ];

  const ROWS = MAZE.length, COLS = MAZE[0].length;

  async function loadScores() {
    try { const rows = await api.getScores("pacman"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;

    function initGame() {
      g.score = 0; g.lives = 3; g.frame = 0; g.frightTimer = 0;
      g.pac = { x:10*CELL+CELL/2, y:16*CELL+CELL/2, dir:{x:0,y:0}, nextDir:{x:1,y:0}, mouth:0, mouthDir:1, speed:1.4 };

      // Dots
      g.dots = []; g.powerDots = [];
      for (let r=0; r<ROWS; r++) for (let c=0; c<COLS; c++) {
        if (MAZE[r][c]===2) g.dots.push({x:c,y:r,eaten:false});
        if (MAZE[r][c]===3) g.powerDots.push({x:c,y:r,eaten:false});
      }

      // Ghosts
      const ghostColors = ["#ff6b6b","#ffb8ff","#00ffff","#ffb847"];
      const ghostStarts = [[1,1],[18,1],[1,18],[18,18]];
      g.ghosts = ghostColors.map((color,i) => ({
        x: ghostStarts[i][0]*CELL+CELL/2, y: ghostStarts[i][1]*CELL+CELL/2,
        dir:{x:i<2?1:-1,y:0}, color, frightened:false,
        speed:0.9, scatter:i%2===0,
      }));

      setDisplay(d => ({...d, score:0, lives:3, won:false, over:false}));
    }

    function canMove(x, y, dx, dy, speed=1) {
      const nx = x + dx*speed, ny = y + dy*speed;
      const margin = CELL*0.4;
      const checks = [
        [nx, ny],
        [nx-margin, ny-margin],
        [nx+margin, ny-margin],
        [nx-margin, ny+margin],
        [nx+margin, ny+margin],
      ];
      return checks.every(([cx,cy]) => {
        const col = Math.floor(cx/CELL), row = Math.floor(cy/CELL);
        if (row<0||row>=ROWS||col<0||col>=COLS) return true;
        return MAZE[row][col] !== 1;
      });
    }

    function start() {
      g.started = true; g.running = true; g.over = false; g.won = false;
      initGame();
      setDisplay(d => ({...d, started:true}));
    }

    const onKey = e => {
      if (!g.started && e.code==="Space") { start(); e.preventDefault(); return; }
      if ((g.over||g.won) && e.code==="Space") { start(); e.preventDefault(); return; }
      if (e.code==="ArrowLeft")  g.pac.nextDir = {x:-1,y:0};
      if (e.code==="ArrowRight") g.pac.nextDir = {x:1,y:0};
      if (e.code==="ArrowUp")    g.pac.nextDir = {x:0,y:-1};
      if (e.code==="ArrowDown")  g.pac.nextDir = {x:0,y:1};
      if (["ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Space"].includes(e.code)) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);

    // Touch swipe
    let ts = null;
    canvas.addEventListener("touchstart", e => { ts={x:e.touches[0].clientX,y:e.touches[0].clientY}; e.preventDefault(); },{passive:false});
    canvas.addEventListener("touchend", e => {
      if (!ts) return;
      if (!g.started||g.over||g.won) { start(); return; }
      const dx=e.changedTouches[0].clientX-ts.x, dy=e.changedTouches[0].clientY-ts.y;
      if (Math.abs(dx)>Math.abs(dy)) g.pac.nextDir=dx>0?{x:1,y:0}:{x:-1,y:0};
      else g.pac.nextDir=dy>0?{x:0,y:1}:{x:0,y:-1};
      ts=null; e.preventDefault();
    },{passive:false});

    function drawMaze() {
      for (let r=0; r<ROWS; r++) {
        for (let c=0; c<COLS; c++) {
          if (MAZE[r][c]===1) {
            ctx.fillStyle = "#1a1a6e";
            ctx.fillRect(c*CELL, r*CELL, CELL, CELL);
            ctx.strokeStyle = "#4444ff";
            ctx.lineWidth = 1.5;
            ctx.strokeRect(c*CELL+0.75, r*CELL+0.75, CELL-1.5, CELL-1.5);
          }
        }
      }
    }

    function drawDots() {
      g.dots.forEach(d => {
        if (d.eaten) return;
        ctx.fillStyle = "#ffdb99";
        ctx.beginPath(); ctx.arc(d.x*CELL+CELL/2, d.y*CELL+CELL/2, 3, 0, Math.PI*2); ctx.fill();
      });
      g.powerDots.forEach(d => {
        if (d.eaten) return;
        const pulse = Math.sin(g.frame*0.1)*2;
        ctx.fillStyle = "#ffd700";
        ctx.shadowBlur=10; ctx.shadowColor="#ffd700";
        ctx.beginPath(); ctx.arc(d.x*CELL+CELL/2, d.y*CELL+CELL/2, 6+pulse, 0, Math.PI*2); ctx.fill();
        ctx.shadowBlur=0;
      });
    }

    function drawPac() {
      const p = g.pac;
      p.mouth += 0.15 * p.mouthDir;
      if (p.mouth > 0.4 || p.mouth < 0.02) p.mouthDir *= -1;
      const angle = Math.atan2(p.dir.y, p.dir.x);
      ctx.fillStyle = "#ffd700";
      ctx.shadowBlur=8; ctx.shadowColor="#ffd700";
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.arc(p.x, p.y, CELL*0.45, angle+p.mouth, angle+Math.PI*2-p.mouth);
      ctx.closePath(); ctx.fill();
      ctx.shadowBlur=0;
    }

    function drawGhost(gh) {
      const x=gh.x, y=gh.y, r=CELL*0.45;
      const color = gh.frightened ? (g.frightTimer<60&&Math.floor(g.frame/8)%2===0?"#fff":"#0000ff") : gh.color;
      ctx.fillStyle = color;
      ctx.shadowBlur=6; ctx.shadowColor=color;
      ctx.beginPath();
      ctx.arc(x, y-r*0.1, r, Math.PI, 0);
      ctx.lineTo(x+r, y+r);
      // Wavy bottom
      for (let i=4;i>=0;i--) ctx.lineTo(x-r+r*2/4*i + (i%2===0?0:r*0.3), y+r*(i%2===0?0.7:1));
      ctx.lineTo(x-r, y+r);
      ctx.closePath(); ctx.fill();
      ctx.shadowBlur=0;
      // Eyes
      if (!gh.frightened) {
        ctx.fillStyle="#fff";
        ctx.beginPath(); ctx.arc(x-r*0.3, y-r*0.1, r*0.25, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(x+r*0.3, y-r*0.1, r*0.25, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle="#00f";
        ctx.beginPath(); ctx.arc(x-r*0.3+gh.dir.x*2, y-r*0.1+gh.dir.y*2, r*0.12, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(x+r*0.3+gh.dir.x*2, y-r*0.1+gh.dir.y*2, r*0.12, 0, Math.PI*2); ctx.fill();
      }
    }

    function moveGhost(gh) {
      const DIRS = [{x:1,y:0},{x:-1,y:0},{x:0,y:1},{x:0,y:-1}];
      gh.x += gh.dir.x * gh.speed;
      gh.y += gh.dir.y * gh.speed;
      const col = Math.floor(gh.x / CELL);
      const row = Math.floor(gh.y / CELL);
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS || MAZE[row][col] === 1) {
        gh.x -= gh.dir.x * gh.speed;
        gh.y -= gh.dir.y * gh.speed;
        const pc = Math.floor(gh.x/CELL), pr = Math.floor(gh.y/CELL);
        const vd = DIRS.filter(d => { const nc=pc+d.x,nr=pr+d.y; return nc>=0&&nc<COLS&&nr>=0&&nr<ROWS&&MAZE[nr][nc]!==1; });
        if (vd.length > 0) gh.dir = vd[Math.floor(Math.random()*vd.length)];
        return;
      }
      if (g.frame % Math.max(1, Math.floor(CELL/gh.speed)) === 0) {
        const vd = DIRS.filter(d => {
          if (d.x===-gh.dir.x && d.y===-gh.dir.y) return false;
          const nc=col+d.x, nr=row+d.y;
          return nc>=0&&nc<COLS&&nr>=0&&nr<ROWS&&MAZE[nr][nc]!==1;
        });
        if (vd.length === 0) { gh.dir={x:-gh.dir.x,y:-gh.dir.y}; return; }
        if (gh.frightened) {
          gh.dir = vd[Math.floor(Math.random()*vd.length)];
        } else {
          gh.dir = vd.reduce((best,d) => {
            const dc=Math.hypot(col+d.x-g.pac.x/CELL, row+d.y-g.pac.y/CELL);
            const db=Math.hypot(col+best.x-g.pac.x/CELL, row+best.y-g.pac.y/CELL);
            return dc<db?d:best;
          });
        }
      }
    }

    function loop() {
      ctx.fillStyle="#000"; ctx.fillRect(0,0,W,H);

      if (!g.started) {
        ctx.fillStyle="#ffd700"; ctx.font="bold 28px Georgia"; ctx.textAlign="center";
        ctx.fillText("👻 PAC-MAN", W/2, H/2-40);
        ctx.font="14px Georgia"; ctx.fillStyle="rgba(255,255,255,.5)";
        ctx.fillText("Ok tuşları ile oyna", W/2, H/2);
        ctx.fillText("Tüm noktaları topla!", W/2, H/2+26);
        ctx.fillText("SPACE → Başla", W/2, H/2+56);
        ctx.textAlign="left";
        g.animFrame=requestAnimationFrame(loop); return;
      }

      drawMaze();
      drawDots();

      if (g.running) {
        g.frame++;
        if (g.frightTimer>0) g.frightTimer--;
        const p=g.pac;
        if (canMove(p.x,p.y,p.nextDir.x,p.nextDir.y,p.speed)) p.dir=p.nextDir;
        if (canMove(p.x,p.y,p.dir.x,p.dir.y,p.speed)) { p.x+=p.dir.x*p.speed; p.y+=p.dir.y*p.speed; }
        if (p.x<0) p.x=W; if (p.x>W) p.x=0;
        g.dots.forEach(d => {
          if (d.eaten) return;
          if (Math.hypot(p.x-d.x*CELL-CELL/2, p.y-d.y*CELL-CELL/2)<CELL*0.5) {
            d.eaten=true; g.score+=10; setDisplay(d2=>({...d2,score:g.score}));
          }
        });
        g.powerDots.forEach(d => {
          if (d.eaten) return;
          if (Math.hypot(p.x-d.x*CELL-CELL/2, p.y-d.y*CELL-CELL/2)<CELL*0.6) {
            d.eaten=true; g.score+=50; g.frightTimer=300;
            g.ghosts.forEach(gh=>gh.frightened=true);
            setDisplay(d2=>({...d2,score:g.score}));
          }
        });
        if (g.frightTimer===0) g.ghosts.forEach(gh=>gh.frightened=false);
        g.ghosts.forEach(gh=>moveGhost(gh));
        g.ghosts.forEach(gh=>{
          if (Math.hypot(p.x-gh.x, p.y-gh.y)<CELL*0.55) {
            if (gh.frightened) {
              gh.frightened=false; gh.x=9*CELL+CELL/2; gh.y=9*CELL+CELL/2;
              g.score+=200; setDisplay(d=>({...d,score:g.score}));
            } else {
              g.lives--; setDisplay(d=>({...d,lives:g.lives}));
              if (g.lives<=0) {
                g.running=false; g.over=true;
                if (g.score>g.hiScore) g.hiScore=g.score;
                api.saveScore(g.score,"pacman").catch(()=>{});
                loadScores();
                setDisplay(d=>({...d,over:true,hiScore:g.hiScore}));
              } else {
                p.x=10*CELL+CELL/2; p.y=16*CELL+CELL/2;
                p.dir={x:0,y:0}; p.nextDir={x:1,y:0};
              }
            }
          }
        });
        if (g.dots.every(d=>d.eaten) && g.powerDots.every(d=>d.eaten)) {
          g.running=false; g.won=true;
          if (g.score>g.hiScore) g.hiScore=g.score;
          api.saveScore(g.score,"pacman").catch(()=>{});
          loadScores();
          setDisplay(d=>({...d,won:true,hiScore:g.hiScore}));
        }
      }

      g.ghosts.forEach(gh=>drawGhost(gh));
      drawPac();

      // HUD
      ctx.fillStyle="rgba(255,255,255,.7)"; ctx.font="13px Georgia"; ctx.textAlign="left";
      ctx.fillText("❤️".repeat(Math.max(0,g.lives)), 8, H-8);
      ctx.textAlign="right";
      ctx.fillText("Skor: "+g.score, W-8, H-8);
      ctx.textAlign="left";

      if (g.over||g.won) {
        ctx.fillStyle="rgba(0,0,0,.75)"; ctx.fillRect(0,0,W,H);
        ctx.fillStyle=g.won?"#ffd700":"#ff6b6b";
        ctx.font="bold 28px Georgia"; ctx.textAlign="center";
        ctx.fillText(g.won?"TEBRİKLER! 🎉":"OYUN BİTTİ! 👻", W/2, H/2-20);
        ctx.fillStyle="rgba(255,255,255,.5)"; ctx.font="14px Georgia";
        ctx.fillText("Skor: "+g.score, W/2, H/2+10);
        ctx.fillText("SPACE → Tekrar", W/2, H/2+36);
        ctx.textAlign="left";
      }

      g.animFrame=requestAnimationFrame(loop);
    }

    g.animFrame=requestAnimationFrame(loop);
    return ()=>{ cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown",onKey); };
  }, []);

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(255,215,0,.15),rgba(247,151,30,.1))", border:"1px solid rgba(255,215,0,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>👻 Pac-Man</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>Ok tuşları ile oyna | Noktaları topla | Hayaletlerden kaç!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#ffd700", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={COLS*CELL} height={ROWS*CELL+28}
        style={{ width:"100%", borderRadius:14, border:"1px solid rgba(255,255,255,.08)", display:"block" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length===0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s,i)=>(
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#ffd700", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Ping Pong ────────────────────────────────────────────────────────────────
function PingPongGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false,
    playerScore: 0, aiScore: 0, hiScore: 0, frame: 0,
    player: { x:0, y:0, w:12, h:80, speed:6, dy:0 },
    ai: { x:0, y:0, w:12, h:80, speed:4 },
    ball: { x:0, y:0, vx:5, vy:3, r:8 },
    particles: [], keys: {}, animFrame: null,
    scored: false, scoreTimer: 0,
  });
  const [display, setDisplay] = useState({ playerScore:0, aiScore:0, hiScore:0, over:false, started:false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);
  const WIN_SCORE = 7;

  async function loadScores() {
    try { const rows = await api.getScores("pingpong"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;

    function resetBall(dir = 1) {
      g.ball.x = W/2; g.ball.y = H/2;
      const angle = (Math.random()*0.6 - 0.3);
      const speed = 5 + Math.floor((g.playerScore + g.aiScore) / 3) * 0.4;
      g.ball.vx = dir * speed * Math.cos(angle);
      g.ball.vy = speed * Math.sin(angle) * (Math.random() > 0.5 ? 1 : -1);
      g.scored = false;
    }

    function initGame() {
      g.player.x = 20; g.player.y = H/2 - g.player.h/2;
      g.ai.x = W - 20 - g.ai.w; g.ai.y = H/2 - g.ai.h/2;
      g.playerScore = 0; g.aiScore = 0; g.frame = 0;
      g.particles = [];
      resetBall(1);
    }

    function start() {
      g.started = true; g.running = true; g.over = false;
      initGame();
      setDisplay(d => ({...d, started:true, over:false, playerScore:0, aiScore:0}));
    }

    function explode(x, y, color) {
      for (let i = 0; i < 10; i++) {
        const angle = (i/10)*Math.PI*2;
        g.particles.push({
          x, y,
          vx: Math.cos(angle) * (2+Math.random()*3),
          vy: Math.sin(angle) * (2+Math.random()*3),
          life: 25, color,
        });
      }
    }

    const onKey = e => {
      g.keys[e.code] = true;
      if (!g.started && e.code==="Space") { start(); e.preventDefault(); }
      if (g.over && e.code==="Space") { start(); e.preventDefault(); }
      if (["ArrowUp","ArrowDown","Space"].includes(e.code)) e.preventDefault();
    };
    const onKeyUp = e => { g.keys[e.code] = false; };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);

    // Mouse/touch control
    canvas.addEventListener("mousemove", e => {
      const rect = canvas.getBoundingClientRect();
      const scaleY = H / rect.height;
      const my = (e.clientY - rect.top) * scaleY;
      g.player.y = Math.max(0, Math.min(H - g.player.h, my - g.player.h/2));
    });
    canvas.addEventListener("touchmove", e => {
      const rect = canvas.getBoundingClientRect();
      const scaleY = H / rect.height;
      const my = (e.touches[0].clientY - rect.top) * scaleY;
      g.player.y = Math.max(0, Math.min(H - g.player.h, my - g.player.h/2));
      e.preventDefault();
    }, {passive:false});
    canvas.addEventListener("click", () => {
      if (!g.started || g.over) start();
    });

    function drawPaddle(x, y, w, h, color) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 6);
      ctx.fill();
      // Glow
      ctx.shadowBlur = 12; ctx.shadowColor = color;
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    function loop() {
      // Background
      ctx.fillStyle = "#050810";
      ctx.fillRect(0, 0, W, H);

      // Center line
      ctx.setLineDash([8, 8]);
      ctx.strokeStyle = "rgba(255,255,255,.1)";
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(W/2, 0); ctx.lineTo(W/2, H); ctx.stroke();
      ctx.setLineDash([]);

      // Stars
      for (let i=0; i<40; i++) {
        const sx = (i*173)%W, sy = (i*97)%H;
        ctx.fillStyle = `rgba(255,255,255,${0.1+Math.sin(i+g.frame*0.02)*0.1})`;
        ctx.fillRect(sx, sy, 1, 1);
      }

      if (!g.started) {
        ctx.fillStyle = "#4facfe"; ctx.font = "bold 28px Georgia"; ctx.textAlign = "center";
        ctx.fillText("🏓 PING PONG", W/2, H/2-40);
        ctx.font = "14px Georgia"; ctx.fillStyle = "rgba(255,255,255,.5)";
        ctx.fillText("↑ ↓ veya Mouse ile oyna", W/2, H/2);
        ctx.fillText("İlk " + WIN_SCORE + " sayıya ulaşan kazanır", W/2, H/2+26);
        ctx.fillText("SPACE veya Tıkla → Başla", W/2, H/2+56);
        ctx.textAlign = "left";
        g.animFrame = requestAnimationFrame(loop);
        return;
      }

      if (g.running) {
        g.frame++;

        // Klavye kontrolü
        if (g.keys["ArrowUp"])   g.player.y = Math.max(0, g.player.y - g.player.speed);
        if (g.keys["ArrowDown"]) g.player.y = Math.min(H - g.player.h, g.player.y + g.player.speed);
        if (g.keys["KeyW"])      g.player.y = Math.max(0, g.player.y - g.player.speed);
        if (g.keys["KeyS"])      g.player.y = Math.min(H - g.player.h, g.player.y + g.player.speed);

        // AI hareketi
        const aiCenter = g.ai.y + g.ai.h/2;
        const aiSpeed = g.ai.speed + Math.floor((g.playerScore+g.aiScore)/4)*0.3;
        if (g.ball.vx > 0) {
          if (aiCenter < g.ball.y - 10) g.ai.y = Math.min(H-g.ai.h, g.ai.y + aiSpeed);
          if (aiCenter > g.ball.y + 10) g.ai.y = Math.max(0, g.ai.y - aiSpeed);
        } else {
          // Orta konuma dön
          if (aiCenter < H/2 - 20) g.ai.y += aiSpeed * 0.5;
          if (aiCenter > H/2 + 20) g.ai.y -= aiSpeed * 0.5;
        }

        // Top hareketi
        g.ball.x += g.ball.vx;
        g.ball.y += g.ball.vy;

        // Üst/alt duvar
        if (g.ball.y - g.ball.r < 0) { g.ball.y = g.ball.r; g.ball.vy = Math.abs(g.ball.vy); }
        if (g.ball.y + g.ball.r > H) { g.ball.y = H-g.ball.r; g.ball.vy = -Math.abs(g.ball.vy); }

        // Oyuncu raket çarpışması
        const p = g.player;
        if (g.ball.x - g.ball.r < p.x + p.w && g.ball.x + g.ball.r > p.x &&
            g.ball.y > p.y && g.ball.y < p.y + p.h && g.ball.vx < 0) {
          const hitPos = (g.ball.y - p.y) / p.h - 0.5;
          const speed = Math.min(12, Math.sqrt(g.ball.vx**2 + g.ball.vy**2) * 1.05);
          g.ball.vx = speed * Math.cos(hitPos * 1.2);
          g.ball.vy = speed * Math.sin(hitPos * 1.2);
          g.ball.x = p.x + p.w + g.ball.r;
          explode(g.ball.x, g.ball.y, "#4facfe");
        }

        // AI raket çarpışması
        const ai = g.ai;
        if (g.ball.x + g.ball.r > ai.x && g.ball.x - g.ball.r < ai.x + ai.w &&
            g.ball.y > ai.y && g.ball.y < ai.y + ai.h && g.ball.vx > 0) {
          const hitPos = (g.ball.y - ai.y) / ai.h - 0.5;
          const speed = Math.min(12, Math.sqrt(g.ball.vx**2 + g.ball.vy**2) * 1.05);
          g.ball.vx = -speed * Math.cos(hitPos * 1.2);
          g.ball.vy = speed * Math.sin(hitPos * 1.2);
          g.ball.x = ai.x - g.ball.r;
          explode(g.ball.x, g.ball.y, "#ff6b6b");
        }

        // Skor
        if (g.ball.x < 0) {
          g.aiScore++;
          explode(50, H/2, "#ff6b6b");
          setDisplay(d => ({...d, aiScore:g.aiScore}));
          if (g.aiScore >= WIN_SCORE) {
            g.running = false; g.over = true;
            api.saveScore(g.playerScore, "pingpong").catch(()=>{});
            if (g.playerScore > g.hiScore) g.hiScore = g.playerScore;
            loadScores();
            setDisplay(d => ({...d, over:true, hiScore:g.hiScore}));
          } else { setTimeout(()=>resetBall(1), 800); g.running = false; setTimeout(()=>{ g.running=true; }, 800); }
        }
        if (g.ball.x > W) {
          g.playerScore++;
          explode(W-50, H/2, "#4facfe");
          setDisplay(d => ({...d, playerScore:g.playerScore}));
          if (g.playerScore >= WIN_SCORE) {
            g.running = false; g.over = true;
            api.saveScore(g.playerScore, "pingpong").catch(()=>{});
            if (g.playerScore > g.hiScore) g.hiScore = g.playerScore;
            loadScores();
            setDisplay(d => ({...d, over:true, hiScore:g.hiScore}));
          } else { setTimeout(()=>resetBall(-1), 800); g.running = false; setTimeout(()=>{ g.running=true; }, 800); }
        }
      }

      // Parçacıklar
      g.particles = g.particles.filter(p => p.life > 0);
      g.particles.forEach(p => {
        ctx.fillStyle = p.color; ctx.globalAlpha = p.life/25;
        ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI*2); ctx.fill();
        p.x+=p.vx; p.y+=p.vy; p.life--;
      });
      ctx.globalAlpha = 1;

      // Raketler
      drawPaddle(g.player.x, g.player.y, g.player.w, g.player.h, "#4facfe");
      drawPaddle(g.ai.x, g.ai.y, g.ai.w, g.ai.h, "#ff6b6b");

      // Top
      ctx.fillStyle = "#fff";
      ctx.shadowBlur = 15; ctx.shadowColor = "#fff";
      ctx.beginPath(); ctx.arc(g.ball.x, g.ball.y, g.ball.r, 0, Math.PI*2); ctx.fill();
      ctx.shadowBlur = 0;

      // Skorlar
      ctx.fillStyle = "rgba(79,172,254,.8)"; ctx.font = "bold 48px Georgia"; ctx.textAlign = "center";
      ctx.fillText(g.playerScore, W/4, 64);
      ctx.fillStyle = "rgba(255,107,107,.8)";
      ctx.fillText(g.aiScore, 3*W/4, 64);
      ctx.textAlign = "left";

      // Etiketler
      ctx.fillStyle = "rgba(255,255,255,.2)"; ctx.font = "11px Georgia"; ctx.textAlign = "center";
      ctx.fillText("SEN", W/4, 82);
      ctx.fillText("BİLGİSAYAR", 3*W/4, 82);
      ctx.textAlign = "left";

      if (g.over) {
        ctx.fillStyle = "rgba(0,0,0,.7)"; ctx.fillRect(0,0,W,H);
        const won = g.playerScore >= WIN_SCORE;
        ctx.fillStyle = won?"#48c78e":"#ff6b6b";
        ctx.font = "bold 32px Georgia"; ctx.textAlign = "center";
        ctx.fillText(won?"KAZANDIN! 🏆":"KAYBETTİN! 😢", W/2, H/2-20);
        ctx.fillStyle = "rgba(255,255,255,.5)"; ctx.font = "14px Georgia";
        ctx.fillText("SPACE veya Tıkla → Tekrar", W/2, H/2+20);
        ctx.textAlign = "left";
      }

      g.animFrame = requestAnimationFrame(loop);
    }

    g.animFrame = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onKeyUp); };
  }, []);

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(79,172,254,.15),rgba(0,242,254,.1))", border:"1px solid rgba(79,172,254,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🏓 Ping Pong</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>↑ ↓ veya Mouse | İlk {WIN_SCORE} sayı kazanır!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#4facfe", fontSize:18, fontWeight:800 }}>{display.playerScore} — {display.aiScore}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={720} height={420}
        style={{ width:"100%", borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"none", display:"block" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s, i) => (
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#4facfe", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Tetris ───────────────────────────────────────────────────────────────────
function TetrisGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false,
    score: 0, hiScore: 0, lines: 0, level: 1, frame: 0,
    board: [], piece: null, nextPiece: null,
    dropTimer: 0, dropInterval: 48,
    keys: {}, animFrame: null,
    lockTimer: 0, lockDelay: 30,
  });
  const [display, setDisplay] = useState({ score:0, hiScore:0, lines:0, level:1, over:false, started:false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  const COLS = 10, ROWS = 20, CELL = 28;
  const COLORS = ["#000","#00f2fe","#ffd700","#a78bfa","#48c78e","#ff6b6b","#4facfe","#ff9f43"];
  const SHAPES = [
    [],
    [[1,1,1,1]],
    [[2,2],[2,2]],
    [[0,3,0],[3,3,3]],
    [[4,0],[4,0],[4,4]],
    [[0,5],[0,5],[5,5]],
    [[6,6,0],[0,6,6]],
    [[0,7,7],[7,7,0]],
  ];

  async function loadScores() {
    try { const rows = await api.getScores("tetris"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;

    function emptyBoard() {
      return Array.from({length:ROWS}, () => Array(COLS).fill(0));
    }

    function randomPiece() {
      const type = Math.floor(Math.random()*7) + 1;
      const shape = SHAPES[type].map(r => [...r]);
      return { type, shape, x: Math.floor(COLS/2) - Math.floor(shape[0].length/2), y: 0 };
    }

    function start() {
      g.started = true; g.running = true; g.over = false;
      g.score = 0; g.lines = 0; g.level = 1; g.frame = 0;
      g.board = emptyBoard();
      g.dropInterval = 48;
      g.piece = randomPiece();
      g.nextPiece = randomPiece();
      setDisplay(d => ({...d, started:true, over:false, score:0, lines:0, level:1}));
    }

    function rotate(shape) {
      return shape[0].map((_,i) => shape.map(r => r[i]).reverse());
    }

    function isValid(shape, x, y) {
      return shape.every((row, r) =>
        row.every((cell, c) => {
          if (!cell) return true;
          const nx = x + c, ny = y + r;
          return nx >= 0 && nx < COLS && ny < ROWS && (ny < 0 || !g.board[ny][nx]);
        })
      );
    }

    function lockPiece() {
      const {shape, x, y, type} = g.piece;
      shape.forEach((row, r) => row.forEach((cell, c) => {
        if (cell && y+r >= 0) g.board[y+r][x+c] = type;
      }));

      // Satır temizle
      let cleared = 0;
      for (let r = ROWS-1; r >= 0; r--) {
        if (g.board[r].every(c => c !== 0)) {
          g.board.splice(r, 1);
          g.board.unshift(Array(COLS).fill(0));
          cleared++; r++;
        }
      }

      if (cleared > 0) {
        const pts = [0,100,300,500,800][cleared] * g.level;
        g.score += pts;
        g.lines += cleared;
        g.level = Math.floor(g.lines / 10) + 1;
        g.dropInterval = Math.max(6, 48 - (g.level-1) * 4);
        setDisplay(d => ({...d, score:g.score, lines:g.lines, level:g.level}));
      }

      g.piece = g.nextPiece;
      g.nextPiece = randomPiece();
      g.lockTimer = 0;

      if (!isValid(g.piece.shape, g.piece.x, g.piece.y)) {
        g.running = false; g.over = true;
        if (g.score > g.hiScore) g.hiScore = g.score;
        api.saveScore(g.score, "tetris").catch(()=>{});
        loadScores();
        setDisplay(d => ({...d, over:true, hiScore:g.hiScore}));
      }
    }

    function drawCell(ctx, x, y, type, alpha=1) {
      if (!type) return;
      const color = COLORS[type];
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.fillRect(x*CELL+1, y*CELL+1, CELL-2, CELL-2);
      // Highlight
      ctx.fillStyle = "rgba(255,255,255,.3)";
      ctx.fillRect(x*CELL+2, y*CELL+2, CELL-4, 4);
      ctx.fillRect(x*CELL+2, y*CELL+2, 4, CELL-4);
      // Shadow
      ctx.fillStyle = "rgba(0,0,0,.3)";
      ctx.fillRect(x*CELL+2, y*CELL+CELL-6, CELL-4, 4);
      ctx.fillRect(x*CELL+CELL-6, y*CELL+2, 4, CELL-4);
      ctx.globalAlpha = 1;
    }

    function getGhost() {
      if (!g.piece) return null;
      let gy = g.piece.y;
      while (isValid(g.piece.shape, g.piece.x, gy+1)) gy++;
      return { ...g.piece, y: gy };
    }

    const onKey = e => {
      if (!g.started && e.code==="Space") { start(); e.preventDefault(); return; }
      if (g.over && e.code==="Space") { start(); e.preventDefault(); return; }
      if (!g.running) return;

      if (e.code==="ArrowLeft") {
        if (isValid(g.piece.shape, g.piece.x-1, g.piece.y)) g.piece.x--;
      } else if (e.code==="ArrowRight") {
        if (isValid(g.piece.shape, g.piece.x+1, g.piece.y)) g.piece.x++;
      } else if (e.code==="ArrowDown") {
        if (isValid(g.piece.shape, g.piece.x, g.piece.y+1)) { g.piece.y++; g.score++; setDisplay(d=>({...d,score:g.score})); }
      } else if (e.code==="ArrowUp" || e.code==="KeyZ") {
        const rotated = rotate(g.piece.shape);
        if (isValid(rotated, g.piece.x, g.piece.y)) g.piece.shape = rotated;
        else if (isValid(rotated, g.piece.x+1, g.piece.y)) { g.piece.shape = rotated; g.piece.x++; }
        else if (isValid(rotated, g.piece.x-1, g.piece.y)) { g.piece.shape = rotated; g.piece.x--; }
      } else if (e.code==="Space") {
        // Hard drop
        while (isValid(g.piece.shape, g.piece.x, g.piece.y+1)) { g.piece.y++; g.score+=2; }
        lockPiece();
        setDisplay(d=>({...d,score:g.score}));
        e.preventDefault(); return;
      }
      if (["ArrowLeft","ArrowRight","ArrowDown","ArrowUp","Space"].includes(e.code)) e.preventDefault();
    };

    window.addEventListener("keydown", onKey);

    // Touch controls
    let touchStartX = null, touchStartY = null, touchTime = null;
    canvas.addEventListener("touchstart", e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchTime = Date.now();
      e.preventDefault();
    }, {passive:false});
    canvas.addEventListener("touchend", e => {
      if (!touchStartX) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      const dt = Date.now() - touchTime;

      if (!g.started || g.over) { start(); return; }
      if (!g.running) return;

      if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && dt < 200) {
        // Tap = rotate
        const rotated = rotate(g.piece.shape);
        if (isValid(rotated, g.piece.x, g.piece.y)) g.piece.shape = rotated;
      } else if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 20 && isValid(g.piece.shape, g.piece.x+1, g.piece.y)) g.piece.x++;
        if (dx < -20 && isValid(g.piece.shape, g.piece.x-1, g.piece.y)) g.piece.x--;
      } else if (dy > 30) {
        while (isValid(g.piece.shape, g.piece.x, g.piece.y+1)) { g.piece.y++; g.score+=2; }
        lockPiece();
      }
      touchStartX = null;
      e.preventDefault();
    }, {passive:false});

    function loop() {
      // Background
      ctx.fillStyle = "#0a0814";
      ctx.fillRect(0, 0, W, H);

      // Board area
      ctx.fillStyle = "rgba(255,255,255,.03)";
      ctx.fillRect(0, 0, COLS*CELL, ROWS*CELL);

      // Grid
      ctx.strokeStyle = "rgba(255,255,255,.05)";
      ctx.lineWidth = 0.5;
      for (let r=0; r<ROWS; r++) {
        for (let c=0; c<COLS; c++) {
          ctx.strokeRect(c*CELL, r*CELL, CELL, CELL);
        }
      }

      if (!g.started) {
        ctx.fillStyle = "#a78bfa"; ctx.font = "bold 22px Georgia"; ctx.textAlign = "center";
        ctx.fillText("TETRIS", COLS*CELL/2, ROWS*CELL/2 - 30);
        ctx.font = "13px Georgia"; ctx.fillStyle = "rgba(255,255,255,.5)";
        ctx.fillText("← → Hareket", COLS*CELL/2, ROWS*CELL/2);
        ctx.fillText("↑ Döndür | ↓ Hızlan", COLS*CELL/2, ROWS*CELL/2+22);
        ctx.fillText("SPACE Hard Drop", COLS*CELL/2, ROWS*CELL/2+44);
        ctx.fillText("SPACE → Başla", COLS*CELL/2, ROWS*CELL/2+74);
        ctx.textAlign = "left";
        g.animFrame = requestAnimationFrame(loop);
        return;
      }

      if (g.running) {
        g.frame++;
        g.dropTimer++;
        if (g.dropTimer >= g.dropInterval) {
          if (isValid(g.piece.shape, g.piece.x, g.piece.y+1)) {
            g.piece.y++;
            g.lockTimer = 0;
          } else {
            g.lockTimer++;
            if (g.lockTimer >= g.lockDelay) lockPiece();
          }
          g.dropTimer = 0;
        }
      }

      // Board çiz
      for (let r=0; r<ROWS; r++) for (let c=0; c<COLS; c++) drawCell(ctx, c, r, g.board[r][c]);

      if (g.piece) {
        // Ghost
        const ghost = getGhost();
        if (ghost && ghost.y !== g.piece.y) {
          ghost.shape.forEach((row,r) => row.forEach((cell,c) => {
            if (cell) {
              ctx.globalAlpha = 0.2;
              ctx.fillStyle = COLORS[ghost.type];
              ctx.fillRect((ghost.x+c)*CELL+1, (ghost.y+r)*CELL+1, CELL-2, CELL-2);
              ctx.globalAlpha = 1;
            }
          }));
        }
        // Aktif parça
        g.piece.shape.forEach((row,r) => row.forEach((cell,c) => {
          if (cell) drawCell(ctx, g.piece.x+c, g.piece.y+r, g.piece.type);
        }));
      }

      // Sağ panel
      const px = COLS*CELL + 12;
      ctx.fillStyle = "rgba(255,255,255,.6)"; ctx.font = "11px Georgia"; ctx.textAlign = "left";

      // Next piece
      ctx.fillStyle = "rgba(255,255,255,.4)"; ctx.font = "10px Georgia";
      ctx.fillText("SONRAKİ", px, 20);
      if (g.nextPiece) {
        g.nextPiece.shape.forEach((row,r) => row.forEach((cell,c) => {
          if (cell) {
            ctx.fillStyle = COLORS[g.nextPiece.type];
            ctx.fillRect(px + c*22, 28 + r*22, 20, 20);
            ctx.fillStyle = "rgba(255,255,255,.25)";
            ctx.fillRect(px + c*22 + 1, 28 + r*22 + 1, 18, 3);
          }
        }));
      }

      ctx.fillStyle = "rgba(255,255,255,.4)"; ctx.font = "10px Georgia";
      ctx.fillText("SKOR", px, 130);
      ctx.fillStyle = "#a78bfa"; ctx.font = "bold 16px Georgia";
      ctx.fillText(g.score, px, 148);

      ctx.fillStyle = "rgba(255,255,255,.4)"; ctx.font = "10px Georgia";
      ctx.fillText("SATIR", px, 172);
      ctx.fillStyle = "#48c78e"; ctx.font = "bold 16px Georgia";
      ctx.fillText(g.lines, px, 190);

      ctx.fillStyle = "rgba(255,255,255,.4)"; ctx.font = "10px Georgia";
      ctx.fillText("SEVİYE", px, 214);
      ctx.fillStyle = "#ffd700"; ctx.font = "bold 16px Georgia";
      ctx.fillText(g.level, px, 232);

      if (g.over) {
        ctx.fillStyle = "rgba(0,0,0,.75)"; ctx.fillRect(0,0,COLS*CELL,ROWS*CELL);
        ctx.fillStyle = "#a78bfa"; ctx.font = "bold 24px Georgia"; ctx.textAlign = "center";
        ctx.fillText("OYUN BİTTİ!", COLS*CELL/2, ROWS*CELL/2-20);
        ctx.fillStyle = "rgba(255,255,255,.5)"; ctx.font = "13px Georgia";
        ctx.fillText("SPACE → Tekrar", COLS*CELL/2, ROWS*CELL/2+20);
        ctx.textAlign = "left";
      }

      g.animFrame = requestAnimationFrame(loop);
    }

    g.animFrame = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown", onKey); };
  }, []);

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(167,139,250,.15),rgba(124,58,237,.1))", border:"1px solid rgba(167,139,250,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🟦 Tetris</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>← → Hareket | ↑ Döndür | ↓ Hızlan | SPACE Hard Drop</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#a78bfa", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <div style={{ display:"flex", gap:0 }}>
        <canvas ref={canvasRef} width={COLS*CELL + 90} height={ROWS*CELL}
          style={{ borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"pointer", display:"block" }} />
      </div>
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s, i) => (
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#a78bfa", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Uzay Saldırısı ───────────────────────────────────────────────────────────
function SpaceGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, started: false, over: false,
    score: 0, hiScore: 0, lives: 3, level: 1, frame: 0,
    player: { x: 0, y: 0, w: 40, h: 20, speed: 5 },
    bullets: [], enemyBullets: [],
    enemies: [], particles: [],
    keys: {}, animFrame: null,
    shootCooldown: 0, enemyShootTimer: 0,
    enemyDir: 1, enemySpeed: 0.5, enemyDropped: false,
  });
  const [display, setDisplay] = useState({ score:0, hiScore:0, lives:3, over:false, started:false, level:1 });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  async function loadScores() {
    try { const rows = await api.getScores("space"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const g = gameRef.current;

    function initLevel() {
      g.enemies = [];
      g.bullets = []; g.enemyBullets = [];
      g.enemyDir = 1;
      g.enemySpeed = 0.4 + g.level * 0.15;
      const rows = Math.min(3 + g.level, 6);
      const cols = 10;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          g.enemies.push({
            x: 60 + c * 58, y: 50 + r * 44,
            w: 36, h: 26, alive: true,
            type: r === 0 ? 2 : r < 2 ? 1 : 0,
            animFrame: Math.floor(Math.random()*2),
          });
        }
      }
    }

    function initPlayer() {
      g.player.x = W/2 - g.player.w/2;
      g.player.y = H - 60;
    }

    function start() {
      g.started = true; g.running = true; g.over = false;
      g.score = 0; g.lives = 3; g.level = 1; g.frame = 0;
      g.particles = [];
      initPlayer(); initLevel();
      setDisplay(d => ({...d, started:true, over:false, score:0, lives:3, level:1}));
    }

    function explode(x, y, color) {
      for (let i = 0; i < 12; i++) {
        g.particles.push({
          x, y,
          vx: (Math.random()-0.5)*5,
          vy: (Math.random()-0.5)*5,
          life: 30, color,
        });
      }
    }

    function drawPlayer(ctx) {
      const p = g.player;
      // Body
      ctx.fillStyle = "#4facfe";
      ctx.beginPath();
      ctx.moveTo(p.x + p.w/2, p.y);
      ctx.lineTo(p.x + p.w, p.y + p.h);
      ctx.lineTo(p.x, p.y + p.h);
      ctx.closePath();
      ctx.fill();
      // Cockpit
      ctx.fillStyle = "#00f2fe";
      ctx.beginPath();
      ctx.arc(p.x + p.w/2, p.y + p.h*0.6, 6, 0, Math.PI*2);
      ctx.fill();
      // Wings
      ctx.fillStyle = "#0078cc";
      ctx.fillRect(p.x - 8, p.y + p.h - 8, 12, 8);
      ctx.fillRect(p.x + p.w - 4, p.y + p.h - 8, 12, 8);
      // Engine glow
      ctx.fillStyle = `rgba(79,172,254,${0.3 + Math.sin(g.frame*0.3)*0.2})`;
      ctx.beginPath();
      ctx.arc(p.x + p.w/2, p.y + p.h + 4, 8, 0, Math.PI*2);
      ctx.fill();
    }

    function drawEnemy(ctx, e) {
      const af = Math.floor(g.frame/12)%2;
      ctx.save();
      ctx.translate(e.x + e.w/2, e.y + e.h/2);
      if (e.type === 2) {
        // UFO tipi
        ctx.fillStyle = "#ff6b6b";
        ctx.beginPath(); ctx.ellipse(0, 0, e.w/2, e.h/2, 0, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = "#ff9999";
        ctx.beginPath(); ctx.ellipse(0, -4, e.w/3, e.h/3, 0, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = "#fff";
        [-8,0,8].forEach(ox => { ctx.beginPath(); ctx.arc(ox, 2, 3, 0, Math.PI*2); ctx.fill(); });
      } else if (e.type === 1) {
        // Orta tip
        ctx.fillStyle = "#a78bfa";
        ctx.fillRect(-e.w/2, -e.h/2, e.w, e.h);
        ctx.fillStyle = "#7c3aed";
        ctx.fillRect(-e.w/2 + 4, -e.h/2 + 4, e.w-8, e.h-8);
        ctx.fillStyle = "#fff";
        ctx.fillRect(-6, -4, 4, 8);
        ctx.fillRect(2, -4, 4, 8);
        // Bacaklar
        if (af===0) {
          ctx.fillStyle = "#a78bfa";
          ctx.fillRect(-e.w/2-4, 4, 6, 4);
          ctx.fillRect(e.w/2-2, 4, 6, 4);
        } else {
          ctx.fillRect(-e.w/2-2, 2, 6, 6);
          ctx.fillRect(e.w/2-4, 2, 6, 6);
        }
      } else {
        // Alt tip
        ctx.fillStyle = "#48c78e";
        ctx.beginPath();
        ctx.moveTo(0, -e.h/2);
        ctx.lineTo(e.w/2, e.h/2);
        ctx.lineTo(-e.w/2, e.h/2);
        ctx.closePath(); ctx.fill();
        ctx.fillStyle = "#06d6a0";
        ctx.fillRect(-8, -4, 16, 8);
        if (af===0) {
          ctx.fillStyle = "#48c78e";
          ctx.fillRect(-e.w/2-6, 0, 8, 4);
          ctx.fillRect(e.w/2-2, 0, 8, 4);
        } else {
          ctx.fillRect(-e.w/2-2, -4, 8, 4);
          ctx.fillRect(e.w/2-6, -4, 8, 4);
        }
      }
      ctx.restore();
    }

    const onKey = e => { g.keys[e.code] = true; if(e.code==="Space") e.preventDefault(); };
    const onKeyUp = e => { g.keys[e.code] = false; };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);

    // Mobile touch
    let touchX = null;
    canvas.addEventListener("touchstart", e => { touchX = e.touches[0].clientX; e.preventDefault(); }, {passive:false});
    canvas.addEventListener("touchmove", e => {
      if (!touchX) return;
      const dx = e.touches[0].clientX - touchX;
      g.player.x = Math.max(0, Math.min(W - g.player.w, g.player.x + dx));
      touchX = e.touches[0].clientX;
      e.preventDefault();
    }, {passive:false});
    canvas.addEventListener("touchend", e => {
      if (!g.started) { start(); return; }
      if (g.over) { start(); return; }
      if (g.shootCooldown <= 0) {
        g.bullets.push({ x: g.player.x + g.player.w/2, y: g.player.y, speed: 9 });
        g.shootCooldown = 18;
      }
      touchX = null;
    });

    function loop() {
      ctx.fillStyle = "#050810";
      ctx.fillRect(0, 0, W, H);

      // Stars
      for (let i = 0; i < 80; i++) {
        const sx = (i * 173 + g.frame * 0.3) % W;
        const sy = (i * 97 + g.frame * 0.1) % H;
        const alpha = 0.3 + Math.sin(i + g.frame * 0.05) * 0.3;
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fillRect(sx, sy, i%3===0?2:1, i%3===0?2:1);
      }

      if (!g.started) {
        ctx.fillStyle = "#4facfe"; ctx.font = "bold 26px Georgia"; ctx.textAlign = "center";
        ctx.fillText("🚀 UZAY SALDIRISI", W/2, H/2 - 40);
        ctx.font = "14px Georgia"; ctx.fillStyle = "rgba(255,255,255,.5)";
        ctx.fillText("← → Hareket | SPACE Ateş", W/2, H/2);
        ctx.fillText("SPACE → Başla", W/2, H/2 + 30);
        ctx.textAlign = "left";
        if (g.keys["Space"]) { g.keys["Space"] = false; start(); }
        g.animFrame = requestAnimationFrame(loop);
        return;
      }

      if (g.running) {
        g.frame++;
        if (g.shootCooldown > 0) g.shootCooldown--;

        // Player hareketi
        const p = g.player;
        if (g.keys["ArrowLeft"]  || g.keys["KeyA"]) p.x = Math.max(0, p.x - p.speed);
        if (g.keys["ArrowRight"] || g.keys["KeyD"]) p.x = Math.min(W - p.w, p.x + p.speed);
        if (g.keys["Space"] && g.shootCooldown <= 0) {
          g.bullets.push({ x: p.x + p.w/2, y: p.y, speed: 10 });
          g.shootCooldown = 18;
          g.keys["Space"] = false;
        }

        // Mermi hareketi
        g.bullets = g.bullets.filter(b => b.y > -10);
        g.bullets.forEach(b => { b.y -= b.speed; });
        g.enemyBullets = g.enemyBullets.filter(b => b.y < H + 10);
        g.enemyBullets.forEach(b => { b.y += b.speed; });

        // Düşman hareketi
        const aliveEnemies = g.enemies.filter(e => e.alive);
        if (aliveEnemies.length === 0) {
          g.level++;
          initLevel();
          setDisplay(d => ({...d, level:g.level}));
          g.animFrame = requestAnimationFrame(loop);
          return;
        }

        let minX = Math.min(...aliveEnemies.map(e=>e.x));
        let maxX = Math.max(...aliveEnemies.map(e=>e.x+e.w));

        if (maxX + g.enemySpeed > W - 10) { g.enemyDir = -1; g.enemies.forEach(e=>{ e.y += 18; }); }
        if (minX + g.enemySpeed < 10)      { g.enemyDir = 1;  g.enemies.forEach(e=>{ e.y += 18; }); }
        g.enemies.forEach(e => { if(e.alive) e.x += g.enemyDir * g.enemySpeed; });

        // Düşman ateşi
        g.enemyShootTimer++;
        if (g.enemyShootTimer > Math.max(30, 80 - g.level*5)) {
          const shooters = aliveEnemies.filter(e => {
            const col = Math.round((e.x - 60) / 58);
            return !aliveEnemies.some(e2 => e2 !== e && Math.round((e2.x-60)/58)===col && e2.y > e.y);
          });
          if (shooters.length > 0) {
            const shooter = shooters[Math.floor(Math.random()*shooters.length)];
            g.enemyBullets.push({ x: shooter.x + shooter.w/2, y: shooter.y + shooter.h, speed: 4 + g.level*0.5 });
          }
          g.enemyShootTimer = 0;
        }

        // Çarpışma: oyuncu mermisi vs düşman
        g.bullets.forEach(b => {
          g.enemies.forEach(e => {
            if (!e.alive) return;
            if (b.x > e.x && b.x < e.x+e.w && b.y > e.y && b.y < e.y+e.h) {
              e.alive = false; b.y = -100;
              const pts = [10, 20, 30][e.type];
              g.score += pts;
              explode(e.x+e.w/2, e.y+e.h/2, e.type===2?"#ff6b6b":e.type===1?"#a78bfa":"#48c78e");
              setDisplay(d => ({...d, score:g.score}));
            }
          });
        });

        // Düşman mermisi vs oyuncu
        g.enemyBullets.forEach(b => {
          if (b.x > p.x && b.x < p.x+p.w && b.y > p.y && b.y < p.y+p.h) {
            b.y = H+100;
            g.lives--;
            explode(p.x+p.w/2, p.y+p.h/2, "#4facfe");
            setDisplay(d => ({...d, lives:g.lives}));
            if (g.lives <= 0) {
              g.running = false; g.over = true;
              if (g.score > g.hiScore) g.hiScore = g.score;
              api.saveScore(g.score, "space").catch(()=>{});
              loadScores();
              setDisplay(d => ({...d, over:true, hiScore:g.hiScore}));
            }
          }
        });

        // Düşman yere indi
        if (aliveEnemies.some(e => e.y + e.h >= p.y)) {
          g.running = false; g.over = true;
          if (g.score > g.hiScore) g.hiScore = g.score;
          api.saveScore(g.score, "space").catch(()=>{});
          loadScores();
          setDisplay(d => ({...d, over:true, hiScore:g.hiScore}));
        }
      }

      // Parçacıklar
      g.particles = g.particles.filter(p => p.life > 0);
      g.particles.forEach(p => {
        ctx.fillStyle = p.color; ctx.globalAlpha = p.life/30;
        ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI*2); ctx.fill();
        p.x += p.vx; p.y += p.vy; p.life--;
      });
      ctx.globalAlpha = 1;

      // Düşmanları çiz
      g.enemies.forEach(e => { if(e.alive) drawEnemy(ctx, e); });

      // Mermileri çiz
      g.bullets.forEach(b => {
        ctx.fillStyle = "#4facfe";
        ctx.shadowBlur = 8; ctx.shadowColor = "#4facfe";
        ctx.fillRect(b.x-2, b.y, 4, 12);
        ctx.shadowBlur = 0;
      });
      g.enemyBullets.forEach(b => {
        ctx.fillStyle = "#ff6b6b";
        ctx.shadowBlur = 8; ctx.shadowColor = "#ff6b6b";
        ctx.fillRect(b.x-2, b.y, 4, 12);
        ctx.shadowBlur = 0;
      });

      // Oyuncuyu çiz
      if (g.started && !g.over) drawPlayer(ctx);

      // HUD
      ctx.fillStyle = "rgba(255,255,255,.7)"; ctx.font = "13px Georgia"; ctx.textAlign = "left";
      ctx.fillText("❤️ ".repeat(Math.max(0, g.lives)), 10, H-15);
      ctx.textAlign = "right";
      ctx.fillText("Seviye " + g.level, W-10, H-15);
      ctx.textAlign = "left";

      if (g.over) {
        ctx.fillStyle = "rgba(0,0,0,.7)"; ctx.fillRect(0,0,W,H);
        ctx.fillStyle = "#ff6b6b"; ctx.font = "bold 30px Georgia"; ctx.textAlign = "center";
        ctx.fillText("OYUN BİTTİ! 🚀", W/2, H/2-24);
        ctx.fillStyle = "rgba(255,255,255,.6)"; ctx.font = "16px Georgia";
        ctx.fillText("Skor: " + g.score, W/2, H/2+10);
        ctx.font = "14px Georgia"; ctx.fillStyle = "rgba(255,255,255,.4)";
        ctx.fillText("SPACE → Tekrar Oyna", W/2, H/2+40);
        ctx.textAlign = "left";
        if (g.keys["Space"]) { g.keys["Space"] = false; start(); }
      }

      g.animFrame = requestAnimationFrame(loop);
    }

    g.animFrame = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onKeyUp); };
  }, []);

  const medals = ["🥇","🥈","🥉"];

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(79,172,254,.15),rgba(0,242,254,.1))", border:"1px solid rgba(79,172,254,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🚀 Uzay Saldırısı</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>← → Hareket | SPACE Ateş | Tüm düşmanları yok et!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#4facfe", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={720} height={480}
        style={{ width:"100%", borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"crosshair", display:"block" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s, i) => (
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#4facfe", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Satranç Oyunu ────────────────────────────────────────────────────────────
function ChessGame({ user }) {
  const EMPTY = null;
  const initBoard = () => [
    [{t:"r",c:"b"},{t:"n",c:"b"},{t:"b",c:"b"},{t:"q",c:"b"},{t:"k",c:"b"},{t:"b",c:"b"},{t:"n",c:"b"},{t:"r",c:"b"}],
    Array(8).fill(null).map(()=>({t:"p",c:"b"})),
    Array(8).fill(EMPTY),Array(8).fill(EMPTY),Array(8).fill(EMPTY),Array(8).fill(EMPTY),
    Array(8).fill(null).map(()=>({t:"p",c:"w"})),
    [{t:"r",c:"w"},{t:"n",c:"w"},{t:"b",c:"w"},{t:"q",c:"w"},{t:"k",c:"w"},{t:"b",c:"w"},{t:"n",c:"w"},{t:"r",c:"w"}],
  ];

  const [board, setBoard] = useState(initBoard);
  const [selected, setSelected] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [turn, setTurn] = useState("w");
  const [status, setStatus] = useState("Senin sıran (Beyaz)");
  const [level, setLevel] = useState("medium");
  const [gameOver, setGameOver] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [scores, setScores] = useState({ wins:0, losses:0, draws:0 });
  const [capturedW, setCapturedW] = useState([]);
  const [capturedB, setCapturedB] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [chessScores, setChessScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  async function loadChessScores() {
    try { const rows = await api.getChessScores(); setChessScores(rows); } catch {}
  }

  useEffect(() => { loadChessScores(); }, []);

  const PIECES = { k:"♔", q:"♕", r:"♖", b:"♗", n:"♘", p:"♙" };
  const PIECES_B = { k:"♚", q:"♛", r:"♜", b:"♝", n:"♞", p:"♟" };
  const PIECE_VAL = { p:1, n:3, b:3, r:5, q:9, k:100 };

  function inBounds(r,c) { return r>=0&&r<8&&c>=0&&c<8; }

  function getMoves(b, r, c, checkKing=true) {
    const piece = b[r][c];
    if (!piece) return [];
    const moves = [];
    const {t, col} = {t:piece.t, col:piece.c};
    const enemy = col==="w"?"b":"w";

    function addMove(nr, nc) {
      if (!inBounds(nr,nc)) return false;
      if (b[nr][nc]?.c === col) return false;
      moves.push([nr,nc]);
      return !b[nr][nc];
    }

    function slide(dirs) {
      dirs.forEach(([dr,dc]) => {
        let nr=r+dr, nc=c+dc;
        while(inBounds(nr,nc)) {
          if (b[nr][nc]?.c === col) break;
          moves.push([nr,nc]);
          if (b[nr][nc]) break;
          nr+=dr; nc+=dc;
        }
      });
    }

    if (t==="p") {
      const dir = col==="w"?-1:1;
      const start = col==="w"?6:1;
      if (inBounds(r+dir,c) && !b[r+dir][c]) {
        moves.push([r+dir,c]);
        if (r===start && !b[r+2*dir][c]) moves.push([r+2*dir,c]);
      }
      [[r+dir,c-1],[r+dir,c+1]].forEach(([nr,nc]) => {
        if (inBounds(nr,nc) && b[nr][nc]?.c===enemy) moves.push([nr,nc]);
      });
    } else if (t==="n") {
      [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]].forEach(([dr,dc])=>addMove(r+dr,c+dc));
    } else if (t==="b") {
      slide([[-1,-1],[-1,1],[1,-1],[1,1]]);
    } else if (t==="r") {
      slide([[-1,0],[1,0],[0,-1],[0,1]]);
    } else if (t==="q") {
      slide([[-1,-1],[-1,1],[1,-1],[1,1],[-1,0],[1,0],[0,-1],[0,1]]);
    } else if (t==="k") {
      [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]].forEach(([dr,dc])=>addMove(r+dr,c+dc));
    }

    if (!checkKing) return moves;
    return moves.filter(([nr,nc]) => {
      const nb = b.map(row=>[...row]);
      nb[nr][nc] = nb[r][c];
      nb[r][c] = null;
      return !isInCheck(nb, col);
    });
  }

  function isInCheck(b, col) {
    let kr=-1, kc=-1;
    for(let r=0;r<8;r++) for(let c=0;c<8;c++) if(b[r][c]?.t==="k"&&b[r][c]?.c===col) { kr=r; kc=c; }
    if(kr===-1) return true;
    const enemy = col==="w"?"b":"w";
    for(let r=0;r<8;r++) for(let c=0;c<8;c++) {
      if(b[r][c]?.c===enemy) {
        const moves = getMoves(b,r,c,false);
        if(moves.some(([mr,mc])=>mr===kr&&mc===kc)) return true;
      }
    }
    return false;
  }

  function hasAnyMoves(b, col) {
    for(let r=0;r<8;r++) for(let c=0;c<8;c++) {
      if(b[r][c]?.c===col && getMoves(b,r,c).length>0) return true;
    }
    return false;
  }

  function evalBoard(b) {
    let score = 0;
    const posBonus = {
      p: [[0,0,0,0,0,0,0,0],[5,5,5,5,5,5,5,5],[1,1,2,3,3,2,1,1],[0,0,0,2,2,0,0,0],[0,0,0,2,2,0,0,0],[0,1,-1,0,0,-1,1,0],[0,1,1,-2,-2,1,1,0],[0,0,0,0,0,0,0,0]],
      n: [[-5,-4,-3,-3,-3,-3,-4,-5],[-4,-2,0,0,0,0,-2,-4],[-3,0,1,1,1,1,0,-3],[-3,0,1,2,2,1,0,-3],[-3,0,1,2,2,1,0,-3],[-3,0,1,1,1,1,0,-3],[-4,-2,0,0,0,0,-2,-4],[-5,-4,-3,-3,-3,-3,-4,-5]],
    };
    for(let r=0;r<8;r++) for(let c=0;c<8;c++) {
      const p = b[r][c];
      if(!p) continue;
      const val = PIECE_VAL[p.t] * 10;
      const pos = (posBonus[p.t]?posBonus[p.t][p.c==="b"?r:7-r][c]:0);
      score += p.c==="b" ? (val+pos) : -(val+pos);
    }
    return score;
  }

  function minimax(b, depth, alpha, beta, maximizing) {
    if(depth===0) return evalBoard(b);
    const col = maximizing?"b":"w";
    const moves = [];
    for(let r=0;r<8;r++) for(let c=0;c<8;c++) {
      if(b[r][c]?.c===col) getMoves(b,r,c).forEach(([nr,nc])=>moves.push([r,c,nr,nc]));
    }
    if(!moves.length) return maximizing ? -1000 : 1000;
    if(maximizing) {
      let best = -Infinity;
      for(const [r,c,nr,nc] of moves) {
        const nb = b.map(row=>[...row]);
        nb[nr][nc]=nb[r][c]; nb[r][c]=null;
        best = Math.max(best, minimax(nb,depth-1,alpha,beta,false));
        alpha = Math.max(alpha,best);
        if(beta<=alpha) break;
      }
      return best;
    } else {
      let best = Infinity;
      for(const [r,c,nr,nc] of moves) {
        const nb = b.map(row=>[...row]);
        nb[nr][nc]=nb[r][c]; nb[r][c]=null;
        best = Math.min(best, minimax(nb,depth-1,alpha,beta,true));
        beta = Math.min(beta,best);
        if(beta<=alpha) break;
      }
      return best;
    }
  }

  function aiMove(b) {
    const depth = level==="easy"?1:level==="medium"?2:3;
    const moves = [];
    for(let r=0;r<8;r++) for(let c=0;c<8;c++) {
      if(b[r][c]?.c==="b") getMoves(b,r,c).forEach(([nr,nc])=>moves.push([r,c,nr,nc]));
    }
    if(!moves.length) return null;

    if(level==="easy") {
      // Rastgele ama capture tercih et
      const captures = moves.filter(([r,c,nr,nc])=>b[nr][nc]);
      const pool = captures.length ? captures : moves;
      return pool[Math.floor(Math.random()*pool.length)];
    }

    let bestScore = -Infinity, bestMove = moves[0];
    for(const [r,c,nr,nc] of moves) {
      const nb = b.map(row=>[...row]);
      nb[nr][nc]=nb[r][c]; nb[r][c]=null;
      const score = minimax(nb,depth-1,-Infinity,Infinity,false);
      if(score>bestScore) { bestScore=score; bestMove=[r,c,nr,nc]; }
    }
    return bestMove;
  }

  function handleClick(r, c) {
    if (gameOver || turn!=="w" || thinking) return;
    const b = board;

    if (selected) {
      if (validMoves.some(([mr,mc])=>mr===r&&mc===c)) {
        const nb = b.map(row=>[...row]);
        const captured = nb[r][c];
        if (captured) setCapturedW(p=>[...p, captured]);
        nb[r][c] = nb[selected[0]][selected[1]];
        // Piyon terfisi
        if (nb[r][c].t==="p" && r===0) nb[r][c] = {t:"q",c:"w"};
        nb[selected[0]][selected[1]] = null;
        setLastMove([selected,[r,c]]);
        setSelected(null); setValidMoves([]);
        setBoard(nb); setTurn("b");
        setStatus("Bilgisayar düşünüyor...");

        if (!hasAnyMoves(nb,"b")) {
          if (isInCheck(nb,"b")) { setStatus("Şah mat! Kazandın! 🎉"); setGameOver(true); setScores(s=>({...s,wins:s.wins+1})); api.saveChessScore("win").catch(()=>{}); loadChessScores(); }
          else { setStatus("Pat! Beraberlik!"); setGameOver(true); setScores(s=>({...s,draws:s.draws+1})); api.saveChessScore("draw").catch(()=>{}); loadChessScores(); }
          return;
        }

        setThinking(true);
        setTimeout(() => {
          const move = aiMove(nb);
          if (!move) { setThinking(false); return; }
          const [fr,fc,tr,tc] = move;
          const nb2 = nb.map(row=>[...row]);
          const cap2 = nb2[tr][tc];
          if (cap2) setCapturedB(p=>[...p, cap2]);
          nb2[tr][tc] = nb2[fr][fc];
          if (nb2[tr][tc].t==="p" && tr===7) nb2[tr][tc]={t:"q",c:"b"};
          nb2[fr][fc] = null;
          setLastMove([[fr,fc],[tr,tc]]);
          setBoard(nb2); setTurn("w"); setThinking(false);

          if (!hasAnyMoves(nb2,"w")) {
            if (isInCheck(nb2,"w")) { setStatus("Şah mat! Kaybettin! 😢"); setGameOver(true); setScores(s=>({...s,losses:s.losses+1})); api.saveChessScore("loss").catch(()=>{}); loadChessScores(); }
            else { setStatus("Pat! Beraberlik!"); setGameOver(true); setScores(s=>({...s,draws:s.draws+1})); }
          } else if (isInCheck(nb2,"w")) {
            setStatus("Şah! Dikkat et! ⚠️");
          } else {
            setStatus("Senin sıran (Beyaz)");
          }
        }, level==="easy"?300:level==="medium"?600:1000);
        return;
      }
      setSelected(null); setValidMoves([]);
    }

    if (b[r][c]?.c==="w") {
      const moves = getMoves(b,r,c);
      setSelected([r,c]);
      setValidMoves(moves);
    }
  }

  function resetGame() {
    setBoard(initBoard()); setSelected(null); setValidMoves([]);
    setTurn("w"); setStatus("Senin sıran (Beyaz)"); setGameOver(false);
    setThinking(false); setCapturedW([]); setCapturedB([]); setLastMove(null);
  }

  const LEVEL_LABELS = { easy:"😊 Kolay", medium:"🤔 Orta", hard:"😈 Zor" };

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:560, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(255,215,0,.15),rgba(247,151,30,.1))", border:"1px solid rgba(255,215,0,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>♟️ Satranç</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>{status}</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <select value={level} onChange={e=>{setLevel(e.target.value);resetGame();}} disabled={!gameOver&&turn!=="w"||thinking}
            style={{ padding:"6px 10px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.15)", borderRadius:8, color:"#fff", fontSize:12, cursor:"pointer" }}>
            <option value="easy" style={{background:"#1a1a2e"}}>😊 Kolay</option>
            <option value="medium" style={{background:"#1a1a2e"}}>🤔 Orta</option>
            <option value="hard" style={{background:"#1a1a2e"}}>😈 Zor</option>
          </select>
          <button onClick={()=>{ setShowBoard(v=>!v); loadChessScores(); }} style={{ padding:"6px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
          <button onClick={resetGame} style={{ padding:"6px 14px", background:"rgba(255,107,107,.15)", border:"1px solid rgba(255,107,107,.3)", borderRadius:8, color:"#ff6b6b", fontSize:12, cursor:"pointer" }}>Yeni Oyun</button>
        </div>
      </div>

      {/* Skor */}
      <div style={{ display:"flex", gap:10, marginBottom:12 }}>
        {[["🏆 Kazandı",scores.wins,"#48c78e"],["💀 Kaybetti",scores.losses,"#ff6b6b"],["🤝 Beraberlik",scores.draws,"#ffd700"]].map(([l,v,c])=>(
          <div key={l} style={{ flex:1, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:10, padding:"8px 12px", textAlign:"center" }}>
            <div style={{ color:c, fontSize:18, fontWeight:800 }}>{v}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Alınan taşlar - siyah */}
      <div style={{ height:24, marginBottom:4, display:"flex", gap:2, flexWrap:"wrap" }}>
        {capturedB.map((p,i)=><span key={i} style={{ fontSize:18, opacity:.7 }}>{PIECES_B[p.t]}</span>)}
      </div>

      {/* Tahta */}
      <div style={{ border:"2px solid rgba(255,215,0,.3)", borderRadius:10, overflow:"hidden", display:"inline-block", width:"100%" }}>
        {board.map((row, r) => (
          <div key={r} style={{ display:"flex" }}>
            {row.map((cell, c) => {
              const isLight = (r+c)%2===0;
              const isSel = selected&&selected[0]===r&&selected[1]===c;
              const isValid = validMoves.some(([mr,mc])=>mr===r&&mc===c);
              const isLast = lastMove&&((lastMove[0][0]===r&&lastMove[0][1]===c)||(lastMove[1][0]===r&&lastMove[1][1]===c));
              const isCheck = cell?.t==="k"&&cell?.c===turn&&isInCheck(board,turn);
              let bg = isLight?"#f0d9b5":"#b58863";
              if (isSel) bg = "#f6f669";
              else if (isLast) bg = isLight?"#cdd16f":"#aaa23a";
              if (isCheck) bg = "#ff6b6b";
              return (
                <div key={c} onClick={()=>handleClick(r,c)}
                  style={{ width:"12.5%", aspectRatio:"1", background:bg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", fontSize:"clamp(20px,4vw,36px)" }}>
                  {isValid && <div style={{ position:"absolute", width:cell?"100%":"35%", height:cell?"100%":"35%", borderRadius:cell?"0":"50%", background:cell?"rgba(0,0,0,.25)":"rgba(0,0,0,.2)", border:cell?"3px solid rgba(0,0,0,.3)":"none" }} />}
                  {cell && (
                    <span style={{
                      zIndex:1, userSelect:"none",
                      fontSize:"clamp(20px,3.8vw,34px)",
                      lineHeight:1,
                      color: cell.c==="w" ? "#fff" : "#1a1a1a",
                      WebkitTextStroke: cell.c==="w" ? "1.5px #222" : "1px #fff",
                      paintOrder: "stroke fill",
                      textShadow: cell.c==="w"
                        ? "0 0 4px #000, 0 1px 4px #000"
                        : "0 0 3px rgba(255,255,255,.6)",
                    }}>
                      {cell.c==="w"?PIECES[cell.t]:PIECES_B[cell.t]}
                    </span>
                  )}
                  {c===0 && <span style={{ position:"absolute", top:1, left:2, fontSize:9, color:isLight?"#b58863":"#f0d9b5", fontWeight:700 }}>{8-r}</span>}
                  {r===7 && <span style={{ position:"absolute", bottom:1, right:2, fontSize:9, color:isLight?"#b58863":"#f0d9b5", fontWeight:700 }}>{"abcdefgh"[c]}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Alınan taşlar - beyaz */}
      <div style={{ height:24, marginTop:4, display:"flex", gap:2, flexWrap:"wrap" }}>
        {capturedW.map((p,i)=><span key={i} style={{ fontSize:18, opacity:.7 }}>{PIECES[p.t]}</span>)}
      </div>

      {gameOver && (
        <div style={{ marginTop:14, background:"rgba(255,215,0,.08)", border:"1px solid rgba(255,215,0,.25)", borderRadius:12, padding:"16px 20px", textAlign:"center" }}>
          <div style={{ color:"#ffd700", fontSize:18, fontWeight:700, marginBottom:10 }}>{status}</div>
          <button onClick={resetGame} style={{ padding:"10px 28px", background:"linear-gradient(135deg,#ffd700,#f7971e)", border:"none", borderRadius:10, color:"#1a1a2e", fontWeight:700, fontSize:14, cursor:"pointer" }}>Tekrar Oyna</button>
        </div>
      )}

      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SATRANÇ SKOR TABLOSU</span>
          </div>
          <div style={{ padding:"10px 18px", display:"grid", gridTemplateColumns:"1fr 80px 80px 80px 80px", gap:8, borderBottom:"1px solid rgba(255,255,255,.06)" }}>
            <span style={{ color:"rgba(255,255,255,.3)", fontSize:10, letterSpacing:"1px" }}>OYUNCU</span>
            <span style={{ color:"rgba(72,199,142,.6)", fontSize:10, textAlign:"center" }}>KAZANDI</span>
            <span style={{ color:"rgba(255,107,107,.6)", fontSize:10, textAlign:"center" }}>KAYBETTİ</span>
            <span style={{ color:"rgba(255,215,0,.6)", fontSize:10, textAlign:"center" }}>BERABERLİK</span>
            <span style={{ color:"rgba(255,255,255,.3)", fontSize:10, textAlign:"center" }}>TOPLAM</span>
          </div>
          {chessScores.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz oyun yok</div>
            : chessScores.map((s,i) => (
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"grid", gridTemplateColumns:"1fr 80px 80px 80px 80px", gap:8, alignItems:"center", background:i%2?"rgba(255,255,255,.015)":"transparent" }}>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, fontWeight:600 }}>{s.name}</span>
                <span style={{ color:"#48c78e", fontSize:14, fontWeight:700, textAlign:"center" }}>{s.wins}</span>
                <span style={{ color:"#ff6b6b", fontSize:14, fontWeight:700, textAlign:"center" }}>{s.losses}</span>
                <span style={{ color:"#ffd700", fontSize:14, fontWeight:700, textAlign:"center" }}>{s.draws}</span>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:13, textAlign:"center" }}>{s.total}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Snake Oyunu ──────────────────────────────────────────────────────────────
function SnakeGame({ user }) {
  const canvasRef = useRef(null);
  const gameRef = useRef({
    running: false, over: false, started: false,
    snake: [], dir: { x:1, y:0 }, nextDir: { x:1, y:0 },
    food: null, score: 0, hiScore: 0, frame: 0,
    animFrame: null, speed: 8,
  });
  const [display, setDisplay] = useState({ score:0, hiScore:0, over:false, started:false });
  const [scores, setScores] = useState([]);
  const [showBoard, setShowBoard] = useState(false);

  async function loadScores() {
    try { const rows = await api.getScores("snake"); setScores(rows); } catch {}
  }

  useEffect(() => { loadScores(); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const CELL = 20;
    const COLS = Math.floor(W / CELL);
    const ROWS = Math.floor(H / CELL);
    const g = gameRef.current;

    function initSnake() {
      g.snake = [
        { x:5, y:Math.floor(ROWS/2) },
        { x:4, y:Math.floor(ROWS/2) },
        { x:3, y:Math.floor(ROWS/2) },
      ];
      g.dir = { x:1, y:0 };
      g.nextDir = { x:1, y:0 };
      spawnFood();
    }

    function spawnFood() {
      let pos;
      do {
        pos = { x: Math.floor(Math.random()*COLS), y: Math.floor(Math.random()*ROWS) };
      } while (g.snake.some(s => s.x===pos.x && s.y===pos.y));
      g.food = pos;
    }

    function restart() {
      g.running = true; g.over = false; g.started = true;
      g.score = 0; g.frame = 0; g.speed = 8;
      initSnake();
      setDisplay(d => ({...d, over:false, score:0, started:true}));
    }

    function start() {
      g.started = true; g.running = true;
      initSnake();
      setDisplay(d => ({...d, started:true}));
    }

    const onKey = e => {
      if (!g.started && (e.code==="Space"||e.code==="ArrowUp"||e.code==="ArrowDown"||e.code==="ArrowLeft"||e.code==="ArrowRight")) { start(); return; }
      if (g.over && e.code==="Space") { restart(); return; }
      if (e.code==="ArrowUp"    && g.dir.y!==1)  g.nextDir = { x:0, y:-1 };
      if (e.code==="ArrowDown"  && g.dir.y!==-1) g.nextDir = { x:0, y:1 };
      if (e.code==="ArrowLeft"  && g.dir.x!==1)  g.nextDir = { x:-1, y:0 };
      if (e.code==="ArrowRight" && g.dir.x!==-1) g.nextDir = { x:1, y:0 };
      if (["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.code)) e.preventDefault();
    };

    window.addEventListener("keydown", onKey);

    // Touch controls
    let touchStart = null;
    canvas.addEventListener("touchstart", e => { touchStart = { x:e.touches[0].clientX, y:e.touches[0].clientY }; });
    canvas.addEventListener("touchend", e => {
      if (!touchStart) return;
      if (!g.started) { start(); return; }
      if (g.over) { restart(); return; }
      const dx = e.changedTouches[0].clientX - touchStart.x;
      const dy = e.changedTouches[0].clientY - touchStart.y;
      if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 20 && g.dir.x !== -1) g.nextDir = { x:1, y:0 };
        if (dx < -20 && g.dir.x !== 1) g.nextDir = { x:-1, y:0 };
      } else {
        if (dy > 20 && g.dir.y !== -1) g.nextDir = { x:0, y:1 };
        if (dy < -20 && g.dir.y !== 1) g.nextDir = { x:0, y:-1 };
      }
      touchStart = null;
    });

    function drawCell(x, y, color, radius=4) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x*CELL+1, y*CELL+1, CELL-2, CELL-2, radius);
      ctx.fill();
    }

    function loop() {
      ctx.fillStyle = "#0a0814";
      ctx.fillRect(0, 0, W, H);

      // Grid
      ctx.strokeStyle = "rgba(255,255,255,.03)";
      ctx.lineWidth = 0.5;
      for (let x=0; x<=COLS; x++) { ctx.beginPath(); ctx.moveTo(x*CELL,0); ctx.lineTo(x*CELL,H); ctx.stroke(); }
      for (let y=0; y<=ROWS; y++) { ctx.beginPath(); ctx.moveTo(0,y*CELL); ctx.lineTo(W,y*CELL); ctx.stroke(); }

      if (!g.started) {
        ctx.fillStyle = "rgba(255,255,255,.9)"; ctx.font = "bold 24px Georgia"; ctx.textAlign = "center";
        ctx.fillText("🐍 YILAN OYUNU", W/2, H/2 - 30);
        ctx.font = "14px Georgia"; ctx.fillStyle = "rgba(255,255,255,.5)";
        ctx.fillText("SPACE veya ok tuşları → Başla", W/2, H/2 + 10);
        ctx.textAlign = "left";
        g.animFrame = requestAnimationFrame(loop);
        return;
      }

      if (g.running) {
        g.frame++;
        const fps = Math.min(4 + Math.floor(g.score / 5), 15);
        if (g.frame % Math.max(1, Math.floor(60/fps)) === 0) {
          g.dir = { ...g.nextDir };
          const head = { x: g.snake[0].x + g.dir.x, y: g.snake[0].y + g.dir.y };

          // Duvar çarpışması
          if (head.x < 0 || head.x >= COLS || head.y < 0 || head.y >= ROWS) {
            g.running = false; g.over = true;
            const finalScore = g.score;
            if (finalScore > g.hiScore) g.hiScore = finalScore;
            api.saveScore(finalScore, "snake").catch(() => {});
            loadScores();
            setDisplay({ score:finalScore, hiScore:g.hiScore, over:true, started:true });
            g.animFrame = requestAnimationFrame(loop);
            return;
          }

          // Kendine çarpışma
          if (g.snake.some(s => s.x===head.x && s.y===head.y)) {
            g.running = false; g.over = true;
            const finalScore = g.score;
            if (finalScore > g.hiScore) g.hiScore = finalScore;
            api.saveScore(finalScore, "snake").catch(() => {});
            loadScores();
            setDisplay({ score:finalScore, hiScore:g.hiScore, over:true, started:true });
            g.animFrame = requestAnimationFrame(loop);
            return;
          }

          g.snake.unshift(head);

          // Yemek yeme
          if (head.x === g.food.x && head.y === g.food.y) {
            g.score++;
            spawnFood();
            setDisplay(d => ({...d, score:g.score}));
          } else {
            g.snake.pop();
          }
        }
      }

      // Yılanı çiz
      g.snake.forEach((s, i) => {
        if (i === 0) {
          // Baş
          const grad = ctx.createRadialGradient(s.x*CELL+CELL/2, s.y*CELL+CELL/2, 2, s.x*CELL+CELL/2, s.y*CELL+CELL/2, CELL/2);
          grad.addColorStop(0, "#06d6a0");
          grad.addColorStop(1, "#048a81");
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.roundRect(s.x*CELL+1, s.y*CELL+1, CELL-2, CELL-2, 6);
          ctx.fill();
          // Gözler
          ctx.fillStyle = "#fff";
          const eyeOff = g.dir.x !== 0 ? { ex1:g.dir.x>0?12:4, ey1:4, ex2:g.dir.x>0?12:4, ey2:12 } : { ex1:4, ey1:g.dir.y>0?12:4, ex2:12, ey2:g.dir.y>0?12:4 };
          ctx.beginPath(); ctx.arc(s.x*CELL+eyeOff.ex1, s.y*CELL+eyeOff.ey1, 3, 0, Math.PI*2); ctx.fill();
          ctx.beginPath(); ctx.arc(s.x*CELL+eyeOff.ex2, s.y*CELL+eyeOff.ey2, 3, 0, Math.PI*2); ctx.fill();
          ctx.fillStyle = "#1a1a2e";
          ctx.beginPath(); ctx.arc(s.x*CELL+eyeOff.ex1+0.5, s.y*CELL+eyeOff.ey1+0.5, 1.5, 0, Math.PI*2); ctx.fill();
          ctx.beginPath(); ctx.arc(s.x*CELL+eyeOff.ex2+0.5, s.y*CELL+eyeOff.ey2+0.5, 1.5, 0, Math.PI*2); ctx.fill();
        } else {
          const t = i / g.snake.length;
          const r = Math.floor(6 + t * 42);
          const g2 = Math.floor(214 - t * 100);
          const b = Math.floor(160 - t * 80);
          drawCell(s.x, s.y, `rgb(${r},${g2},${b})`);
        }
      });

      // Yemeği çiz
      if (g.food) {
        const pulse = Math.sin(g.frame * 0.1) * 2;
        ctx.fillStyle = "#ff6b6b";
        ctx.beginPath();
        ctx.arc(g.food.x*CELL+CELL/2, g.food.y*CELL+CELL/2, CELL/2 - 2 + pulse, 0, Math.PI*2);
        ctx.fill();
        ctx.fillStyle = "#ff9999";
        ctx.beginPath();
        ctx.arc(g.food.x*CELL+CELL/2-2, g.food.y*CELL+CELL/2-2, 3, 0, Math.PI*2);
        ctx.fill();
      }

      if (g.over) {
        ctx.fillStyle = "rgba(0,0,0,.7)"; ctx.fillRect(0,0,W,H);
        ctx.fillStyle = "#ff6b6b"; ctx.font = "bold 28px Georgia"; ctx.textAlign = "center";
        ctx.fillText("GAME OVER! 💀", W/2, H/2-20);
        ctx.fillStyle = "rgba(255,255,255,.6)"; ctx.font = "14px Georgia";
        ctx.fillText("SPACE → Tekrar Oyna", W/2, H/2+16);
        ctx.textAlign = "left";
      }

      g.animFrame = requestAnimationFrame(loop);
    }

    g.animFrame = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(g.animFrame); window.removeEventListener("keydown", onKey); };
  }, []);

  const medals = ["🥇","🥈","🥉"];

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:740, margin:"0 auto" }}>
      <div style={{ background:"linear-gradient(135deg,rgba(6,214,160,.15),rgba(4,138,129,.1))", border:"1px solid rgba(6,214,160,.25)", borderRadius:16, padding:"14px 22px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:19, fontWeight:700 }}>🐍 Yılan Oyunu</h2>
          <p style={{ color:"rgba(255,255,255,.4)", margin:"2px 0 0", fontSize:11 }}>Ok tuşları → Yön | Yemek ye, büyü!</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"#06d6a0", fontSize:22, fontWeight:800 }}>{display.score}</div>
            <div style={{ color:"rgba(255,255,255,.3)", fontSize:10 }}>EN İYİ: {display.hiScore}</div>
          </div>
          <button onClick={()=>{ setShowBoard(v=>!v); loadScores(); }} style={{ padding:"7px 14px", background:"rgba(255,215,0,.1)", border:"1px solid rgba(255,215,0,.25)", borderRadius:8, color:"#ffd700", fontSize:12, cursor:"pointer" }}>🏆 Skor</button>
        </div>
      </div>
      <canvas ref={canvasRef} width={720} height={400}
        style={{ width:"100%", borderRadius:14, border:"1px solid rgba(255,255,255,.08)", cursor:"pointer", display:"block" }} />
      {showBoard && (
        <div style={{ marginTop:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,215,0,.2)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,215,0,.1)" }}>
            <span style={{ color:"#ffd700", fontSize:12, fontWeight:700, letterSpacing:"1px" }}>🏆 SKOR TABLOSU</span>
          </div>
          {scores.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,.2)", fontSize:12 }}>Henüz skor yok</div>
            : scores.map((s, i) => (
              <div key={s.username} style={{ padding:"10px 18px", borderBottom:"1px solid rgba(255,255,255,.04)", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, width:24, color:i===0?"#ffd700":i===1?"#c0c0c0":i===2?"#cd7f32":"rgba(255,255,255,.4)", fontWeight:700 }}>{i+1}.</span>
                <span style={{ color:"rgba(255,255,255,.7)", fontSize:13, flex:1 }}>{s.name}</span>
                <span style={{ color:"#ffd700", fontSize:15, fontWeight:700 }}>{s.score}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── Başlangıç Parası Bölümü (Kasiyer) ───────────────────────────────────────
function AdvanceSection({ kasaKey, kasaName, selDate }) {
  const [advance, setAdvance] = useState(null);
  const [amount,  setAmount]  = useState("");
  const [sending, setSending] = useState(false);
  const [toast,   showToast]  = useToast();
  const isToday = selDate === getTodayKey();

  async function refresh() {
    const data = await api.getAdvance(kasaKey, selDate).catch(() => null);
    setAdvance(data);
  }

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, [kasaKey, selDate]);

  async function send() {
    const n = parseFloat(amount.replace(",", "."));
    if (!n || n <= 0) return;
    setSending(true);
    try {
      await api.addAdvance({
        id: `${Date.now()}-${Math.random()}`,
        kasa_key: kasaKey,
        kasa_name: kasaName,
        date: selDate,
        amount: n,
        requested_at: new Date().toISOString(),
      });
      showToast("Talep gönderildi ✓");
      setAmount("");
      refresh();
    } catch (e) { showToast(e.message, false); }
    setSending(false);
  }

  const fmtMoney = n => parseFloat(n).toLocaleString("tr-TR", { minimumFractionDigits: 2 });

  return (
    <div style={{ marginBottom:18, background:"rgba(72,199,142,.04)", border:"1px solid rgba(72,199,142,.15)", borderRadius:14, overflow:"hidden" }}>
      <Toast toast={toast} />
      <div style={{ padding:"13px 20px", borderBottom:"1px solid rgba(72,199,142,.1)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ color:"rgba(72,199,142,.8)", fontSize:11, letterSpacing:"1px", textTransform:"uppercase" }}>💰 Kasa Başlangıç Bildirimi Parası</span>
        {advance?.status === "approved" && <span style={{ color:"rgba(72,199,142,.5)", fontSize:11 }}>✓ Onaylandı</span>}
        {advance?.status === "pending"  && <span style={{ color:"rgba(255,193,7,.6)",  fontSize:11 }}>⏳ Onay Bekleniyor</span>}
      </div>
      <div style={{ padding:"14px 20px" }}>
        {!advance && isToday && (
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="Miktar girin (₺)" min="0" step="0.01"
              style={{ ...inp, flex:1, fontSize:13, padding:"9px 12px" }}
              onKeyDown={e => e.key === "Enter" && send()} />
            <button onClick={send} disabled={sending || !amount}
              style={{ padding:"9px 20px", background: amount ? "linear-gradient(135deg,#48c78e,#06d6a0)" : "rgba(255,255,255,.05)", border:"none", borderRadius:9, color: amount ? "#fff" : "rgba(255,255,255,.2)", fontWeight:700, cursor: amount ? "pointer" : "not-allowed", fontSize:13, whiteSpace:"nowrap" }}>
              {sending ? "Gönderiliyor…" : "Gönder →"}
            </button>
          </div>
        )}
        {advance?.status === "pending" && (
          <div style={{ color:"rgba(255,193,7,.7)", fontSize:13, textAlign:"center", padding:"6px 0" }}>
            ⏳ {fmtMoney(advance.amount)} ₺ — Yönetici onayı bekleniyor
          </div>
        )}
        {advance?.status === "approved" && (
          <div style={{ color:"rgba(72,199,142,.4)", fontSize:13, textAlign:"center", padding:"6px 0" }}>
            ✓ {fmtMoney(advance.amount)} ₺ — Onaylandı
          </div>
        )}
        {!advance && !isToday && (
          <div style={{ color:"rgba(255,255,255,.2)", fontSize:13, textAlign:"center", padding:"6px 0" }}>
            Bu tarih için başlangıç parası kaydı yok
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Başlangıç Onayı Paneli (Admin) ──────────────────────────────────────────
export function AdvancesPanel() {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [toast,    showToast]   = useToast();

  async function refresh() {
    try {
      const rows = await api.getAllAdvances();
      setRequests(rows);
    } catch {}
    setLoading(false);
  }

  useEffect(() => { refresh(); const iv = setInterval(refresh, 5000); return () => clearInterval(iv); }, []);

  async function approve(req) {
    showToast("Onaylanıyor…");
    await api.approveAdvance(req.id);
    showToast("Başlangıç parası onaylandı ✓");
    refresh();
  }

  async function cancel(req) {
    showToast("İptal ediliyor…");
    await api.cancelAdvance(req.id);
    showToast("Onay iptal edildi", false);
    refresh();
  }

  const fmtD  = d => { const s = d?.slice?.(0,10)||""; const [y,m,dd]=s.split("-"); return dd?`${dd}.${m}.${y}`:s; };
  const fmtDT = iso => new Date(iso).toLocaleString("tr-TR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
  const fmtMoney = n => parseFloat(n).toLocaleString("tr-TR",{minimumFractionDigits:2});

  const pending  = requests.filter(r => r.status === "pending");
  const approved = requests.filter(r => r.status === "approved");

  return (
    <div style={{ fontFamily:"Georgia,serif", maxWidth:680, margin:"0 auto" }}>
      <Toast toast={toast} />
      <div style={{ background:"linear-gradient(135deg,rgba(72,199,142,.15),rgba(6,214,160,.1))", border:"1px solid rgba(72,199,142,.25)", borderRadius:16, padding:"18px 22px", marginBottom:22 }}>
        <h2 style={{ color:"#fff", margin:0, fontSize:20, fontWeight:700 }}>💰 Kasa Başlangıç Bildirimi Onayı</h2>
        <p style={{ color:"rgba(255,255,255,.4)", margin:"4px 0 0", fontSize:12 }}>Kasiyerlerden gelen başlangıç parası talepleri</p>
      </div>

      {loading
        ? <div style={{ color:"rgba(255,255,255,.3)", textAlign:"center", padding:40 }}>Yükleniyor…</div>
        : <>
            {/* Bekleyenler */}
            {pending.length > 0 && (
              <div style={{ marginBottom:24 }}>
                <div style={{ color:"rgba(255,193,7,.7)", fontSize:11, letterSpacing:"1px", marginBottom:10, textTransform:"uppercase" }}>⏳ Onay Bekleyenler</div>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {pending.map(req => (
                    <div key={req.id} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,193,7,.25)", borderRadius:14, overflow:"hidden" }}>
                      <div style={{ padding:"12px 18px", background:"rgba(255,193,7,.06)", borderBottom:"1px solid rgba(255,193,7,.15)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:16 }}>⏳</span>
                          <span style={{ color:"#ffd700", fontWeight:700, fontSize:13 }}>{req.kasa_name}</span>
                          <span style={{ color:"rgba(255,255,255,.35)", fontSize:11 }}>{fmtD(req.date)}</span>
                        </div>
                        <span style={{ color:"rgba(255,255,255,.3)", fontSize:11 }}>{fmtDT(req.requested_at)}</span>
                      </div>
                      <div style={{ padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <div style={{ color:"#48c78e", fontSize:22, fontWeight:800 }}>{fmtMoney(req.amount)} ₺</div>
                        <button onClick={()=>approve(req)} style={{ padding:"10px 22px", background:"linear-gradient(135deg,#48c78e,#06d6a0)", border:"none", borderRadius:10, color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>✓ Onayla</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pending.length === 0 && (
              <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:14, padding:"32px 20px", textAlign:"center", marginBottom:24 }}>
                <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
                <div style={{ color:"rgba(255,255,255,.3)", fontSize:13 }}>Bekleyen talep yok</div>
              </div>
            )}

            {/* Onaylanmışlar */}
            {approved.length > 0 && (
              <div>
                <div style={{ color:"rgba(72,199,142,.5)", fontSize:11, letterSpacing:"1px", marginBottom:10, textTransform:"uppercase" }}>✓ Onaylanmışlar</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {approved.map(req => (
                    <div key={req.id} style={{ background:"rgba(255,255,255,.02)", border:"1px solid rgba(72,199,142,.1)", borderRadius:12, padding:"12px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <span style={{ color:"rgba(72,199,142,.5)", fontSize:13 }}>✓</span>
                        <span style={{ color:"rgba(255,255,255,.4)", fontSize:12 }}>{req.kasa_name}</span>
                        <span style={{ color:"rgba(255,255,255,.25)", fontSize:11 }}>{fmtD(req.date)}</span>
                        <span style={{ color:"rgba(72,199,142,.5)", fontSize:13, fontWeight:700 }}>{fmtMoney(req.amount)} ₺</span>
                      </div>
                      <button onClick={()=>cancel(req)} style={{ padding:"5px 12px", background:"rgba(255,107,107,.1)", border:"1px solid rgba(255,107,107,.25)", borderRadius:7, color:"#ff6b6b", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                        Onayı İptal Et
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
      }
    </div>
  );
}
